import { useEffect, useRef, useState, useCallback, useMemo, useLayoutEffect, createContext, useContext } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Search, Database, Wrench, Bot, Plug, FileText, Copy, BarChart2, Shield, Eye } from "lucide-react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

const AnimContext = createContext(false);

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  bg: "#020408",
  blue: "#00A8FF",
  purple: "#8B5CF6",
  cyan: "#06EEF0",
  white: "#F0F6FF",
  pink: "#FF6B9D",
  orange: "#FF8C42",
  emerald: "#10B981",
  red: "#FF4466",
  gold: "#F5C542",
  dim: "rgba(240,246,255,0.45)",
  dimmer: "rgba(240,246,255,0.18)",
};

// ─── Motion presets ───────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } },
};

// ─── Scroll-driven ambient background ─────────────────────────────────────────
// The page "changes temperature" as you descend: the nebula glow interpolates
// through the same accent colours the chapters use, and the starfield drifts.
const NEBULA_STOPS = [
  [6, 238, 240],   // cyan      — foundation
  [139, 92, 246],  // purple    — math / learning
  [255, 140, 66],  // orange    — training / inference
  [16, 185, 129],  // emerald
  [255, 107, 157], // pink      — scaling
  [245, 197, 66],  // gold      — modern AI
];
function nebulaColor(p) {
  const n = NEBULA_STOPS.length - 1;
  const x = Math.max(0, Math.min(1, p)) * n;
  const i = Math.min(n - 1, Math.floor(x));
  const f = x - i;
  const a = NEBULA_STOPS[i], b = NEBULA_STOPS[i + 1];
  return [
    Math.round(a[0] + (b[0] - a[0]) * f),
    Math.round(a[1] + (b[1] - a[1]) * f),
    Math.round(a[2] + (b[2] - a[2]) * f),
  ];
}

function StarBackground() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    // Viewport size with a guard: some embedded contexts report innerWidth as 1.
    const vpW = () => (window.innerWidth > 1 ? window.innerWidth : window.screen.width);
    const vpH = () => (window.innerHeight > 1 ? window.innerHeight : window.screen.height);
    const resize = () => { canvas.width = vpW(); canvas.height = vpH(); };
    resize();
    window.addEventListener("resize", resize);

    // Scroll progress is read directly each frame (no scroll listener) so it's
    // always current and doesn't depend on the scroll event firing.
    const scrollProgress = () => {
      const max = document.documentElement.scrollHeight - vpH();
      return max > 0 ? Math.min(1, window.scrollY / max) : 0;
    };

    const stars = Array.from({ length: 180 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.2 + 0.2,
      base: Math.random() * 0.7 + 0.15,
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.008 + 0.003,
      depth: Math.random() * 0.8 + 0.2, // parallax factor
    }));
    let t = 0;

    const render = () => {
      const W = canvas.width, H = canvas.height;
      const p = scrollProgress();
      ctx.clearRect(0, 0, W, H);

      // Two nebula glows tinted by scroll progress (offset hues for variety).
      const [r1, g1c, b1] = nebulaColor(p);
      const [r2, g2c, b2] = nebulaColor(p + 0.18);
      const gA = ctx.createRadialGradient(W * 0.2, H * 0.25, 0, W * 0.2, H * 0.25, W * 0.45);
      gA.addColorStop(0, `rgba(${r1},${g1c},${b1},0.12)`); gA.addColorStop(1, "transparent");
      ctx.fillStyle = gA; ctx.fillRect(0, 0, W, H);
      const gB = ctx.createRadialGradient(W * 0.78, H * 0.6, 0, W * 0.78, H * 0.6, W * 0.4);
      gB.addColorStop(0, `rgba(${r2},${g2c},${b2},0.09)`); gB.addColorStop(1, "transparent");
      ctx.fillStyle = gB; ctx.fillRect(0, 0, W, H);

      stars.forEach(s => {
        const alpha = s.base + 0.25 * Math.sin(t * s.speed + s.phase);
        // Gentle parallax: nearer stars drift up faster as you scroll.
        const y = (((s.y - p * 0.25 * s.depth) % 1) + 1) % 1;
        ctx.beginPath();
        ctx.arc(s.x * W, y * H, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(240,246,255,${alpha.toFixed(2)})`;
        ctx.fill();
      });
      t++;
      raf = requestAnimationFrame(render);
    };
    render();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas ref={canvasRef} className="ai-star-bg" style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      pointerEvents: "none", zIndex: 0,
    }} />
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ id, label, title, accent = C.cyan, children, minH, center = false }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  // `100vh` recalculates as the mobile browser chrome shows/hides while
  // scrolling, which reflows every section at once and reads as flicker.
  // `100svh` is pinned to the smallest possible viewport, so it never moves.
  const resolvedMinH = minH || "100svh";

  return (
    <section ref={ref} id={id} style={{
      minHeight: resolvedMinH, display: "flex", flexDirection: "column",
      justifyContent: "center", padding: "100px clamp(24px,8vw,120px)",
      position: "relative", zIndex: 1,
      alignItems: center ? "center" : "flex-start",
      textAlign: center ? "center" : "left",
    }}>
      <motion.div initial="hidden" animate={isInView ? "visible" : "hidden"}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}>
        <motion.div variants={fadeUp} style={{
          fontFamily: "'Orbitron', monospace", fontSize: 10, letterSpacing: "0.25em",
          color: accent, marginBottom: 20, opacity: 0.7, textTransform: "uppercase",
        }}>{label}</motion.div>
        <motion.h2 variants={fadeUp} style={{
          color: C.white, fontSize: "clamp(36px,5.5vw,88px)", fontWeight: 800,
          lineHeight: 1.04, margin: "0 0 0", letterSpacing: "-0.02em",
          fontFamily: "'Raleway', sans-serif",
        }}>{title}</motion.h2>
      </motion.div>
      {children}
    </section>
  );
}

// ─── Concept Viz Section (interactive wrapper) ────────────────────────────────
function ConceptVizSection({ category, accent = C.cyan, title, hook, children, fullWidth = false }) {
  return (
    <section style={{ padding: "40px clamp(24px,8vw,120px)", position: "relative", zIndex: 1 }}>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(ellipse at 12% 50%, ${accent}0d 0%, transparent 58%)` }} />
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
        style={{ position: "relative" }}>
        <div style={{ position: "relative", background: "rgba(255,255,255,0.022)", border: "1px solid rgba(255,255,255,0.065)", borderRadius: 24, overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: "8%", right: "8%", height: 1, background: `linear-gradient(90deg, transparent, ${accent}cc, ${accent}, ${accent}cc, transparent)`, boxShadow: `0 0 18px ${accent}70` }} />
          <div className="concept-ghost" style={{ position: "absolute", right: -8, bottom: -16, fontSize: "clamp(140px,20vw,240px)", fontWeight: 900, lineHeight: 1, color: `${accent}05`, fontFamily: "'Raleway', sans-serif", userSelect: "none", pointerEvents: "none", letterSpacing: "-0.04em" }}>{title[0]}</div>
          {fullWidth ? (
            <div style={{ padding: "48px clamp(28px,4vw,56px)", position: "relative" }}>
              <div style={{ marginBottom: 32 }}>
                <motion.div variants={fadeUp} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px 5px 10px", background: `${accent}14`, border: `1px solid ${accent}38`, borderRadius: 100, marginBottom: 16 }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: accent, boxShadow: `0 0 8px ${accent}` }} />
                  <span style={{ fontFamily: "'Orbitron', monospace", fontSize: 8.5, letterSpacing: "0.26em", color: accent, textTransform: "uppercase", fontWeight: 700 }}>{category}</span>
                </motion.div>
                <motion.h3 variants={fadeUp} style={{ color: C.white, fontSize: "clamp(22px,2.4vw,40px)", fontWeight: 800, lineHeight: 1.1, margin: "0 0 14px", letterSpacing: "-0.02em", fontFamily: "'Raleway', sans-serif" }}>{title}</motion.h3>
                {hook && <motion.p variants={fadeUp} style={{ color: C.dim, fontSize: "clamp(12px,1.1vw,15px)", lineHeight: 1.8, margin: 0, maxWidth: 680 }}>{hook}</motion.p>}
              </div>
              {children}
            </div>
          ) : (
            <div className="concept-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.5fr)", gap: "clamp(24px,4vw,48px)", padding: "clamp(24px,4vw,48px) clamp(16px,4vw,56px)", alignItems: "center", position: "relative" }}>
              <div>
                <motion.div variants={fadeUp} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px 5px 10px", background: `${accent}14`, border: `1px solid ${accent}38`, borderRadius: 100, marginBottom: 22 }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: accent, boxShadow: `0 0 8px ${accent}` }} />
                  <span style={{ fontFamily: "'Orbitron', monospace", fontSize: 8.5, letterSpacing: "0.26em", color: accent, textTransform: "uppercase", fontWeight: 700 }}>{category}</span>
                </motion.div>
                <motion.h3 variants={fadeUp} style={{ color: C.white, fontSize: "clamp(20px,2.2vw,38px)", fontWeight: 800, lineHeight: 1.1, margin: "0 0 18px", letterSpacing: "-0.02em", fontFamily: "'Raleway', sans-serif" }}>{title}</motion.h3>
                {hook && <motion.p variants={fadeUp} style={{ color: C.dim, fontSize: "clamp(12px,1.05vw,14px)", lineHeight: 1.8, margin: 0 }}>{hook}</motion.p>}
              </div>
              <div style={{ position: "relative" }}>{children}</div>
            </div>
          )}
        </div>
      </motion.div>
    </section>
  );
}

// ─── 1. NEURAL NETWORK VIZ ────────────────────────────────────────────────────
function NeuralNetworkViz() {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState(0); // 0=single neuron, 1=expanding, 2=full network
  const stateRef = useRef({ t: 0, particles: [], expandProgress: 0, phase: 0, particleTimer: 0 });
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = 480, H = 300;
    canvas.width = W; canvas.height = H;

    const inputs = [0.8, 0.5, 0.3];
    const weights = [0.7, 0.9, 0.4];
    const bias = 0.2;
    const sigmoid = x => 1 / (1 + Math.exp(-x));

    const sum = inputs.reduce((a, v, i) => a + v * weights[i], 0) + bias;
    const activated = sigmoid(sum);

    // ── Network layout ──
    const layers = [3, 4, 4, 2];
    const netNodes = [];
    layers.forEach((count, li) => {
      for (let ni = 0; ni < count; ni++) {
        netNodes.push({
          layer: li,
          x: 60 + li * (W - 120) / (layers.length - 1),
          y: H / 2 + (ni - (count - 1) / 2) * 52,
          pulse: Math.random() * Math.PI * 2,
        });
      }
    });

    const netEdges = [];
    netNodes.forEach((a, ai) => {
      netNodes.forEach((b, bi) => {
        if (b.layer === a.layer + 1) netEdges.push({ a: ai, b: bi, w: Math.random() * 0.7 + 0.3, ph: Math.random() * Math.PI * 2 });
      });
    });

    const makeParticle = idx => ({ x: 28, iy: H / 2 + (idx - 1) * 70, ty: H / 2 + (idx - 1) * 22, idx, done: false });

    const draw = () => {
      const s = stateRef.current;
      const t = s.t;
      ctx.clearRect(0, 0, W, H);

      if (s.phase < 2) {
        // ── Single Neuron ──
        const cx = W / 2, cy = H / 2, R = 44;
        const alpha = s.phase === 1 ? Math.max(0, 1 - s.expandProgress * 2) : 1;

        inputs.forEach((val, i) => {
          const iy = cy + (i - 1) * 70;
          const ty = cy + (i - 1) * 22;
          const lw = 1 + weights[i] * 3;
          ctx.beginPath(); ctx.moveTo(28, iy); ctx.lineTo(cx - R, ty);
          ctx.strokeStyle = C.cyan + Math.round(alpha * 0x55).toString(16).padStart(2, "0");
          ctx.lineWidth = lw; ctx.stroke();
          ctx.fillStyle = C.cyan + Math.round(alpha * 220).toString(16).padStart(2, "0");
          ctx.font = "bold 10px 'Orbitron', monospace"; ctx.fillText(`x${i+1}=${val}`, 2, iy + 4);
          // weight badge
          const mx = (28 + cx - R) / 2 - 6, my = (iy + ty) / 2;
          ctx.fillStyle = C.gold + Math.round(alpha * 200).toString(16).padStart(2, "0");
          ctx.font = "9px 'Orbitron', monospace"; ctx.fillText(`w=${weights[i]}`, mx, my - 4);
        });

        // particles
        s.particles.forEach(p => {
          const progress = (p.x - 28) / (cx - R - 28);
          const cy2 = p.iy + (p.ty - p.iy) * Math.min(progress, 1);
          if (!p.done) p.x += 2.5;
          if (p.x >= cx - R) p.done = true;
          const pg = ctx.createRadialGradient(p.x, cy2, 0, p.x, cy2, 8);
          pg.addColorStop(0, C.cyan + "ff"); pg.addColorStop(1, "transparent");
          ctx.fillStyle = pg; ctx.beginPath(); ctx.arc(p.x, cy2, 8, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(p.x, cy2, 2.5, 0, Math.PI * 2); ctx.fillStyle = "#fff"; ctx.fill();
        });
        s.particles = s.particles.filter(p => !p.done);

        // Neuron glow
        const ng = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 2.8);
        ng.addColorStop(0, C.purple + Math.round(alpha * 0x28).toString(16).padStart(2, "0"));
        ng.addColorStop(1, "transparent");
        ctx.fillStyle = ng; ctx.beginPath(); ctx.arc(cx, cy, R * 2.8, 0, Math.PI * 2); ctx.fill();

        const pulse = 0.7 + 0.3 * Math.sin(t * 0.05);
        ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(139,92,246,0.14)`; ctx.fill();
        ctx.strokeStyle = C.purple + Math.round(pulse * alpha * 200).toString(16).padStart(2, "0");
        ctx.lineWidth = 2; ctx.stroke();

        // inside text
        ctx.textAlign = "center";
        ctx.fillStyle = C.white + Math.round(alpha * 200).toString(16).padStart(2, "0");
        ctx.font = "bold 10px 'Orbitron', monospace"; ctx.fillText(`Σ=${sum.toFixed(2)}`, cx, cy - 6);
        ctx.fillStyle = C.emerald + Math.round(alpha * 200).toString(16).padStart(2, "0");
        ctx.fillText(`σ=${activated.toFixed(2)}`, cx, cy + 10);
        ctx.fillStyle = C.orange + Math.round(alpha * 180).toString(16).padStart(2, "0");
        ctx.font = "9px 'Orbitron', monospace"; ctx.fillText(`b+${bias}`, cx, cy + R + 16);
        ctx.textAlign = "left";

        // output line
        const op = 0.5 + 0.5 * Math.sin(t * 0.07);
        ctx.beginPath(); ctx.moveTo(cx + R, cy); ctx.lineTo(W - 30, cy);
        ctx.strokeStyle = C.emerald + Math.round(op * alpha * 220).toString(16).padStart(2, "0");
        ctx.lineWidth = 2 + op; ctx.stroke();
        const og = ctx.createRadialGradient(W - 30, cy, 0, W - 30, cy, 14);
        og.addColorStop(0, C.emerald + Math.round(op * alpha * 255).toString(16).padStart(2, "0"));
        og.addColorStop(1, "transparent");
        ctx.fillStyle = og; ctx.beginPath(); ctx.arc(W - 30, cy, 14, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = C.emerald + Math.round(alpha * 210).toString(16).padStart(2, "0");
        ctx.font = "bold 10px 'Orbitron', monospace"; ctx.fillText(`out=${activated.toFixed(2)}`, W - 92, cy - 7);
      }

      if (s.phase >= 1) {
        // ── Network (fade in) ──
        const na = s.phase === 1 ? Math.min((s.expandProgress - 0.3) / 0.7, 1) : 1;
        if (na <= 0) { rafRef.current = requestAnimationFrame(draw); return; }

        netEdges.forEach(e => {
          const A = netNodes[e.a], B = netNodes[e.b];
          const fp = ((t * 0.018 + e.ph) % 1 + 1) % 1;
          const fx = A.x + (B.x - A.x) * fp, fy = A.y + (B.y - A.y) * fp;
          ctx.beginPath(); ctx.moveTo(A.x, A.y); ctx.lineTo(B.x, B.y);
          ctx.strokeStyle = C.purple + Math.round(na * 0x28).toString(16).padStart(2, "0");
          ctx.lineWidth = e.w * 1.5; ctx.stroke();
          const pg = ctx.createRadialGradient(fx, fy, 0, fx, fy, 5);
          pg.addColorStop(0, C.cyan + Math.round(na * 0xaa).toString(16).padStart(2, "0")); pg.addColorStop(1, "transparent");
          ctx.fillStyle = pg; ctx.beginPath(); ctx.arc(fx, fy, 5, 0, Math.PI * 2); ctx.fill();
        });

        netNodes.forEach(n => {
          const p = 0.6 + 0.4 * Math.sin(t * 0.04 + n.pulse);
          const r = n.layer === layers.length - 1 ? 18 : 13;
          const ng = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, r * 2.2);
          ng.addColorStop(0, C.purple + Math.round(na * p * 0x50).toString(16).padStart(2, "0")); ng.addColorStop(1, "transparent");
          ctx.fillStyle = ng; ctx.beginPath(); ctx.arc(n.x, n.y, r * 2.2, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(139,92,246,${na * 0.16})`; ctx.fill();
          ctx.strokeStyle = C.purple + Math.round(na * p * 0xbb).toString(16).padStart(2, "0");
          ctx.lineWidth = 1.5; ctx.stroke();
        });

        ["Input", "Hidden", "Hidden", "Output"].forEach((lbl, li) => {
          const lnodes = netNodes.filter(n => n.layer === li);
          const lx = lnodes[0].x; const ly = Math.min(...lnodes.map(n => n.y)) - 22;
          ctx.fillStyle = C.dimmer + Math.round(na * 200).toString(16).padStart(2, "0");
          ctx.font = "8px 'Orbitron', monospace"; ctx.textAlign = "center";
          ctx.fillText(lbl.toUpperCase(), lx, ly); ctx.textAlign = "left";
        });
      }
    };

    const tick = () => {
      const s = stateRef.current;
      s.t++;
      s.particleTimer++;
      if (s.phase === 0 && s.particleTimer % 55 === 0) {
        inputs.forEach((_, i) => s.particles.push(makeParticle(i)));
      }
      if (s.phase === 1) {
        s.expandProgress = Math.min(s.expandProgress + 0.014, 1);
        if (s.expandProgress >= 1) { s.phase = 2; setPhase(2); }
      }
      draw();
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  const handleExpand = () => {
    if (stateRef.current.phase === 0) { stateRef.current.phase = 1; setPhase(1); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <canvas ref={canvasRef} style={{ width: 480, maxWidth: "100%", height: 300, borderRadius: 14, background: "rgba(0,0,0,0.35)", border: "1px solid rgba(139,92,246,0.25)", display: "block" }} />
      <AnimatePresence mode="wait">
        {phase < 2 ? (
          <motion.button key="btn" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={handleExpand}
            style={{ alignSelf: "flex-start", padding: "7px 20px", borderRadius: 100, border: `1px solid ${C.purple}55`, background: C.purple + "18", color: C.purple, fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.14em", cursor: "pointer", textTransform: "uppercase" }}>
            {phase === 0 ? "Expand to Network →" : "Expanding…"}
          </motion.button>
        ) : (
          <motion.div key="hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{ color: C.purple + "88", fontSize: 9, fontFamily: "'Orbitron', monospace", letterSpacing: "0.15em" }}>
            HOVER NEURONS TO REPLAY · PARTICLES SHOW DATA FLOW
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── 2. DEEP LEARNING VIZ ─────────────────────────────────────────────────────
function DeepLearningViz() {
  const containerRef = useRef(null);
  const [activeLayer, setActiveLayer] = useState(-1);
  const [auto, setAuto] = useState(false);
  const timerRef = useRef(null);

  const layers = [
    { label: "Layer 1", sub: "Edge Detection", col: C.blue,    desc: "Pixels → gradients. Raw contrast differences light up wherever brightness changes." },
    { label: "Layer 2", sub: "Shape Assembly", col: C.cyan,    desc: "Edges combine into curves, corners, and contours. The outline of ears begins to form." },
    { label: "Layer 3", sub: "Part Detection", col: C.purple,  desc: "Curves assemble into recognisable parts — triangular ear silhouettes, circular eyes." },
    { label: "Layer 4", sub: "Face Composition", col: C.pink,  desc: "Parts spatially combine. The model now holds a high-level face representation." },
    { label: "Layer 5", sub: "\"Cat\"", col: C.emerald,        desc: "The final layer collapses everything into a single concept: Cat. 99.2% confidence." },
  ];

  // Start auto-advance on mount
  useEffect(() => { setAuto(true); }, []);

  const paused = useContext(AnimContext);

  // Auto-advance through layers
  useEffect(() => {
    if (!auto || paused) return;
    const advance = () => {
      setActiveLayer(prev => {
        const next = prev + 1;
        if (next >= layers.length) { setAuto(false); return prev; }
        return next;
      });
    };
    timerRef.current = setInterval(advance, 1400);
    return () => clearInterval(timerRef.current);
  }, [auto, paused]);

  // Simple pixel art cat face (16×16 grid, 1=fur,2=eye,3=nose,4=mouth,5=ear)
  const catGrid = [
    [0,0,0,0,1,1,0,0,0,0,1,1,0,0,0,0],
    [0,0,0,1,1,1,1,0,0,1,1,1,1,0,0,0],
    [0,0,1,5,1,1,1,0,0,1,1,1,5,1,0,0],
    [0,1,5,5,1,1,1,1,1,1,1,1,5,5,1,0],
    [1,1,5,1,1,1,1,1,1,1,1,1,1,5,1,1],
    [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
    [1,1,2,2,1,1,1,1,1,1,1,1,2,2,1,1],
    [1,1,2,2,1,1,1,1,1,1,1,1,2,2,1,1],
    [1,1,1,1,1,1,3,1,3,1,1,1,1,1,1,1],
    [1,1,1,1,1,1,1,4,1,1,1,1,1,1,1,1],
    [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
    [0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0],
    [0,0,1,1,1,1,1,1,1,1,1,1,1,1,0,0],
    [0,0,0,1,1,1,1,1,1,1,1,1,1,0,0,0],
    [0,0,0,0,1,1,1,1,1,1,1,1,0,0,0,0],
    [0,0,0,0,0,1,1,1,1,1,1,0,0,0,0,0],
  ];

  const cellColors = { 0: "transparent", 1: "#c8a882", 2: "#2a7a3b", 3: "#e8917a", 4: "#c45840", 5: "#8b6a4a" };
  const CELL = 9;

  // What each layer highlights (which cell types)
  const highlights = [
    [1, 5],   // Layer 1 - all edges (every boundary)
    [5],      // Layer 2 - ears (shapes)
    [2, 5],   // Layer 3 - eyes + ears
    [2, 3, 4, 5], // Layer 4 - full face features
    [1, 2, 3, 4, 5], // Layer 5 - everything
  ];

  const getHighlightCol = (layerIdx, cellVal) => {
    if (layerIdx < 0) return null;
    if (highlights[layerIdx]?.includes(cellVal)) return layers[layerIdx].col;
    return null;
  };

  return (
    <div ref={containerRef} className="ai-deep-learning-viz" style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
      {/* Cat pixel art */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, letterSpacing: "0.15em", marginBottom: 8 }}>INPUT IMAGE</div>
        <div style={{ display: "inline-block", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, overflow: "hidden", background: "#1a1a1a" }}>
          {catGrid.map((row, ri) => (
            <div key={ri} style={{ display: "flex" }}>
              {row.map((cell, ci) => {
                const hl = getHighlightCol(activeLayer, cell);
                return (
                  <div key={ci} style={{
                    width: CELL, height: CELL,
                    background: hl ? hl : cell === 0 ? "transparent" : cellColors[cell],
                    boxShadow: hl ? `0 0 4px ${hl}` : "none",
                    transition: "background 0.4s, box-shadow 0.4s",
                  }} />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Layer stack */}
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, letterSpacing: "0.15em", marginBottom: 8 }}>FEATURE EXTRACTION</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {layers.map((layer, i) => {
            const isActive = activeLayer === i;
            const isPast = activeLayer > i;
            return (
              <motion.div key={i} animate={{ opacity: i > activeLayer + 1 ? 0.35 : 1 }}
                onClick={() => { setAuto(false); setActiveLayer(i); }}
                style={{
                  padding: "10px 14px", borderRadius: 10, cursor: "pointer",
                  background: isActive ? layer.col + "22" : isPast ? layer.col + "0e" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${isActive ? layer.col + "88" : isPast ? layer.col + "38" : "rgba(255,255,255,0.08)"}`,
                  transition: "all 0.35s",
                  boxShadow: isActive ? `0 0 20px ${layer.col}28` : "none",
                }}>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: isActive ? 6 : 0 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: layer.col, flexShrink: 0, boxShadow: isActive ? `0 0 8px ${layer.col}` : "none", transition: "box-shadow 0.3s" }} />
                  <span style={{ color: C.white, fontSize: 14, fontWeight: 700, fontFamily: "'Raleway', sans-serif" }}>{layer.label}</span>
                  <span style={{ color: layer.col, fontSize: 12, marginLeft: 4 }}>{layer.sub}</span>
                </div>
                <div style={{ minHeight: 60, overflow: "hidden" }}>
                  <AnimatePresence>
                    {isActive && (
                      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        style={{ color: C.dim, fontSize: 13, lineHeight: 1.6, margin: 0, paddingLeft: 15 }}>
                        {layer.desc}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>
        {activeLayer === layers.length - 1 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            style={{ marginTop: 12, padding: "10px 14px", borderRadius: 10, background: C.emerald + "18", border: `1px solid ${C.emerald}50` }}>
            <div style={{ color: C.emerald, fontFamily: "'Orbitron', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em" }}>
              🐱 CAT — 99.2% confidence
            </div>
            <div style={{ color: C.dim, fontSize: 13, marginTop: 4 }}>
              5 layers · {layers.length * 16 * 16} feature activations processed
            </div>
          </motion.div>
        )}
        <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button onClick={() => { setActiveLayer(-1); setAuto(true); }}
            style={{ padding: "5px 14px", borderRadius: 100, border: `1px solid ${C.blue}44`, background: C.blue + "12", color: C.blue, fontSize: 9, fontFamily: "'Orbitron', monospace", cursor: "pointer", letterSpacing: "0.1em" }}>
            REPLAY
          </button>
          <span style={{ color: C.dimmer, fontSize: 10, alignSelf: "center", fontFamily: "'Orbitron', monospace" }}>OR CLICK ANY LAYER</span>
        </div>
      </div>
    </div>
  );
}

// ─── 3. LLM FLOW VIZ ─────────────────────────────────────────────────────────
const LLM_STAGES = ["Text", "Tokens", "Vectors", "Transformer", "Next Token"];
const LLM_SENTENCE = "The cat sat";
const LLM_TOKENS = [{ t: "The", col: C.cyan }, { t: " cat", col: C.purple }, { t: " sat", col: C.pink }];
const LLM_NEXT = ["on", "still", "quietly", "down", "there"];

function LLMFlowViz() {
  const [stage, setStage] = useState(0);
  const [nextIdx, setNextIdx] = useState(0);
  const containerRef = useRef(null);
  const paused = useContext(AnimContext);
  const pausedRef = useRef(paused);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  const startedRef = useRef(false);
  const stageRef = useRef(0);
  const nextIdxRef = useRef(0);

  const cycleNext = (i) => {
    if (pausedRef.current) return;
    const idx = i % LLM_NEXT.length;
    setNextIdx(idx);
    nextIdxRef.current = idx;
    setTimeout(() => cycleNext(i + 1), 900);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !startedRef.current) {
        startedRef.current = true;
        io.disconnect();
        let s = 0;
        const advance = () => {
          if (pausedRef.current) return;
          s++;
          setStage(s);
          stageRef.current = s;
          if (s < LLM_STAGES.length - 1) setTimeout(advance, 1200);
          else { setNextIdx(0); nextIdxRef.current = 0; cycleNext(0); }
        };
        setTimeout(advance, 800);
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Resume cycling when unpaused (if stages already finished)
  useEffect(() => {
    if (!paused && startedRef.current && stageRef.current >= LLM_STAGES.length - 1) {
      cycleNext(nextIdxRef.current + 1);
    }
  }, [paused]);

  const stageColors = [C.white, C.cyan, C.purple, C.orange, C.emerald];

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {/* Pipeline label strip */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0, marginBottom: 28, overflowX: "auto" }}>
        {LLM_STAGES.map((s, i) => (
          <div key={i} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0, flexShrink: 0 }}>
            <motion.div
              animate={{ opacity: stage >= i ? 1 : 0.25, scale: stage === i ? 1.08 : 1 }}
              transition={{ duration: 0.4 }}
              style={{
                padding: "7px 14px", borderRadius: 8,
                background: stage >= i ? stageColors[i] + "22" : "rgba(255,255,255,0.04)",
                border: `1px solid ${stage >= i ? stageColors[i] + "66" : "rgba(255,255,255,0.1)"}`,
                color: stage >= i ? stageColors[i] : C.dimmer,
                fontSize: 11, fontFamily: "'Orbitron', monospace", letterSpacing: "0.08em",
                whiteSpace: "nowrap", fontWeight: stage === i ? 700 : 400,
              }}>
              {s}
            </motion.div>
            {i < LLM_STAGES.length - 1 && (
              <motion.div animate={{ opacity: stage > i ? 1 : 0.2 }} transition={{ duration: 0.5 }}
                style={{ width: 24, height: 1, background: `linear-gradient(90deg, ${stageColors[i]}, ${stageColors[i+1]})`, flexShrink: 0 }} />
            )}
          </div>
        ))}
      </div>

      {/* Stage visualizations */}
      <div style={{ minHeight: 120, position: "relative" }}>
        <AnimatePresence mode="wait">
          {stage === 0 && (
            <motion.div key="text" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
              style={{ fontSize: "clamp(22px,3vw,36px)", color: C.white, fontFamily: "'Raleway', sans-serif", fontWeight: 700, letterSpacing: "-0.01em" }}>
              "{LLM_SENTENCE}"
            </motion.div>
          )}
          {stage === 1 && (
            <motion.div key="tokens" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
              style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {LLM_TOKENS.map((tok, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.15 }}
                  style={{ padding: "8px 16px", borderRadius: 10, background: tok.col + "22", border: `1.5px solid ${tok.col}55`, color: tok.col, fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 18, whiteSpace: "pre" }}>
                  {tok.t}
                </motion.div>
              ))}
            </motion.div>
          )}
          {stage === 2 && (
            <motion.div key="vectors" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
              style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              {/* 2D semantic space */}
              <svg viewBox="0 0 180 120" width="180" height="120" style={{ flexShrink: 0, borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.2)" }}>
                {/* Axes */}
                <line x1={10} y1={110} x2={170} y2={110} stroke="rgba(255,255,255,0.1)" strokeWidth={1}/>
                <line x1={10} y1={10} x2={10} y2={110} stroke="rgba(255,255,255,0.1)" strokeWidth={1}/>
                <text x={90} y={118} textAnchor="middle" fontSize={6} fontFamily="'Orbitron', monospace" fill={C.dimmer}>semantic space</text>
                {/* Cluster hints */}
                <circle cx={30} cy={40} r={22} fill={C.cyan + "08"} />
                <circle cx={130} cy={75} r={28} fill={C.purple + "08"} />
                {/* Token points */}
                {[
                  { label: "The", x: 30, y: 38, col: C.cyan, note: "article" },
                  { label: "cat", x: 120, y: 58, col: C.purple, note: "noun" },
                  { label: "sat", x: 140, y: 88, col: C.pink, note: "verb" },
                ].map((t, i) => (
                  <motion.g key={t.label} initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.2 }}>
                    <circle cx={t.x} cy={t.y} r={5} fill={t.col + "33"} stroke={t.col} strokeWidth={1.5} style={{ filter: `drop-shadow(0 0 4px ${t.col}88)` }}/>
                    <text x={t.x + 8} y={t.y - 4} fontSize={8} fontFamily="'Orbitron', monospace" fill={t.col} fontWeight="700">{t.label}</text>
                    <text x={t.x + 8} y={t.y + 6} fontSize={6} fontFamily="'Orbitron', monospace" fill={C.dimmer}>{t.note}</text>
                  </motion.g>
                ))}
                {/* Similarity line between cat & sat (syntactically related) */}
                <motion.line initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
                  x1={120} y1={58} x2={140} y2={88} stroke={C.pink + "44"} strokeWidth={1} strokeDasharray="3 2"/>
              </svg>
              <div>
                <div style={{ color: C.purple, fontSize: 11, fontFamily: "'Orbitron', monospace", marginBottom: 6 }}>768-DIM VECTORS</div>
                <div style={{ color: C.dim, fontSize: 12, lineHeight: 1.6, maxWidth: 160 }}>
                  Each token becomes a point in high-dimensional space. Similar meanings cluster together.
                </div>
              </div>
            </motion.div>
          )}
          {stage === 3 && (
            <motion.div key="transformer" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
              style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              {Array.from({ length: 6 }, (_, i) => (
                <motion.div key={i} initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ delay: i * 0.1 }}
                  style={{ width: 38, borderRadius: 8, background: C.orange + "18", border: `1px solid ${C.orange}44`, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
                  <motion.div animate={{ y: [0, -80, -80, 0] }} transition={{ repeat: Infinity, duration: 1.8, delay: i * 0.28, ease: "easeInOut" }}
                    style={{ width: "100%", padding: "8px 4px", textAlign: "center" }}>
                    <div style={{ fontSize: 7, color: C.orange, fontFamily: "'Orbitron', monospace", letterSpacing: "0.06em", lineHeight: 1.4 }}>
                      ATT<br />N
                    </div>
                    <div style={{ height: 40 }} />
                    <div style={{ fontSize: 7, color: C.orange, fontFamily: "'Orbitron', monospace", letterSpacing: "0.06em", lineHeight: 1.4 }}>
                      FFN
                    </div>
                  </motion.div>
                </motion.div>
              ))}
              <div style={{ color: C.dimmer, fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.05em" }}>
                6 transformer layers
              </div>
            </motion.div>
          )}
          {stage >= 4 && (
            <motion.div key="output" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ color: C.dim, fontFamily: "'Raleway', sans-serif", fontSize: 18, fontWeight: 600 }}>
                "{LLM_SENTENCE}"
              </div>
              <motion.div animate={{ x: [0, 8, 0], opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 1.2 }}
                style={{ color: C.emerald, fontSize: 22 }}>→</motion.div>
              <AnimatePresence mode="wait">
                <motion.div key={nextIdx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  style={{ padding: "8px 20px", borderRadius: 12, background: C.emerald + "25", border: `1.5px solid ${C.emerald}66`, color: C.emerald, fontSize: 22, fontFamily: "'Raleway', sans-serif", fontWeight: 700, boxShadow: `0 0 20px ${C.emerald}30` }}>
                  {LLM_NEXT[nextIdx]}
                </motion.div>
              </AnimatePresence>
              <div style={{ color: C.dimmer, fontSize: 10, fontFamily: "'Orbitron', monospace" }}>cycling predictions</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ─── 4. LOGITS + SOFTMAX VIZ ─────────────────────────────────────────────────
const LOGIT_WORDS = [
  { word: "cat",   logit: 4.2, col: C.orange  },
  { word: "dog",   logit: 2.8, col: C.blue    },
  { word: "house", logit: 1.1, col: C.purple  },
  { word: "pizza", logit: 0.3, col: C.pink    },
  { word: "car",   logit: -0.9, col: C.cyan   },
];

function LogitsSoftmaxViz() {
  const [mode, setMode] = useState("logits"); // logits | softmax
  const containerRef = useRef(null);

  const maxLogit = Math.max(...LOGIT_WORDS.map(w => w.logit));
  const minLogit = Math.min(...LOGIT_WORDS.map(w => w.logit));
  const logitRange = maxLogit - minLogit;

  // Compute softmax
  const exp = LOGIT_WORDS.map(w => Math.exp(w.logit));
  const expSum = exp.reduce((a, b) => a + b, 0);
  const probs = exp.map(e => e / expSum);

  const maxProb = Math.max(...probs);

  const logitHeight = (w) => Math.max(8, ((w.logit - minLogit) / logitRange) * 160);
  const probHeight = (p) => Math.max(8, (p / maxProb) * 160);

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {/* Bars */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end", height: 200, paddingBottom: 0, marginBottom: 20 }}>
        {LOGIT_WORDS.map((w, i) => {
          const h = mode === "logits" ? logitHeight(w) : probHeight(probs[i]);
          const label = mode === "logits"
            ? `${w.logit > 0 ? "+" : ""}${w.logit.toFixed(1)}`
            : `${(probs[i] * 100).toFixed(1)}%`;
          return (
            <div key={w.word} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, flex: 1 }}>
              {/* Value label */}
              <motion.div animate={{ opacity: 1 }} style={{ fontSize: 11, fontFamily: "'Orbitron', monospace", color: w.col, whiteSpace: "nowrap" }}>
                {label}
              </motion.div>
              {/* Bar */}
              <div style={{ width: "100%", display: "flex", flexWrap: "wrap", alignItems: "flex-end", height: 160 }}>
                <motion.div
                  animate={{ height: h }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  style={{ width: "100%", borderRadius: "6px 6px 0 0", background: `linear-gradient(to top, ${w.col}, ${w.col}88)`, boxShadow: `0 0 16px ${w.col}44`, position: "relative", overflow: "hidden" }}>
                  {/* Shimmer */}
                  <motion.div animate={{ y: ["-100%", "200%"] }} transition={{ repeat: Infinity, duration: 2, delay: i * 0.3, ease: "linear" }}
                    style={{ position: "absolute", top: 0, left: 0, right: 0, height: "40%", background: `linear-gradient(to bottom, transparent, ${w.col}44, transparent)` }} />
                </motion.div>
              </div>
              {/* Word label */}
              <div style={{ color: C.white, fontSize: 14.5, fontFamily: "'Raleway', sans-serif", fontWeight: 700 }}>{w.word}</div>
            </div>
          );
        })}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 0, borderRadius: 100, border: "1px solid rgba(255,255,255,0.12)", overflow: "hidden" }}>
          {["logits", "softmax"].map(m => (
            <button key={m} onClick={() => setMode(m)}
              style={{ padding: "7px 18px", fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", border: "none", background: mode === m ? C.gold + "30" : "transparent", color: mode === m ? C.gold : C.dimmer, transition: "all 0.25s", fontWeight: mode === m ? 700 : 400 }}>
              {m === "logits" ? "Raw Logits" : "After Softmax"}
            </button>
          ))}
        </div>
        <div style={{ color: C.dim, fontSize: 14, lineHeight: 1.5 }}>
          {mode === "logits"
            ? "Raw scores — can be any value, don't sum to 1."
            : `Probabilities — sum to 100%. "cat" wins at ${(probs[0]*100).toFixed(1)}%.`}
        </div>
      </div>
    </div>
  );
}

// ─── 5. TEMPERATURE VIZ ──────────────────────────────────────────────────────
const TEMP_WORDS = [
  { word: "cat",    baseLogit: 3.8, col: C.orange  },
  { word: "dog",    baseLogit: 2.4, col: C.blue    },
  { word: "bird",   baseLogit: 1.0, col: C.purple  },
  { word: "mouse",  baseLogit: 0.1, col: C.cyan    },
  { word: "dragon", baseLogit: -1.2, col: C.pink   },
];

function TemperatureViz() {
  const [temp, setTemp] = useState(1.0);
  const containerRef = useRef(null);

  // Compute probabilities at given temperature
  const computeProbs = (t) => {
    const scaled = TEMP_WORDS.map(w => w.baseLogit / t);
    const exp = scaled.map(x => Math.exp(Math.min(x, 20))); // clamp to avoid Infinity
    const sum = exp.reduce((a, b) => a + b, 0);
    return exp.map(e => e / sum);
  };

  const probs = computeProbs(temp);
  const maxP = Math.max(...probs);

  const tempDesc = temp < 0.5
    ? "Very deterministic — model almost always picks the top token."
    : temp < 0.9
    ? "Focused — top tokens dominate, some variety."
    : temp < 1.4
    ? "Balanced — creative but coherent."
    : "High entropy — surprising tokens become likely. Unpredictable.";

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {/* Bars */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", height: 180, marginBottom: 20 }}>
        {TEMP_WORDS.map((w, i) => {
          const h = Math.max(6, (probs[i] / maxP) * 150);
          return (
            <div key={w.word} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, flex: 1 }}>
              <div style={{ fontSize: 10, fontFamily: "'Orbitron', monospace", color: w.col }}>{(probs[i] * 100).toFixed(1)}%</div>
              <div style={{ width: "100%", display: "flex", flexWrap: "wrap", alignItems: "flex-end", height: 150 }}>
                <motion.div
                  animate={{ height: h }}
                  transition={{ duration: 0.2 }}
                  style={{ width: "100%", borderRadius: "5px 5px 0 0", background: `linear-gradient(to top, ${w.col}, ${w.col}66)`, boxShadow: probs[i] === maxP ? `0 0 20px ${w.col}55` : "none" }} />
              </div>
              <div style={{ color: probs[i] === maxP ? w.col : C.dim, fontSize: 13, fontFamily: "'Raleway', sans-serif", fontWeight: probs[i] === maxP ? 700 : 400 }}>{w.word}</div>
            </div>
          );
        })}
      </div>

      {/* Temperature slider */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, letterSpacing: "0.15em" }}>TEMPERATURE</div>
            <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Orbitron', monospace", color: C.pink }}>{temp.toFixed(2)}</div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {[0.1, 0.5, 1.0, 1.5, 2.0].map(t => (
              <button key={t} onClick={() => setTemp(t)}
                style={{ padding: "3px 10px", borderRadius: 100, fontSize: 9, fontFamily: "'Orbitron', monospace", cursor: "pointer", border: `1px solid ${Math.abs(temp - t) < 0.05 ? C.pink + "88" : "rgba(255,255,255,0.1)"}`, background: Math.abs(temp - t) < 0.05 ? C.pink + "20" : "transparent", color: Math.abs(temp - t) < 0.05 ? C.pink : C.dimmer }}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <input type="range" min="0.05" max="2.5" step="0.05" value={temp}
          onChange={e => setTemp(parseFloat(e.target.value))}
          style={{ width: "100%", accentColor: C.pink, height: 4, cursor: "pointer" }} />
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", fontSize: 8, fontFamily: "'Orbitron', monospace", color: C.dimmer, marginTop: 4 }}>
          <span>0.05 (deterministic)</span><span>2.5 (chaotic)</span>
        </div>
      </div>

      <div style={{ padding: "10px 14px", borderRadius: 10, background: C.pink + "12", border: `1px solid ${C.pink}30` }}>
        <div style={{ color: C.dim, fontSize: 14, lineHeight: 1.6 }}>{tempDesc}</div>
      </div>
    </div>
  );
}

// ─── 7. KV CACHE VIZ ─────────────────────────────────────────────────────────
const KV_TOKENS = ["The", "cat", "sat", "on", "the", "mat"];

function KVCacheViz() {
  const paused = useContext(AnimContext);
  const [mode, setMode] = useState("before"); // before | after
  const [step, setStep] = useState(0);
  const [running, setRunning] = useState(false);
  const timerRef = useRef(null);

  const runAnimation = (m) => {
    if (running) return;
    setRunning(true);
    setStep(0);
    let s = 0;
    const totalSteps = m === "before" ? KV_TOKENS.length * 2 : KV_TOKENS.length + 2;
    timerRef.current = setInterval(() => {
      s++;
      setStep(s);
      if (s >= totalSteps) { clearInterval(timerRef.current); setRunning(false); }
    }, 500);
  };

  useEffect(() => {
    if (paused) { clearInterval(timerRef.current); setRunning(false); return; }
    runAnimation(mode);
    return () => clearInterval(timerRef.current);
  }, [mode, paused]);

  const totalTokens = KV_TOKENS.length;

  return (
    <div className="ai-kv-cache-viz" style={{ width: "100%" }}>
      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 0, borderRadius: 100, border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden", width: "fit-content", maxWidth: "100%", marginBottom: 24 }}>
        {["before", "after"].map(m => (
          <button key={m} onClick={() => { if (!running) setMode(m); }}
            style={{ padding: "7px clamp(10px,3vw,20px)", fontSize: "clamp(8.5px,2.4vw,10px)", whiteSpace: "nowrap", fontFamily: "'Orbitron', monospace", letterSpacing: "0.1em", textTransform: "uppercase", cursor: running ? "wait" : "pointer", border: "none", background: mode === m ? C.emerald + "28" : "transparent", color: mode === m ? C.emerald : C.dimmer, transition: "all 0.25s", fontWeight: mode === m ? 700 : 400 }}>
            {m === "before" ? "Without Cache" : "With KV Cache"}
          </button>
        ))}
      </div>

      {mode === "before" ? (
        /* Without cache: every new token recomputes everything */
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {KV_TOKENS.map((tok, ti) => {
            const visible = step > ti * 2;
            const computing = step === ti * 2 + 1;
            return (
              <motion.div key={ti} initial={{ opacity: 0, x: -20 }} animate={{ opacity: visible ? 1 : 0, x: visible ? 0 : -20 }} transition={{ duration: 0.3 }}
                style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                <div style={{ width: 60, textAlign: "right", color: C.dim, fontSize: 14, fontFamily: "'Raleway', sans-serif" }}>{tok}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {KV_TOKENS.slice(0, ti + 1).map((t2, j) => (
                    <motion.div key={j}
                      animate={{ background: computing ? C.pink + "40" : C.pink + "15", borderColor: computing ? C.pink + "bb" : C.pink + "44", scale: computing ? 1.05 : 1 }}
                      transition={{ duration: 0.2 }}
                      style={{ width: 32, height: 28, borderRadius: 6, border: `1px solid ${C.pink}44`, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", fontSize: 8, fontFamily: "'Orbitron', monospace", color: C.pink }}>
                      K,V
                    </motion.div>
                  ))}
                </div>
                {visible && (
                  <div style={{ fontSize: 9, color: computing ? C.pink : C.dimmer, fontFamily: "'Orbitron', monospace" }}>
                    {computing ? `⟳ recomputing ${ti + 1} K,V pairs` : `✓ done (${ti + 1} ops)`}
                  </div>
                )}
              </motion.div>
            );
          })}
          {step > 0 && (
            <div style={{ marginTop: 8, padding: "8px 14px", borderRadius: 8, background: C.pink + "12", border: `1px solid ${C.pink}30` }}>
              <div style={{ color: C.pink, fontSize: 11, fontFamily: "'Orbitron', monospace" }}>
                Total ops: {Array.from({ length: Math.min(step / 2, totalTokens) }, (_, i) => i + 1).reduce((a, b) => a + b, 0)} K,V computations
              </div>
            </div>
          )}
        </div>
      ) : (
        /* With cache: compute once, read from cache */
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Cache bank */}
          <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(0,0,0,0.3)", border: `1px solid ${C.emerald}33`, marginBottom: 8 }}>
            <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.emerald, letterSpacing: "0.15em", marginBottom: 8 }}>KV CACHE</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {KV_TOKENS.map((tok, ti) => (
                <motion.div key={ti}
                  animate={{ opacity: step > ti ? 1 : 0.15, background: step > ti ? C.emerald + "22" : "transparent", borderColor: step > ti ? C.emerald + "88" : "rgba(255,255,255,0.1)", scale: step === ti + 1 ? 1.12 : 1 }}
                  transition={{ duration: 0.3 }}
                  style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid", fontSize: 12, fontFamily: "'Raleway', sans-serif", color: step > ti ? C.emerald : C.dimmer, fontWeight: 700 }}>
                  {tok}
                  {step > ti && <span style={{ fontSize: 7, fontFamily: "'Orbitron', monospace", opacity: 0.7, display: "block", letterSpacing: "0.08em" }}>K,V ✓</span>}
                </motion.div>
              ))}
            </div>
          </div>

          {/* New token generation */}
          {step >= totalTokens && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, background: C.emerald + "12", border: `1px solid ${C.emerald}44` }}>
              <div style={{ color: C.emerald, fontSize: 18 }}>→</div>
              <div>
                <div style={{ color: C.emerald, fontSize: 14, fontFamily: "'Raleway', sans-serif", fontWeight: 700 }}>Next token reads cache instantly</div>
                <div style={{ color: C.dim, fontSize: 10, fontFamily: "'Orbitron', monospace", marginTop: 2 }}>0 recomputation · O(1) lookup per cached token</div>
              </div>
            </motion.div>
          )}

          {step >= totalTokens + 1 && (
            <div style={{ padding: "8px 14px", borderRadius: 8, background: C.emerald + "12", border: `1px solid ${C.emerald}30` }}>
              <div style={{ color: C.emerald, fontSize: 11, fontFamily: "'Orbitron', monospace" }}>
                Total ops: {totalTokens} K,V computations (vs {Array.from({ length: totalTokens }, (_, i) => i + 1).reduce((a, b) => a + b, 0)} without cache)
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── 8. SELF-SUPERVISED LEARNING VIZ ─────────────────────────────────────────
const SSL_SENTENCES = [
  { words: ["The", "model", "learned", "to", "___", "language"], answer: "understand", idx: 4 },
  { words: ["Paris", "is", "the", "___", "of", "France"], answer: "capital", idx: 3 },
  { words: ["She", "opened", "the", "___", "and", "walked", "in"], answer: "door", idx: 3 },
  { words: ["The", "sun", "rises", "in", "the", "___"], answer: "east", idx: 5 },
];
const SSL_WRONG = [["forget", "skip", "destroy", "compress"], ["city", "museum", "river", "tower"], ["window", "book", "car", "floor"], ["west", "morning", "sky", "cloud"]];

function SelfSupervisedViz() {
  const paused = useContext(AnimContext);
  const pausedRef = useRef(paused);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  const ioFiredRef = useRef(false);

  const [si, setSi] = useState(0);
  const [phase, setPhase] = useState("masked"); // masked | guessing | reveal
  const [guess, setGuess] = useState(null);
  const containerRef = useRef(null);
  const phaseRef = useRef(null);

  const sentence = SSL_SENTENCES[si];
  const wrongs = SSL_WRONG[si];
  const allOptions = [sentence.answer, ...wrongs].sort(() => Math.random() - 0.5);

  const advance = () => {
    if (pausedRef.current) return;
    clearTimeout(phaseRef.current);
    setPhase("masked");
    setGuess(null);
    phaseRef.current = setTimeout(() => {
      if (pausedRef.current) return;
      setPhase("guessing");
      phaseRef.current = setTimeout(() => {
        if (pausedRef.current) return;
        setGuess(sentence.answer);
        setPhase("reveal");
        phaseRef.current = setTimeout(() => {
          if (pausedRef.current) return;
          setSi(s => (s + 1) % SSL_SENTENCES.length);
          setPhase("masked");
          setGuess(null);
          advance();
        }, 2200);
      }, 1500);
    }, 800);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { io.disconnect(); ioFiredRef.current = true; advance(); }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => { io.disconnect(); clearTimeout(phaseRef.current); };
  }, []);

  // Resume when unpaused (IO already fired)
  useEffect(() => {
    if (!paused && ioFiredRef.current) advance();
  }, [paused]);

  useEffect(() => {
    // Reset allOptions when si changes
  }, [si]);

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {/* Sentence display */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 24, minHeight: 50 }}>
        {sentence.words.map((w, wi) => {
          const isMasked = wi === sentence.idx;
          return (
            <motion.div key={`${si}-${wi}`}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: wi * 0.06 }}
              style={{
                padding: "8px 14px", borderRadius: 10,
                background: isMasked
                  ? (phase === "reveal" ? C.emerald + "25" : C.pink + "20")
                  : "rgba(255,255,255,0.06)",
                border: `1.5px solid ${isMasked ? (phase === "reveal" ? C.emerald + "88" : C.pink + "88") : "rgba(255,255,255,0.1)"}`,
                color: isMasked ? (phase === "reveal" ? C.emerald : C.pink) : C.white,
                fontSize: 16, fontFamily: "'Raleway', sans-serif", fontWeight: isMasked ? 800 : 500,
                boxShadow: isMasked && phase === "reveal" ? `0 0 20px ${C.emerald}30` : "none",
                minWidth: isMasked ? 80 : "auto", textAlign: "center",
              }}>
              {isMasked
                ? (phase === "masked" ? "[MASK]" : phase === "guessing" ? "..." : sentence.answer)
                : w}
            </motion.div>
          );
        })}
      </div>

      {/* Model's guess display */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontFamily: "'Orbitron', monospace", color: C.dimmer, letterSpacing: "0.12em" }}>MODEL CONSIDERS:</div>
        {[sentence.answer, ...wrongs.slice(0, 3)].map((opt, oi) => {
          const isCorrect = opt === sentence.answer;
          const prob = isCorrect ? 0.72 : [0.12, 0.09, 0.07][oi - 1] ?? 0.07;
          return (
            <motion.div key={opt}
              animate={{
                opacity: phase === "masked" ? 0.3 : 1,
                scale: phase === "reveal" && isCorrect ? 1.1 : 1,
                background: phase === "reveal" && isCorrect ? C.emerald + "30" : opt === guess ? C.pink + "20" : "rgba(255,255,255,0.06)",
              }}
              transition={{ duration: 0.3 }}
              style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${phase === "reveal" && isCorrect ? C.emerald + "88" : "rgba(255,255,255,0.12)"}`, fontSize: 14, fontFamily: "'Raleway', sans-serif", color: phase === "reveal" && isCorrect ? C.emerald : C.dim }}>
              {opt}
              {phase !== "masked" && (
                <span style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", marginLeft: 5, opacity: 0.7 }}>{(prob * 100).toFixed(0)}%</span>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Status */}
      <AnimatePresence mode="wait">
        <motion.div key={`${si}-${phase}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          style={{ padding: "8px 14px", borderRadius: 8, background: phase === "reveal" ? C.emerald + "12" : C.cyan + "10", border: `1px solid ${phase === "reveal" ? C.emerald + "40" : C.cyan + "30"}`, marginBottom: 16 }}>
          <div style={{ color: phase === "reveal" ? C.emerald : C.cyan, fontSize: 11, fontFamily: "'Orbitron', monospace", letterSpacing: "0.1em" }}>
            {phase === "masked" && "MASKING TOKEN · NO HUMAN LABEL NEEDED"}
            {phase === "guessing" && "PREDICTING FROM CONTEXT..."}
            {phase === "reveal" && `✓ CORRECT · LOSS = 0.12 · WEIGHTS UPDATED`}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Weight update visualization */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, letterSpacing: "0.1em", flexShrink: 0 }}>LAYER WEIGHTS</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: 3, flex: 1, maxWidth: 240 }}>
          {Array.from({ length: 30 }, (_, i) => {
            const seed = (si * 30 + i) * 7;
            const base = ((Math.sin(seed) + 1) / 2);
            const updated = phase === "reveal" ? ((Math.sin(seed + 1.1) + 1) / 2) : base;
            const flash = phase === "reveal" && Math.abs(Math.sin(seed + 1.1) - Math.sin(seed)) > 0.3;
            return (
              <motion.div key={`w-${si}-${i}`}
                animate={{ opacity: flash ? [1, 0.3, 1] : 1 }}
                transition={{ duration: 0.4, repeat: flash ? 1 : 0 }}
                style={{
                  height: 8, borderRadius: 2,
                  background: flash ? C.emerald : `hsl(${200 + updated * 60}, 70%, ${30 + updated * 30}%)`,
                  transition: "background 0.5s",
                  boxShadow: flash ? `0 0 4px ${C.emerald}` : "none",
                }} />
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── 9. PRETRAINING VIZ ──────────────────────────────────────────────────────
const PT_SOURCES = [
  { label: "Web",     count: 67, col: C.blue,    icon: "🌐", delay: 0    },
  { label: "Books",   count: 15, col: C.orange,  icon: "📚", delay: 0.4  },
  { label: "Code",    count: 10, col: C.emerald, icon: "💻", delay: 0.8  },
  { label: "Science", count:  5, col: C.purple,  icon: "🔬", delay: 1.2  },
  { label: "Other",   count:  3, col: C.pink,    icon: "✦",  delay: 1.6  },
];

function PretrainingViz() {
  const paused = useContext(AnimContext);
  const [tokenCount, setTokenCount] = useState(0);
  const counterRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    counterRef.current = setInterval(() => {
      setTokenCount(n => Math.min(n + Math.floor(Math.random() * 8 + 4), 999));
    }, 80);
    return () => clearInterval(counterRef.current);
  }, [paused]);

  return (
    <div style={{ width: "100%" }}>
      {/* Main layout: sources → LLM core */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0, padding: "20px 0" }}>

        {/* Source column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, flexShrink: 0, width: 110 }}>
          {PT_SOURCES.map((src, i) => (
            <motion.div key={src.label}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: src.delay, duration: 0.5 }}
              style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, padding: "7px 12px", borderRadius: 10, background: src.col + "18", border: `1px solid ${src.col}44` }}>
              <span style={{ fontSize: 14 }}>{src.icon}</span>
              <div>
                <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: src.col, letterSpacing: "0.08em" }}>{src.label}</div>
                <div style={{ fontSize: 8, fontFamily: "'Orbitron', monospace", color: C.dimmer }}>{src.count}%</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Flow pipes */}
        <div style={{ flex: 1, position: "relative", height: 220, minWidth: 60 }}>
          <svg width="100%" height="220" style={{ overflow: "visible" }}>
            {PT_SOURCES.map((src, i) => {
              const y = 21 + i * 46; // matches row heights
              const cy = 110;
              return (
                <g key={src.label}>
                  <path d={`M 0 ${y} C 40 ${y}, 60 ${cy}, 100% ${cy}`}
                    fill="none" stroke={src.col} strokeWidth="1.5" strokeOpacity="0.25" />
                  {/* Animated particle */}
                  <motion.circle r="4" fill={src.col}
                    filter={`drop-shadow(0 0 4px ${src.col})`}
                    animate={{ offsetDistance: ["0%", "100%"] }}
                    style={{ offsetPath: `path("M 0 ${y} C 40 ${y}, 60 ${cy}, 100% ${cy}")` }}
                  >
                    <animateMotion dur={`${1.6 + i * 0.3}s`} repeatCount="indefinite" begin={`${src.delay}s`}>
                      <mpath href={`#pt-path-${i}`} />
                    </animateMotion>
                  </motion.circle>
                  <path id={`pt-path-${i}`} d={`M 0 ${y} C 40 ${y}, 60 ${cy}, 100% ${cy}`} fill="none" />
                </g>
              );
            })}
          </svg>
        </div>

        {/* Training step demo */}
        <TrainingStepDemo />
      </div>
    </div>
  );
}

function TrainingStepDemo() {
  const paused = useContext(AnimContext);
  const pausedRef = useRef(paused);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  const STEPS = [
    { text: ["The", "cat", "sat", "on", "the", "[?]"], predict: "mat", actual: "mat", loss: "0.94", correct: false },
    { text: ["Paris", "is", "the", "[?]", "of", "France"], predict: "city", actual: "capital", loss: "1.31", correct: false },
    { text: ["The", "cat", "sat", "on", "the", "[?]"], predict: "mat", actual: "mat", loss: "0.21", correct: true },
  ];

  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState("predict"); // predict | loss | update
  const timerRef = useRef(null);
  const stepRef = useRef(0);

  useEffect(() => {
    const tick = () => {
      if (pausedRef.current) { timerRef.current = setTimeout(tick, 300); return; }
      setPhase(p => {
        if (p === "predict") { timerRef.current = setTimeout(tick, 1000); return "loss"; }
        if (p === "loss") { timerRef.current = setTimeout(tick, 900); return "update"; }
        // update → next step
        const next = (stepRef.current + 1) % STEPS.length;
        stepRef.current = next;
        setStep(next);
        timerRef.current = setTimeout(tick, 700);
        return "predict";
      });
    };
    timerRef.current = setTimeout(tick, 800);
    return () => clearTimeout(timerRef.current);
  }, []);

  const s = STEPS[step];
  const phaseCol = phase === "predict" ? C.cyan : phase === "loss" ? C.pink : C.emerald;

  return (
    <div style={{ flex: 1, minWidth: 180, padding: "12px 14px", borderRadius: 12, background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ fontSize: 8, fontFamily: "'Orbitron', monospace", color: C.dimmer, marginBottom: 10, letterSpacing: "0.12em" }}>TRAINING STEP</div>

      {/* Sentence */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
        {s.text.map((w, i) => {
          const isMask = w === "[?]";
          return (
            <div key={i} style={{ padding: "3px 7px", borderRadius: 5, fontSize: 11,
              background: isMask ? (phase === "predict" ? C.cyan + "22" : phase === "loss" && !s.correct ? C.pink + "22" : C.emerald + "22") : "rgba(255,255,255,0.06)",
              border: `1px solid ${isMask ? (phase === "predict" ? C.cyan + "55" : phase === "loss" && !s.correct ? C.pink + "55" : C.emerald + "55") : "rgba(255,255,255,0.08)"}`,
              color: isMask ? (phase === "predict" ? C.cyan : phase === "loss" && !s.correct ? C.pink : C.emerald) : C.dim,
              fontFamily: "'Raleway', sans-serif", fontWeight: isMask ? 700 : 400 }}>
              {isMask ? (phase === "predict" ? s.predict : s.actual) : w}
            </div>
          );
        })}
      </div>

      {/* Phase indicator */}
      <AnimatePresence mode="wait">
        <motion.div key={phase} initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
          style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: phaseCol, boxShadow: `0 0 6px ${phaseCol}` }} />
          <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: phaseCol, letterSpacing: "0.1em" }}>
            {phase === "predict" && `PREDICTED: "${s.predict}"`}
            {phase === "loss" && `LOSS: ${s.loss} ${s.correct ? "✓" : "↑ updating weights"}`}
            {phase === "update" && "WEIGHTS UPDATED"}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ─── 10. BACKPROPAGATION VIZ ─────────────────────────────────────────────────
const BP_LAYERS = [
  { label: "Input",   nodes: 3, col: C.cyan   },
  { label: "Hidden1", nodes: 4, col: C.blue   },
  { label: "Hidden2", nodes: 4, col: C.purple },
  { label: "Output",  nodes: 2, col: C.pink   },
];

function BackpropViz() {
  const [pass, setPass] = useState("idle"); // idle | forward | backward
  const [fwdStep, setFwdStep] = useState(-1);
  const [bwdStep, setBwdStep] = useState(-1);
  const timerRef = useRef(null);

  const runForward = () => {
    setPass("forward"); setFwdStep(0); setBwdStep(-1);
    let s = 0;
    timerRef.current = setInterval(() => {
      s++;
      setFwdStep(s);
      if (s >= BP_LAYERS.length - 1) {
        clearInterval(timerRef.current);
        setTimeout(() => runBackward(), 400);
      }
    }, 600);
  };

  const runBackward = () => {
    setPass("backward"); setBwdStep(BP_LAYERS.length - 1);
    let s = BP_LAYERS.length - 1;
    timerRef.current = setInterval(() => {
      s--;
      setBwdStep(s);
      if (s <= 0) { clearInterval(timerRef.current); setPass("done"); }
    }, 600);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  const W = 420, H = 200;
  const nodeR = 14;
  const layerX = (li) => 40 + li * (W - 80) / (BP_LAYERS.length - 1);
  const nodeY = (ni, count) => H / 2 + (ni - (count - 1) / 2) * 44;

  return (
    <div style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, height: H, display: "block", overflow: "visible" }}>
        {/* Edges */}
        {BP_LAYERS.slice(0, -1).map((layA, li) =>
          Array.from({ length: layA.nodes }, (_, ni) =>
            Array.from({ length: BP_LAYERS[li + 1].nodes }, (_, nj) => {
              const x1 = layerX(li), y1 = nodeY(ni, layA.nodes);
              const x2 = layerX(li + 1), y2 = nodeY(nj, BP_LAYERS[li + 1].nodes);
              const isFwd = pass === "forward" && fwdStep > li;
              const isBwd = (pass === "backward" || pass === "done") && bwdStep <= li;
              return (
                <line key={`${li}-${ni}-${nj}`} x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke={isBwd ? C.pink : isFwd ? C.cyan : "rgba(255,255,255,0.07)"}
                  strokeWidth={isBwd ? 1.5 : isFwd ? 1 : 0.8}
                  strokeOpacity={isBwd ? 0.7 : isFwd ? 0.5 : 1} />
              );
            })
          )
        )}

        {/* Nodes */}
        {BP_LAYERS.map((layer, li) =>
          Array.from({ length: layer.nodes }, (_, ni) => {
            const x = layerX(li), y = nodeY(ni, layer.nodes);
            const isFwdActive = pass === "forward" && fwdStep >= li;
            const isBwdActive = (pass === "backward" || pass === "done") && bwdStep <= li;
            const col = isBwdActive ? C.pink : isFwdActive ? layer.col : "rgba(255,255,255,0.15)";
            return (
              <g key={`${li}-${ni}`}>
                <circle cx={x} cy={y} r={nodeR + 6} fill={col} fillOpacity="0.1" />
                <circle cx={x} cy={y} r={nodeR}
                  fill={isBwdActive ? C.pink + "30" : isFwdActive ? layer.col + "25" : "rgba(255,255,255,0.05)"}
                  stroke={col} strokeWidth={isFwdActive || isBwdActive ? 2 : 1} strokeOpacity={isFwdActive || isBwdActive ? 0.9 : 0.25} />
              </g>
            );
          })
        )}

        {/* Layer labels */}
        {BP_LAYERS.map((layer, li) => (
          <text key={li} x={layerX(li)} y={H - 4} textAnchor="middle"
            fill={C.dimmer} fontSize="7" fontFamily="'Orbitron', monospace" letterSpacing="0.06em">
            {layer.label.toUpperCase()}
          </text>
        ))}

        {/* Direction labels */}
        {(pass === "forward" || pass === "backward" || pass === "done") && (
          <text x={W / 2} y={18} textAnchor="middle" fontSize="8" fontFamily="'Orbitron', monospace"
            fill={pass === "forward" ? C.cyan : C.pink} letterSpacing="0.1em">
            {pass === "forward" ? "→ FORWARD PASS · COMPUTING ACTIVATIONS →" : "← BACKWARD PASS · PROPAGATING GRADIENTS ←"}
          </text>
        )}
      </svg>

      <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={() => { clearInterval(timerRef.current); setFwdStep(-1); setBwdStep(-1); setPass("idle"); setTimeout(runForward, 50); }}
          style={{ padding: "7px 20px", borderRadius: 100, border: `1px solid ${C.pink}55`, background: C.pink + "18", color: C.pink, fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.12em", cursor: "pointer", textTransform: "uppercase" }}>
          {pass === "idle" ? "Run Pass →" : "Replay →"}
        </button>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
            <div style={{ width: 20, height: 2, background: C.cyan }} />
            <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.cyan }}>Forward</div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
            <div style={{ width: 20, height: 2, background: C.pink }} />
            <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.pink }}>Backward (gradients)</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 11. GRADIENT DESCENT VIZ ────────────────────────────────────────────────
function GradientDescentViz() {
  const [lr, setLr] = useState(0.15);
  const [step, setStep] = useState(0);
  const [running, setRunning] = useState(false);
  const timerRef = useRef(null);

  // Loss landscape: a simple quadratic bowl with a bump
  const lossAt = (x) => 0.8 * (x - 0.65) ** 2 + 0.08 * Math.sin(x * 10) * Math.exp(-4 * (x - 0.5) ** 2) + 0.05;
  const gradAt = (x) => {
    const dx = 0.001;
    return (lossAt(x + dx) - lossAt(x - dx)) / (2 * dx);
  };

  const MAX_STEPS = 20;
  // Pre-compute ball trajectory
  const trajectory = (() => {
    const pts = [{ x: 0.1, loss: lossAt(0.1) }];
    let cx = 0.1;
    for (let i = 0; i < MAX_STEPS; i++) {
      cx = Math.max(0.02, Math.min(0.98, cx - lr * gradAt(cx)));
      pts.push({ x: cx, loss: lossAt(cx) });
    }
    return pts;
  })();

  const runAnimation = () => {
    if (running) return;
    setRunning(true); setStep(0);
    let s = 0;
    timerRef.current = setInterval(() => {
      s++;
      setStep(s);
      if (s >= MAX_STEPS) { clearInterval(timerRef.current); setRunning(false); }
    }, 120);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  // SVG landscape
  const W = 420, H = 160, PAD = 24;
  const toSvgX = (x) => PAD + x * (W - PAD * 2);
  const toSvgY = (loss) => H - PAD - (loss / 0.5) * (H - PAD * 2);

  const curvePts = Array.from({ length: 80 }, (_, i) => {
    const x = i / 79;
    return `${toSvgX(x)},${toSvgY(lossAt(x))}`;
  });

  const ball = trajectory[Math.min(step, trajectory.length - 1)];

  return (
    <div style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, height: H, display: "block" }}>
        {/* Gradient fill under curve */}
        <defs>
          <linearGradient id="lossGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={C.gold} stopOpacity="0.25" />
            <stop offset="100%" stopColor={C.gold} stopOpacity="0.03" />
          </linearGradient>
        </defs>
        <polygon points={`${toSvgX(0)},${H - PAD} ${curvePts.join(" ")} ${toSvgX(1)},${H - PAD}`} fill="url(#lossGrad)" />
        <polyline points={curvePts.join(" ")} fill="none" stroke={C.gold} strokeWidth="2.5" strokeOpacity="0.8" />

        {/* Axis */}
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
        <text x={PAD} y={H - 6} fontSize="7" fontFamily="'Orbitron', monospace" fill={C.dimmer}>PARAMETER VALUE</text>
        <text x={6} y={PAD + 6} fontSize="7" fontFamily="'Orbitron', monospace" fill={C.dimmer}>LOSS</text>

        {/* Trajectory trail */}
        {trajectory.slice(0, step + 1).map((pt, i) => i > 0 && (
          <line key={i}
            x1={toSvgX(trajectory[i - 1].x)} y1={toSvgY(trajectory[i - 1].loss)}
            x2={toSvgX(pt.x)} y2={toSvgY(pt.loss)}
            stroke={C.emerald} strokeWidth="1.5" strokeOpacity={0.3 + 0.4 * (i / step)} />
        ))}

        {/* Ball */}
        {step > 0 && (
          <g>
            <circle cx={toSvgX(ball.x)} cy={toSvgY(ball.loss)} r={10} fill={C.emerald} fillOpacity="0.18" />
            <circle cx={toSvgX(ball.x)} cy={toSvgY(ball.loss)} r={6} fill={C.emerald} />
            <circle cx={toSvgX(ball.x)} cy={toSvgY(ball.loss)} r={3} fill="white" fillOpacity="0.9" />
          </g>
        )}

        {/* Start dot */}
        <circle cx={toSvgX(trajectory[0].x)} cy={toSvgY(trajectory[0].loss)} r={5}
          fill="none" stroke={C.dim} strokeWidth="1.5" strokeDasharray="3,2" />
      </svg>

      {/* Controls */}
      <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={runAnimation}
          style={{ padding: "7px 20px", borderRadius: 100, border: `1px solid ${C.gold}55`, background: C.gold + "18", color: C.gold, fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.12em", cursor: running ? "wait" : "pointer", textTransform: "uppercase" }}>
          {running ? "Running…" : step === 0 ? "Roll Downhill →" : "Replay →"}
        </button>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 160 }}>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, letterSpacing: "0.12em" }}>LEARNING RATE</div>
            <div style={{ fontSize: 13, fontFamily: "'Orbitron', monospace", color: C.gold, fontWeight: 700 }}>{lr.toFixed(2)}</div>
          </div>
          <input type="range" min="0.02" max="0.5" step="0.01" value={lr}
            onChange={e => { setLr(parseFloat(e.target.value)); setStep(0); setRunning(false); clearInterval(timerRef.current); }}
            style={{ width: "100%", accentColor: C.gold, cursor: "pointer" }} />
          <div style={{ fontSize: 11, color: C.dim }}>
            {lr < 0.1 ? "Too small — very slow convergence." : lr < 0.25 ? "Good — smooth convergence." : "Large — may overshoot the minimum."}
          </div>
        </div>
        {step > 0 && (
          <div style={{ fontSize: 10, fontFamily: "'Orbitron', monospace", color: C.emerald }}>
            Step {step} · loss {ball.loss.toFixed(4)}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 12. FINE-TUNING VIZ ─────────────────────────────────────────────────────
const FT_LAYERS = [
  { label: "Embed",    frozen: true  },
  { label: "Attn 1",  frozen: true  },
  { label: "FFN 1",   frozen: true  },
  { label: "Attn 2",  frozen: true  },
  { label: "FFN 2",   frozen: true  },
  { label: "Attn 3",  frozen: false },
  { label: "FFN 3",   frozen: false },
  { label: "Head",    frozen: false },
];

function FineTuningViz() {
  const [mode, setMode] = useState("full"); // full | last | head

  const activeCount = { full: 8, last: 3, head: 1 }[mode];
  const frozenCount = 8 - activeCount;

  return (
    <div style={{ width: "100%" }}>
      {/* Layer stack */}
      <div style={{ display: "flex", gap: 6, alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap" }}>
        {FT_LAYERS.map((layer, i) => {
          const isActive = i >= 8 - activeCount;
          const col = isActive ? C.emerald : C.dimmer;
          return (
            <motion.div key={layer.label}
              animate={{ background: isActive ? C.emerald + "25" : "rgba(255,255,255,0.04)", borderColor: isActive ? C.emerald + "88" : "rgba(255,255,255,0.1)", scale: isActive ? 1.04 : 1 }}
              transition={{ duration: 0.35 }}
              style={{ width: 52, borderRadius: 8, border: "1px solid", overflow: "hidden", position: "relative" }}>
              {/* Frozen lock */}
              {!isActive && (
                <div style={{ position: "absolute", top: 4, right: 4, fontSize: 10, opacity: 0.5 }}>🔒</div>
              )}
              {/* Glow bar at top */}
              {isActive && (
                <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 1.8, delay: i * 0.1 }}
                  style={{ height: 2, background: `linear-gradient(90deg, transparent, ${C.emerald}, transparent)` }} />
              )}
              <div style={{ padding: "10px 4px", textAlign: "center" }}>
                <div style={{ fontSize: 7, fontFamily: "'Orbitron', monospace", color: col, letterSpacing: "0.05em", lineHeight: 1.5 }}>
                  {layer.label.toUpperCase()}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Mode buttons */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 0, borderRadius: 100, border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden" }}>
          {[
            { key: "full", label: "Full Fine-tune" },
            { key: "last", label: "Last 3 Layers" },
            { key: "head", label: "Head Only" },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setMode(key)}
              style={{ padding: "7px 16px", fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.08em", cursor: "pointer", border: "none", background: mode === key ? C.emerald + "28" : "transparent", color: mode === key ? C.emerald : C.dimmer, transition: "all 0.25s", whiteSpace: "nowrap" }}>
              {label}
            </button>
          ))}
        </div>
        <div style={{ color: C.dim, fontSize: 13, lineHeight: 1.5 }}>
          {mode === "full" && `All 8 layers train. Most expensive. Best quality for large domain shifts.`}
          {mode === "last" && `${frozenCount} layers frozen 🔒, ${activeCount} train. Good trade-off for task adaptation.`}
          {mode === "head" && `Only the output head trains. Fastest. Works when task is simple.`}
        </div>
      </div>
    </div>
  );
}

// ─── 13. RLHF VIZ ────────────────────────────────────────────────────────────
const RLHF_RESPONSES = [
  { text: "Here's how to make a bomb…", score: -0.9, label: "Harmful" },
  { text: "I can't help with that, but I can explain chemistry safely.", score: 0.85, label: "Aligned" },
  { text: "Sure! First you need…", score: -0.6, label: "Unsafe" },
  { text: "That's outside what I can assist with.", score: 0.7, label: "Refusal" },
];

function RLHFViz() {
  const paused = useContext(AnimContext);
  const pausedRef = useRef(paused);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  const ioFiredRef = useRef(false);

  const [phase, setPhase] = useState(0); // 0=compare, 1=reward, 2=update
  const [selected, setSelected] = useState(null);
  const [iteration, setIteration] = useState(1);
  const timerRef = useRef(null);

  const runCycle = () => {
    if (pausedRef.current) return;
    setPhase(0); setSelected(null);
    timerRef.current = setTimeout(() => {
      if (pausedRef.current) return;
      setSelected(1); // human picks response B
      setPhase(1);
      timerRef.current = setTimeout(() => {
        if (pausedRef.current) return;
        setPhase(2);
        timerRef.current = setTimeout(() => {
          if (pausedRef.current) return;
          setIteration(n => n + 1);
          runCycle();
        }, 1200);
      }, 1000);
    }, 1000);
  };

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { io.disconnect(); ioFiredRef.current = true; runCycle(); }
    }, { threshold: 0.3 });
    // find the container after mount
    const timer = setTimeout(() => {
      const el = document.querySelector('[data-rlhf]');
      if (el) io.observe(el);
    }, 100);
    return () => { clearTimeout(timer); io.disconnect(); clearTimeout(timerRef.current); };
  }, []);

  // Resume when unpaused (IO already fired)
  useEffect(() => {
    if (!paused && ioFiredRef.current) runCycle();
  }, [paused]);

  const actors = [
    { label: "LLM", sub: "generates responses", col: C.blue, icon: "🤖" },
    { label: "Human", sub: "picks the better one", col: C.cyan, icon: "👤" },
    { label: "Reward Model", sub: "learns the preference", col: C.purple, icon: "⚖️" },
  ];

  return (
    <div data-rlhf style={{ width: "100%" }}>
      {/* Iteration badge */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, letterSpacing: "0.15em" }}>ITERATION</div>
        <div style={{ fontSize: 18, fontFamily: "'Orbitron', monospace", color: C.purple, fontWeight: 700 }}>{iteration}</div>
        <div style={{ height: 1, flex: 1, background: `linear-gradient(90deg, ${C.purple}44, transparent)` }} />
      </div>

      {/* Three-actor cycle */}
      <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 24, flexWrap: "wrap" }}>
        {actors.map((actor, i) => (
          <div key={i} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0, flexShrink: 0 }}>
            <motion.div
              animate={{ borderColor: phase >= i ? actor.col + "aa" : "rgba(255,255,255,0.1)", background: phase >= i ? actor.col + "18" : "transparent", scale: phase === i ? 1.06 : 1 }}
              transition={{ duration: 0.4 }}
              style={{ padding: "10px 16px", borderRadius: 12, border: "1px solid", textAlign: "center" }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{actor.icon}</div>
              <div style={{ fontSize: 10, fontFamily: "'Orbitron', monospace", color: phase >= i ? actor.col : C.dimmer, fontWeight: 700 }}>{actor.label}</div>
              <div style={{ fontSize: 11, color: C.dimmer, marginTop: 2 }}>{actor.sub}</div>
            </motion.div>
            {i < 2 && (
              <motion.div animate={{ opacity: phase > i ? 1 : 0.2 }} transition={{ duration: 0.4 }}
                style={{ width: 24, height: 1, background: `linear-gradient(90deg, ${actors[i].col}, ${actors[i+1].col})`, flexShrink: 0 }} />
            )}
          </div>
        ))}
        {/* Feedback arrow back */}
        <motion.div animate={{ opacity: phase >= 2 ? 1 : 0.1 }} transition={{ duration: 0.4 }}
          style={{ marginLeft: 8, fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.purple, letterSpacing: "0.08em" }}>
          ↩ UPDATES LLM
        </motion.div>
      </div>

      {/* Response comparison */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {[0, 1].map(ri => {
          const resp = RLHF_RESPONSES[ri + (iteration % 2) * 2] || RLHF_RESPONSES[ri];
          const isWinner = selected === ri;
          const isLoser = selected !== null && selected !== ri;
          return (
            <motion.div key={`${iteration}-${ri}`}
              animate={{ borderColor: isWinner ? C.emerald + "88" : isLoser ? "rgba(255,80,80,0.3)" : "rgba(255,255,255,0.1)", background: isWinner ? C.emerald + "12" : isLoser ? "rgba(255,80,80,0.06)" : "rgba(255,255,255,0.04)", scale: isWinner ? 1.02 : 1 }}
              transition={{ duration: 0.4 }}
              style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid" }}>
              <div style={{ fontSize: 13, color: C.dim, lineHeight: 1.5, marginBottom: 8, fontFamily: "'Raleway', sans-serif" }}>
                "{resp.text}"
              </div>
              {phase >= 1 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                  <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: isWinner ? C.emerald : C.pink }}>
                    {isWinner ? "✓ CHOSEN" : "✗ REJECTED"}
                  </div>
                  <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer }}>
                    reward: {resp.score > 0 ? "+" : ""}{resp.score}
                  </div>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ─── 14. LORA VIZ ────────────────────────────────────────────────────────────
function LoRAViz() {
  const [showAdapters, setShowAdapters] = useState(false);
  const [rank, setRank] = useState(8);

  const LAYERS = ["Embed", "Attn Q", "Attn K", "Attn V", "FFN 1", "FFN 2", "Head"];
  const paramsFull = 7_000_000_000;
  const paramsLora = LAYERS.length * 2 * 4096 * rank; // A + B matrices per layer
  const pct = ((paramsLora / paramsFull) * 100).toFixed(2);

  return (
    <div style={{ width: "100%" }}>
      {/* Toggle */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 0, borderRadius: 100, border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden" }}>
          {[false, true].map(v => (
            <button key={String(v)} onClick={() => setShowAdapters(v)}
              style={{ padding: "7px 18px", fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.1em", cursor: "pointer", border: "none", background: showAdapters === v ? C.orange + "28" : "transparent", color: showAdapters === v ? C.orange : C.dimmer, transition: "all 0.25s" }}>
              {v ? "With LoRA" : "Base Model"}
            </button>
          ))}
        </div>
        {showAdapters && (
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer }}>RANK r=</div>
            <input type="range" min="1" max="64" value={rank} onChange={e => setRank(+e.target.value)}
              style={{ flex: 1, accentColor: C.orange, cursor: "pointer" }} />
            <div style={{ fontSize: 13, fontFamily: "'Orbitron', monospace", color: C.orange, fontWeight: 700, minWidth: 24 }}>{rank}</div>
          </div>
        )}
      </div>

      {/* Layer stack */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {LAYERS.map((lbl, i) => (
          <div key={lbl} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            {/* Adapter (A×B) */}
            <AnimatePresence>
              {showAdapters && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  style={{ width: 52, padding: "4px 0", background: C.orange + "28", border: `1.5px solid ${C.orange}88`, borderRadius: 6, textAlign: "center" }}>
                  <motion.div animate={{ opacity: [0.6, 1, 0.6] }} transition={{ repeat: Infinity, duration: 1.6, delay: i * 0.12 }}
                    style={{ fontSize: 7, fontFamily: "'Orbitron', monospace", color: C.orange, lineHeight: 1.6 }}>
                    A·B<br />r={rank}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            {/* Base layer (frozen) */}
            <div style={{ width: 52, padding: "8px 4px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, textAlign: "center", position: "relative" }}>
              <div style={{ fontSize: 7, fontFamily: "'Orbitron', monospace", color: C.dim, lineHeight: 1.4 }}>
                {lbl.toUpperCase()}
              </div>
              <div style={{ fontSize: 10, marginTop: 2, opacity: 0.5 }}>🔒</div>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <AnimatePresence mode="wait">
        {showAdapters ? (
          <motion.div key="lora" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ padding: "10px 14px", borderRadius: 10, background: C.orange + "12", border: `1px solid ${C.orange}33` }}>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer }}>TRAINABLE PARAMS</div>
                <div style={{ fontSize: 16, fontFamily: "'Orbitron', monospace", color: C.orange, fontWeight: 700 }}>{(paramsLora / 1e6).toFixed(1)}M</div>
              </div>
              <div>
                <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer }}>VS FULL MODEL</div>
                <div style={{ fontSize: 16, fontFamily: "'Orbitron', monospace", color: C.emerald, fontWeight: 700 }}>{pct}%</div>
              </div>
              <div>
                <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer }}>BASE FROZEN</div>
                <div style={{ fontSize: 16, fontFamily: "'Orbitron', monospace", color: C.dim, fontWeight: 700 }}>7B</div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="base" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <div style={{ fontSize: 13, color: C.dim }}>All 7 billion parameters frozen. Toggle LoRA to attach trainable adapters.</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── 15. RL MAZE VIZ ─────────────────────────────────────────────────────────
const MAZE_GRID = [
  [0,0,1,0,0,0,0],
  [0,1,1,0,1,1,0],
  [0,0,0,0,1,0,0],
  [1,1,0,1,1,0,1],
  [0,0,0,0,0,0,0],
  [0,1,1,1,0,1,0],
  [0,0,0,0,0,0,2], // 2 = goal
];
// 0=open, 1=wall, 2=goal
const START = { r: 0, c: 0 };
const GOAL  = { r: 6, c: 6 };

// BFS shortest path
const bfsPath = () => {
  const visited = Array.from({ length: 7 }, () => Array(7).fill(false));
  const queue = [{ r: 0, c: 0, path: [{ r: 0, c: 0 }] }];
  visited[0][0] = true;
  const dirs = [[0,1],[1,0],[0,-1],[-1,0]];
  while (queue.length) {
    const { r, c, path } = queue.shift();
    if (r === GOAL.r && c === GOAL.c) return path;
    for (const [dr, dc] of dirs) {
      const nr = r + dr, nc = c + dc;
      if (nr >= 0 && nr < 7 && nc >= 0 && nc < 7 && !visited[nr][nc] && MAZE_GRID[nr][nc] !== 1) {
        visited[nr][nc] = true;
        queue.push({ r: nr, c: nc, path: [...path, { r: nr, c: nc }] });
      }
    }
  }
  return [];
};

const OPTIMAL_PATH = bfsPath();

// A longer "naive" first attempt path (explores more cells)
const NAIVE_PATH = (() => {
  // Manually route a suboptimal path
  const p = [{r:0,c:0},{r:1,c:0},{r:2,c:0},{r:2,c:1},{r:2,c:2},{r:2,c:3},{r:1,c:3},{r:0,c:3},{r:0,c:4},{r:0,c:5},{r:0,c:6},{r:1,c:6},{r:2,c:6},{r:2,c:5},{r:3,c:5},{r:4,c:5},{r:4,c:6},{r:5,c:6},{r:6,c:6}];
  return p.filter(({r,c}) => MAZE_GRID[r][c] !== 1);
})();

function RLMazeViz() {
  const [episode, setEpisode] = useState(1);
  const [pathStep, setPathStep] = useState(0);
  const [running, setRunning] = useState(false);
  const timerRef = useRef(null);
  const episodeRef = useRef(1);

  const currentPath = episode <= 2 ? NAIVE_PATH : OPTIMAL_PATH;

  const runEpisode = () => {
    if (running) return;
    setRunning(true);
    setPathStep(0);
    let s = 0;
    timerRef.current = setInterval(() => {
      s++;
      setPathStep(s);
      if (s >= currentPath.length - 1) {
        clearInterval(timerRef.current);
        setRunning(false);
      }
    }, episode <= 2 ? 180 : 130);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  const CELL = 36;
  const pos = currentPath[Math.min(pathStep, currentPath.length - 1)];

  return (
    <div style={{ width: "100%", display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
      {/* Maze */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(7, ${CELL}px)`, gap: 2 }}>
          {MAZE_GRID.map((row, r) =>
            row.map((cell, c) => {
              const isAgent = pos && pos.r === r && pos.c === c;
              const isGoal = cell === 2;
              const isWall = cell === 1;
              const dist = Math.abs(r - GOAL.r) + Math.abs(c - GOAL.c);
              const qVal = Math.max(0, 1 - dist / 12);
              const episodeReveal = Math.min(1, (episode - 1) / 2);
              const showHeat = !isWall && !isGoal && episodeReveal > 0;
              const heatBg = showHeat
                ? `hsl(${30 + qVal * 60}, 90%, ${15 + Math.round(qVal * episodeReveal * 30)}%)`
                : null;
              return (
                    <div key={`${r}-${c}`} style={{
                      width: CELL, height: CELL, borderRadius: 5,
                      background: isWall ? "rgba(255,255,255,0.08)"
                        : isGoal ? C.emerald + "30"
                        : isAgent ? C.orange + "33"
                        : heatBg ?? "rgba(0,0,0,0.25)",
                      border: isGoal ? `1.5px solid ${C.emerald}88` : isWall ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(255,255,255,0.05)",
                      display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center",
                      fontSize: isGoal ? 14 : isAgent ? 16 : 10,
                      transition: "background 0.4s",
                    }}>
                      {isWall && <div style={{ width: 8, height: 8, borderRadius: 2, background: "rgba(255,255,255,0.15)" }} />}
                      {isGoal && "⭐"}
                      {isAgent && !isGoal && "🤖"}
                    </div>
              );
            })
          )}
        </div>
      </div>

      {/* Controls + stats */}
      <div style={{ flex: 1, minWidth: 160, display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, letterSpacing: "0.15em" }}>EPISODE</div>
          <div style={{ fontSize: 28, fontFamily: "'Orbitron', monospace", color: C.orange, fontWeight: 700, lineHeight: 1 }}>{episode}</div>
        </div>
        <div style={{ padding: "8px 12px", borderRadius: 8, background: C.orange + "12", border: `1px solid ${C.orange}33` }}>
          <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.5 }}>
            {episode <= 2
              ? `Path length: ${NAIVE_PATH.length - 1} steps — exploring suboptimally.`
              : `Path length: ${OPTIMAL_PATH.length - 1} steps — learned shortest route!`}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button onClick={runEpisode} disabled={running}
            style={{ padding: "7px 16px", borderRadius: 100, border: `1px solid ${C.orange}55`, background: C.orange + "18", color: C.orange, fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.12em", cursor: running ? "wait" : "pointer", textTransform: "uppercase" }}>
            {running ? "Running…" : "Run Episode →"}
          </button>
          {!running && pathStep > 0 && episode <= 3 && (
            <button onClick={() => { setEpisode(e => e + 1); setPathStep(0); }}
              style={{ padding: "7px 16px", borderRadius: 100, border: `1px solid ${C.emerald}55`, background: C.emerald + "18", color: C.emerald, fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.12em", cursor: "pointer", textTransform: "uppercase" }}>
              Next Episode +
            </button>
          )}
        </div>
        <div style={{ fontSize: 9, color: C.dimmer, fontFamily: "'Orbitron', monospace", lineHeight: 1.6 }}>
          {episode <= 2 ? "EXPLORING · REWARD: -1 PER STEP" : "OPTIMISED · MAX REWARD FOUND"}
        </div>
        {episode > 1 && (
          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 8, color: C.dimmer, fontFamily: "'Orbitron', monospace", marginBottom: 4 }}>Q-VALUE HEAT</div>
            <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
              {["low","","","","high"].map((l, i) => (
                <div key={i} style={{ width: 18, height: 8, borderRadius: 2, background: `hsl(${30 + i * 15}, 90%, 55%)`, opacity: 0.3 + i * 0.15 }} />
              ))}
              <div style={{ fontSize: 7, color: C.dimmer, fontFamily: "'Orbitron', monospace", marginLeft: 4 }}>→ goal</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 16. DISTILLATION VIZ ────────────────────────────────────────────────────
const DISTILL_WORDS = ["cat", "dog", "bird", "fish", "fox"];

function DistillationViz() {
  const [step, setStep] = useState(0); // 0=idle, 1=teacher, 2=transfer, 3=student
  const [running, setRunning] = useState(false);
  const timerRef = useRef(null);

  // Teacher soft probabilities (rich distribution)
  const teacherProbs = [0.62, 0.21, 0.09, 0.05, 0.03];
  // Hard label (one-hot) — what a normal student would see
  const hardProbs    = [1,    0,    0,    0,    0   ];
  // Student after distillation (mimics teacher shape)
  const studentProbs = [0.58, 0.23, 0.11, 0.05, 0.03];

  const runDemo = () => {
    if (running) return;
    setRunning(true); setStep(1);
    timerRef.current = setTimeout(() => {
      setStep(2);
      timerRef.current = setTimeout(() => {
        setStep(3);
        setRunning(false);
      }, 900);
    }, 1000);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const Bar = ({ probs, col, label }) => (
    <div style={{ flex: 1, minWidth: 130 }}>
      <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: col, letterSpacing: "0.12em", marginBottom: 10 }}>{label}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {DISTILL_WORDS.map((w, i) => (
          <div key={w} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
            <div style={{ width: 32, fontSize: 12, color: C.dim, fontFamily: "'Raleway', sans-serif", textAlign: "right", flexShrink: 0 }}>{w}</div>
            <motion.div animate={{ width: `${probs[i] * 100}%` }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              style={{ height: 14, borderRadius: 4, background: `linear-gradient(90deg, ${col}, ${col}66)`, minWidth: probs[i] > 0 ? 4 : 0 }} />
            <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: col, minWidth: 32 }}>{(probs[i] * 100).toFixed(0)}%</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ width: "100%" }}>
      {/* Flow: Teacher → transfer → Student */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 20 }}>
        {/* Teacher */}
        <motion.div animate={{ opacity: step >= 1 ? 1 : 0.3, scale: step === 1 ? 1.02 : 1 }} transition={{ duration: 0.4 }}
          style={{ flex: 1, minWidth: 130, padding: "12px 14px", borderRadius: 12, background: C.blue + "18", border: `1.5px solid ${C.blue}55` }}>
          <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.blue, letterSpacing: "0.12em", marginBottom: 4 }}>TEACHER LLM · 70B</div>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: step >= 1 ? 12 : 0 }}>Large, expensive, slow</div>
          {step >= 1 && <Bar probs={teacherProbs} col={C.blue} label="SOFT LABELS" />}
        </motion.div>

        {/* Arrow */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", paddingTop: 40, gap: 4 }}>
          <motion.div animate={{ opacity: step >= 2 ? 1 : 0.15 }} transition={{ duration: 0.4 }}>
            <motion.div animate={{ x: step >= 2 ? [0, 6, 0] : 0 }} transition={{ repeat: Infinity, duration: 1.2 }}
              style={{ color: C.cyan, fontSize: 22 }}>→</motion.div>
          </motion.div>
          <div style={{ fontSize: 8, fontFamily: "'Orbitron', monospace", color: C.dimmer, textAlign: "center" }}>KL<br />div</div>
        </div>

        {/* Student */}
        <motion.div animate={{ opacity: step >= 3 ? 1 : 0.3, scale: step === 3 ? 1.02 : 1 }} transition={{ duration: 0.4 }}
          style={{ flex: 1, minWidth: 130, padding: "12px 14px", borderRadius: 12, background: C.emerald + "18", border: `1.5px solid ${step >= 3 ? C.emerald + "88" : C.emerald + "33"}` }}>
          <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.emerald, letterSpacing: "0.12em", marginBottom: 4 }}>STUDENT LLM · 3B</div>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: step >= 3 ? 12 : 0 }}>Small, fast, cheap</div>
          {step >= 3 && <Bar probs={studentProbs} col={C.emerald} label="LEARNED DISTRIBUTION" />}
        </motion.div>
      </div>

      {/* Hard label comparison */}
      {step >= 3 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", marginBottom: 12 }}>
          <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, marginBottom: 8 }}>VS HARD LABEL (what normal training sees)</div>
          <Bar probs={hardProbs} col={C.dim} label="" />
          <div style={{ fontSize: 12, color: C.dim, marginTop: 8 }}>Soft labels from the teacher carry richer signal — the model learns that "dog" is almost as valid as "cat".</div>
        </motion.div>
      )}

      <button onClick={runDemo} disabled={running}
        style={{ padding: "7px 20px", borderRadius: 100, border: `1px solid ${C.cyan}55`, background: C.cyan + "18", color: C.cyan, fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.12em", cursor: running ? "wait" : "pointer", textTransform: "uppercase" }}>
        {step === 0 ? "Run Distillation →" : running ? "Transferring…" : "Replay →"}
      </button>
    </div>
  );
}

// ─── 17. CHAIN OF THOUGHT VIZ ────────────────────────────────────────────────
const COT_PROBLEM = "If a train travels 60 mph for 2.5 hours, then 80 mph for 1.5 hours, what is the total distance?";
const COT_STEPS = [
  { label: "Step 1", text: "First segment: 60 mph × 2.5 h = 150 miles", col: C.blue  },
  { label: "Step 2", text: "Second segment: 80 mph × 1.5 h = 120 miles", col: C.cyan  },
  { label: "Step 3", text: "Total distance: 150 + 120 = 270 miles",       col: C.emerald },
];

function ChainOfThoughtViz() {
  const paused = useContext(AnimContext);
  const [mode, setMode] = useState("direct"); // direct | cot
  const [cotStep, setCotStep] = useState(-1);
  const [running, setRunning] = useState(false);
  const timerRef = useRef(null);

  const runCot = () => {
    if (running) return;
    setRunning(true); setCotStep(0);
    let s = 0;
    timerRef.current = setInterval(() => {
      s++;
      setCotStep(s);
      if (s >= COT_STEPS.length) { clearInterval(timerRef.current); setRunning(false); }
    }, 900);
  };

  useEffect(() => {
    clearInterval(timerRef.current);
    setCotStep(-1);
    if (mode === "cot") runCot();
  }, [mode]);

  // Pause / resume
  useEffect(() => {
    if (paused) { clearInterval(timerRef.current); setRunning(false); }
    else if (mode === "cot") runCot();
  }, [paused]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  return (
    <div className="ai-cot-viz" style={{ width: "100%" }}>
      {/* Toggle */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 0, borderRadius: 100, border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden", width: "fit-content", marginBottom: 20 }}>
        {["direct", "cot"].map(m => (
          <button key={m} onClick={() => setMode(m)}
            style={{ padding: "7px 20px", fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.1em", cursor: "pointer", border: "none", background: mode === m ? C.blue + "28" : "transparent", color: mode === m ? C.blue : C.dimmer, transition: "all 0.25s" }}>
            {m === "direct" ? "Direct Answer" : "Chain of Thought"}
          </button>
        ))}
      </div>

      {/* Problem */}
      <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", marginBottom: 16 }}>
        <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, marginBottom: 6 }}>PROBLEM</div>
        <div style={{ color: C.white, fontSize: 14.5, fontFamily: "'Raleway', sans-serif", lineHeight: 1.6 }}>{COT_PROBLEM}</div>
      </div>

      <AnimatePresence mode="wait">
        {mode === "direct" ? (
          <motion.div key="direct" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <div style={{ padding: "12px 14px", borderRadius: 10, background: C.pink + "15", border: `1px solid ${C.pink}44` }}>
              <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.pink, marginBottom: 6 }}>MODEL ANSWER (no reasoning)</div>
              <div style={{ color: C.white, fontSize: 15, fontFamily: "'Raleway', sans-serif", fontWeight: 700 }}>230 miles</div>
              <div style={{ color: C.pink, fontSize: 10, marginTop: 6, fontFamily: "'Orbitron', monospace" }}>✗ WRONG — skipped the second segment</div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="cot" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {COT_STEPS.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -16 }} animate={{ opacity: cotStep > i ? 1 : 0.2, x: 0 }}
                transition={{ duration: 0.4, delay: 0 }}
                style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start", padding: "10px 14px", borderRadius: 10, background: cotStep > i ? s.col + "15" : "rgba(255,255,255,0.03)", border: `1px solid ${cotStep > i ? s.col + "55" : "rgba(255,255,255,0.07)"}` }}>
                <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: s.col, minWidth: 48, marginTop: 1 }}>{s.label}</div>
                <div style={{ color: C.white, fontSize: 14.5, fontFamily: "'Raleway', sans-serif", lineHeight: 1.5 }}>{s.text}</div>
                {cotStep > i && <div style={{ color: s.col, fontSize: 14, marginLeft: "auto", flexShrink: 0 }}>✓</div>}
              </motion.div>
            ))}
            {cotStep >= COT_STEPS.length && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                style={{ padding: "10px 14px", borderRadius: 10, background: C.emerald + "20", border: `1.5px solid ${C.emerald}66` }}>
                <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.emerald, marginBottom: 4 }}>FINAL ANSWER</div>
                <div style={{ color: C.white, fontSize: 16, fontFamily: "'Raleway', sans-serif", fontWeight: 800 }}>270 miles ✓</div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── 18. REASONING MODELS VIZ ────────────────────────────────────────────────
const REASONING_QUESTION = "Is 17519 a prime number?";
const REASONING_THOUGHTS = [
  "Check if divisible by 2… 17519 is odd. No.",
  "Check by 3… digit sum = 1+7+5+1+9 = 23. Not divisible. No.",
  "Check primes up to √17519 ≈ 132…",
  "Try 7: 17519 / 7 ≈ 2502.7. No.",
  "Try 11: 17519 / 11 ≈ 1592.6. No.",
  "Try 13: 17519 / 13 = 1347.6. No.",
  "Try 17: 17519 / 17 = 1030.5. No.",
  "Try 19: 17519 / 19 = 922.05. No.",
  "Try 23: 17519 / 23 = 761.7. No.",
  "Try 127: 17519 / 127 = 137.9. No.",
  "Try 131: 17519 / 131 = 133.7. No.",
  "Checked all primes ≤ 132. None divide evenly.",
];

function ReasoningModelsViz() {
  const paused = useContext(AnimContext);
  const [mode, setMode] = useState("fast");  // fast | reasoning
  const [thinkStep, setThinkStep] = useState(-1);
  const [showAnswer, setShowAnswer] = useState(false);
  const timerRef = useRef(null);

  const run = (m) => {
    clearInterval(timerRef.current);
    setThinkStep(-1); setShowAnswer(false);
    if (m === "fast") {
      setTimeout(() => setShowAnswer(true), 400);
    } else {
      let s = 0;
      timerRef.current = setInterval(() => {
        setThinkStep(s);
        s++;
        if (s >= REASONING_THOUGHTS.length) {
          clearInterval(timerRef.current);
          setTimeout(() => setShowAnswer(true), 400);
        }
      }, 350);
    }
  };

  useEffect(() => {
    if (paused) { clearInterval(timerRef.current); return; }
    run(mode);
    return () => clearInterval(timerRef.current);
  }, [mode, paused]);

  return (
    <div className="ai-reasoning-viz" style={{ width: "100%" }}>
      {/* Toggle */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 0, borderRadius: 100, border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden", width: "fit-content", marginBottom: 20 }}>
        {["fast", "reasoning"].map(m => (
          <button key={m} onClick={() => setMode(m)}
            style={{ padding: "7px 20px", fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.08em", cursor: "pointer", border: "none", background: mode === m ? C.purple + "28" : "transparent", color: mode === m ? C.purple : C.dimmer, transition: "all 0.25s", whiteSpace: "nowrap" }}>
            {m === "fast" ? "Fast Model" : "Reasoning Model"}
          </button>
        ))}
      </div>

      {/* Question */}
      <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", marginBottom: 16 }}>
        <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, marginBottom: 4 }}>QUESTION</div>
        <div style={{ color: C.white, fontSize: 14, fontFamily: "'Raleway', sans-serif", fontWeight: 600 }}>{REASONING_QUESTION}</div>
      </div>

      {/* Thinking steps (reasoning mode) */}
      {mode === "reasoning" && (
        <div style={{ padding: "10px 14px", borderRadius: 10, background: C.purple + "0a", border: `1px solid ${C.purple}22`, marginBottom: 12, maxHeight: 180, overflowY: "auto" }}>
          <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.purple, letterSpacing: "0.12em", marginBottom: 8 }}>INTERNAL THINKING…</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {REASONING_THOUGHTS.slice(0, thinkStep + 1).map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                style={{ fontSize: 13, color: C.dim, fontFamily: "'Raleway', sans-serif", lineHeight: 1.5, paddingLeft: 8, borderLeft: `2px solid ${C.purple}44` }}>
                {t}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Answer */}
      <AnimatePresence>
        {showAnswer && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            style={{ padding: "12px 14px", borderRadius: 10, background: mode === "fast" ? C.pink + "15" : C.emerald + "18", border: `1.5px solid ${mode === "fast" ? C.pink + "55" : C.emerald + "66"}` }}>
            <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: mode === "fast" ? C.pink : C.emerald, marginBottom: 6 }}>
              {mode === "fast" ? "FAST ANSWER (no checking)" : `ANSWER AFTER ${REASONING_THOUGHTS.length} REASONING STEPS`}
            </div>
            <div style={{ color: C.white, fontSize: 16, fontFamily: "'Raleway', sans-serif", fontWeight: 800 }}>
              {mode === "fast" ? "No — 17519 = 127 × 138" : "Yes — 17519 is prime ✓"}
            </div>
            <div style={{ color: mode === "fast" ? C.pink : C.emerald, fontSize: 10, marginTop: 6, fontFamily: "'Orbitron', monospace" }}>
              {mode === "fast" ? "✗ WRONG — hallucinated a factorisation" : "✓ CORRECT — systematic verification"}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── 19. QUANTIZATION VIZ ────────────────────────────────────────────────────
function QuantizationViz() {
  const [bits, setBits] = useState(32);

  const colMap = { 32: C.blue, 8: C.orange, 4: C.pink };
  const col = colMap[bits];
  const memGB = { 32: "28 GB", 8: "7 GB", 4: "3.5 GB" }[bits];
  const quality = { 32: "100%", 8: "99.1%", 4: "97.3%" }[bits];

  const W = 340, H = 140;
  const POINTS = 300;
  const numLevels = { 32: 256, 8: 16, 4: 4 }[bits];
  const MY = H / 2, AMP = H * 0.42;

  const signal = (i) => Math.sin((i / POINTS) * Math.PI * 4) * AMP;

  const quantize = (y) => {
    const step = (AMP * 2) / numLevels;
    return Math.round((y + AMP) / step) * step - AMP;
  };

  // Build smooth curve path
  const smoothPath = Array.from({ length: POINTS + 1 }, (_, i) => {
    const x = (i / POINTS) * W;
    const y = MY + signal(i);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  // Build staircase path
  const stairPath = (() => {
    let d = "";
    let prevQY = null;
    for (let i = 0; i <= POINTS; i++) {
      const x = (i / POINTS) * W;
      const qy = MY + quantize(signal(i));
      if (i === 0) { d += `M${x.toFixed(1)},${qy.toFixed(1)}`; }
      else {
        if (qy !== prevQY) d += `L${x.toFixed(1)},${prevQY.toFixed(1)}`;
        d += `L${x.toFixed(1)},${qy.toFixed(1)}`;
      }
      prevQY = qy;
    }
    return d;
  })();

  // Horizontal level lines for 4-bit
  const levelLines = bits === 4 ? Array.from({ length: numLevels + 1 }, (_, i) => {
    const step = (AMP * 2) / numLevels;
    const y = MY + (-AMP + i * step);
    return y;
  }) : [];

  return (
    <div style={{ width: "100%" }}>
      {/* SVG waveform */}
      <div style={{ marginBottom: 16, borderRadius: 10, overflow: "hidden", border: "1px solid rgba(255,255,255,0.07)", background: "rgba(0,0,0,0.3)" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }}>
          {/* Center axis */}
          <line x1={0} y1={MY} x2={W} y2={MY} stroke="rgba(255,255,255,0.08)" strokeWidth={1} />
          {/* Level lines (4-bit only) */}
          {levelLines.map((y, i) => (
            <line key={i} x1={0} y1={y} x2={W} y2={y} stroke={col + "30"} strokeWidth={0.5} strokeDasharray="4 4" />
          ))}
          {/* Original smooth signal */}
          <path d={smoothPath} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth={1.5} />
          {/* Quantized staircase */}
          <motion.path
            key={bits}
            d={stairPath}
            fill="none"
            stroke={col}
            strokeWidth={2}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            style={{ filter: `drop-shadow(0 0 4px ${col}88)` }}
          />
          {/* Labels */}
          <text x={6} y={12} fontSize={7} fontFamily="'Orbitron', monospace" fill="rgba(255,255,255,0.25)">ORIGINAL</text>
          <text x={6} y={22} fontSize={7} fontFamily="'Orbitron', monospace" fill={col}>{bits}-BIT QUANTIZED</text>
        </svg>
      </div>

      {/* Bit selector */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, letterSpacing: "0.12em" }}>PRECISION</div>
        {[32, 8, 4].map(b => (
          <button key={b} onClick={() => setBits(b)}
            style={{ padding: "6px 16px", borderRadius: 100, fontSize: 10, fontFamily: "'Orbitron', monospace", cursor: "pointer", border: `1px solid ${bits === b ? colMap[b] + "88" : "rgba(255,255,255,0.12)"}`, background: bits === b ? colMap[b] + "22" : "transparent", color: bits === b ? colMap[b] : C.dimmer, fontWeight: bits === b ? 700 : 400 }}>
            {b}-bit
          </button>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {[
          { label: "VRAM NEEDED", value: memGB, col },
          { label: "QUALITY", value: quality, col: bits === 32 ? C.emerald : bits === 8 ? C.cyan : C.orange },
          { label: "BYTES / WEIGHT", value: `${bits / 8}B`, col },
        ].map(({ label, value, col: c }) => (
          <div key={label} style={{ padding: "8px 14px", borderRadius: 8, background: c + "12", border: `1px solid ${c}33` }}>
            <div style={{ fontSize: 8, fontFamily: "'Orbitron', monospace", color: C.dimmer }}>{label}</div>
            <motion.div animate={{ color: c }} style={{ fontSize: 18, fontFamily: "'Orbitron', monospace", fontWeight: 700 }}>{value}</motion.div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── 20. SLM VIZ ─────────────────────────────────────────────────────────────
const SLM_MODELS = [
  { name: "GPT-4",     params: 1800, quality: 95, col: C.blue,    type: "llm" },
  { name: "Llama 70B", params: 70,   quality: 82, col: C.purple,  type: "llm" },
  { name: "Llama 7B",  params: 7,    quality: 68, col: C.cyan,    type: "slm" },
  { name: "Phi-3",     params: 3.8,  quality: 65, col: C.emerald, type: "slm" },
  { name: "SmolLM",    params: 1.7,  quality: 52, col: C.orange,  type: "slm" },
];

function SLMViz() {
  const [highlight, setHighlight] = useState(null);
  const W = 380, H = 200;
  const PAD = { l: 52, r: 20, t: 16, b: 36 };
  const CW = W - PAD.l - PAD.r, CH = H - PAD.t - PAD.b;

  // Log scale for params: map 1B–2000B → 0..1
  const logMin = Math.log10(1), logMax = Math.log10(2000);
  const xOf = (p) => PAD.l + ((Math.log10(p) - logMin) / (logMax - logMin)) * CW;
  const yOf = (q) => PAD.t + CH - ((q - 40) / 60) * CH;

  // On-device threshold x position (~10B)
  const threshX = xOf(10);

  return (
    <div style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", overflow: "visible" }}>
        {/* Zone backgrounds */}
        <rect x={PAD.l} y={PAD.t} width={threshX - PAD.l} height={CH} fill={C.emerald + "08"} rx={4} />
        <rect x={threshX} y={PAD.t} width={W - threshX - PAD.r} height={CH} fill={C.blue + "06"} rx={4} />

        {/* Threshold line */}
        <line x1={threshX} y1={PAD.t} x2={threshX} y2={PAD.t + CH} stroke={C.emerald + "55"} strokeWidth={1} strokeDasharray="5 3" />
        <text x={threshX - 4} y={PAD.t + 10} textAnchor="end" fontSize={6.5} fontFamily="'Orbitron', monospace" fill={C.emerald + "99"}>ON-DEVICE</text>
        <text x={threshX + 4} y={PAD.t + 10} textAnchor="start" fontSize={6.5} fontFamily="'Orbitron', monospace" fill={C.blue + "88"}>DATACENTER</text>

        {/* Axes */}
        <line x1={PAD.l} y1={PAD.t + CH} x2={W - PAD.r} y2={PAD.t + CH} stroke="rgba(255,255,255,0.12)" strokeWidth={1} />
        <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t + CH} stroke="rgba(255,255,255,0.12)" strokeWidth={1} />

        {/* Y axis labels */}
        {[50, 65, 80, 95].map(q => (
          <g key={q}>
            <line x1={PAD.l - 3} y1={yOf(q)} x2={PAD.l} y2={yOf(q)} stroke="rgba(255,255,255,0.15)" strokeWidth={1} />
            <text x={PAD.l - 5} y={yOf(q) + 3} textAnchor="end" fontSize={6.5} fontFamily="'Orbitron', monospace" fill={C.dimmer}>{q}%</text>
          </g>
        ))}

        {/* X axis param labels */}
        {[2, 10, 100, 1000].map(p => (
          <text key={p} x={xOf(p)} y={PAD.t + CH + 12} textAnchor="middle" fontSize={6.5} fontFamily="'Orbitron', monospace" fill={C.dimmer}>{p}B</text>
        ))}

        {/* Axis titles */}
        <text x={PAD.l + CW / 2} y={H - 2} textAnchor="middle" fontSize={6.5} fontFamily="'Orbitron', monospace" fill={C.dimmer}>PARAMETERS →</text>
        <text x={10} y={PAD.t + CH / 2} textAnchor="middle" fontSize={6.5} fontFamily="'Orbitron', monospace" fill={C.dimmer} transform={`rotate(-90, 10, ${PAD.t + CH / 2})`}>QUALITY →</text>

        {/* Model points */}
        {SLM_MODELS.map((m, i) => {
          const cx = xOf(m.params), cy = yOf(m.quality);
          const isHi = highlight === i, isDim = highlight !== null && !isHi;
          return (
            <g key={m.name} style={{ cursor: "pointer" }}
              onMouseEnter={() => setHighlight(i)} onMouseLeave={() => setHighlight(null)}>
              <circle cx={cx} cy={cy} r={isHi ? 10 : 7}
                fill={m.col} fillOpacity={isDim ? 0.15 : 0.25}
                stroke={m.col} strokeWidth={isHi ? 2 : 1.5} strokeOpacity={isDim ? 0.2 : 1} />
              {!isDim && (
                <text x={cx} y={cy - 12} textAnchor="middle" fontSize={7} fontFamily="'Orbitron', monospace" fill={m.col} fontWeight="700">
                  {m.name}
                </text>
              )}
            </g>
          );
        })}

        {/* Hover detail */}
        {highlight !== null && (() => {
          const m = SLM_MODELS[highlight];
          const cx = xOf(m.params), cy = yOf(m.quality);
          return (
            <g>
              <rect x={cx + 12} y={cy - 20} width={110} height={m.type === "slm" ? 46 : 36} rx={5}
                fill={m.col + "22"} stroke={m.col + "66"} strokeWidth={1} />
              <text x={cx + 18} y={cy - 6} fontSize={7} fontFamily="'Orbitron', monospace" fill={m.col} fontWeight="700">{m.params}B params</text>
              <text x={cx + 18} y={cy + 6} fontSize={7} fontFamily="'Orbitron', monospace" fill={C.dim}>Quality: {m.quality}%</text>
              {m.type === "slm" && (
                <text x={cx + 18} y={cy + 18} fontSize={6.5} fontFamily="'Orbitron', monospace" fill={C.emerald}>✓ runs on-device</text>
              )}
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// ─── 21. MOE VIZ ─────────────────────────────────────────────────────────────
const MOE_EXPERTS = [
  { label: "Code",   col: C.emerald, icon: "💻" },
  { label: "Math",   col: C.blue,    icon: "∑"  },
  { label: "Logic",  col: C.purple,  icon: "⊢"  },
  { label: "Lang",   col: C.cyan,    icon: "A"  },
  { label: "Facts",  col: C.orange,  icon: "📚" },
  { label: "Reason", col: C.pink,    icon: "?"  },
  { label: "Vision", col: C.gold,    icon: "👁" },
  { label: "Tools",  col: C.dim,     icon: "⚙" },
];

const MOE_TOKENS = [
  { token: "def",     experts: [0, 2] },
  { token: "∫x²dx",  experts: [1, 2] },
  { token: "Paris",   experts: [3, 4] },
  { token: "console", experts: [0, 7] },
  { token: "if P then",experts: [2, 3]},
];

function MoEViz() {
  const [tokenIdx, setTokenIdx] = useState(0);
  const [auto, setAuto] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!auto) return;
    timerRef.current = setInterval(() => {
      setTokenIdx(i => (i + 1) % MOE_TOKENS.length);
    }, 1400);
    return () => clearInterval(timerRef.current);
  }, [auto]);

  const current = MOE_TOKENS[tokenIdx];
  const activeSet = new Set(current.experts);

  return (
    <div style={{ width: "100%" }}>
      {/* Token display */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, letterSpacing: "0.12em" }}>INPUT TOKEN</div>
        <AnimatePresence mode="wait">
          <motion.div key={tokenIdx} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            style={{ padding: "8px 18px", borderRadius: 10, background: C.purple + "25", border: `1.5px solid ${C.purple}77`, color: C.purple, fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 18, boxShadow: `0 0 20px ${C.purple}30` }}>
            {current.token}
          </motion.div>
        </AnimatePresence>
        <div style={{ height: 1, flex: 1, background: `linear-gradient(90deg, ${C.purple}44, transparent)` }} />
        <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer }}>→ ROUTER → top-2 experts</div>
      </div>

      {/* Expert grid */}
      <div className="moe-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
        {MOE_EXPERTS.map((exp, i) => {
          const isActive = activeSet.has(i);
          return (
            <motion.div key={exp.label}
              animate={{ background: isActive ? exp.col + "28" : "rgba(255,255,255,0.04)", borderColor: isActive ? exp.col + "88" : "rgba(255,255,255,0.08)", scale: isActive ? 1.05 : 1, boxShadow: isActive ? `0 0 20px ${exp.col}33` : "none" }}
              transition={{ duration: 0.3 }}
              style={{ padding: "10px 8px", borderRadius: 10, border: "1px solid", textAlign: "center", position: "relative" }}>
              {!isActive && (
                <div style={{ position: "absolute", top: 4, right: 5, fontSize: 7, color: C.dimmer, fontFamily: "'Orbitron', monospace" }}>zzz</div>
              )}
              <div style={{ fontSize: 16, marginBottom: 3 }}>{exp.icon}</div>
              <div style={{ fontSize: 8, fontFamily: "'Orbitron', monospace", color: isActive ? exp.col : C.dimmer, fontWeight: isActive ? 700 : 400 }}>
                {exp.label.toUpperCase()}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 0, borderRadius: 100, border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden" }}>
          {MOE_TOKENS.map((t, i) => (
            <button key={i} onClick={() => { setAuto(false); clearInterval(timerRef.current); setTokenIdx(i); }}
              style={{ padding: "5px 12px", fontSize: 12, fontFamily: "'Raleway', sans-serif", cursor: "pointer", border: "none", background: tokenIdx === i ? C.purple + "25" : "transparent", color: tokenIdx === i ? C.purple : C.dimmer, transition: "all 0.2s", fontWeight: tokenIdx === i ? 700 : 400 }}>
              {t.token}
            </button>
          ))}
        </div>
        <button onClick={() => { setAuto(true); }}
          style={{ padding: "5px 14px", borderRadius: 100, border: `1px solid ${auto ? C.emerald + "66" : "rgba(255,255,255,0.1)"}`, background: auto ? C.emerald + "18" : "transparent", color: auto ? C.emerald : C.dimmer, fontSize: 9, fontFamily: "'Orbitron', monospace", cursor: "pointer" }}>
          {auto ? "AUTO" : "AUTO →"}
        </button>
      </div>
    </div>
  );
}

// ─── 22. INFERENCE-TIME SCALING VIZ ──────────────────────────────────────────
const ITS_QUESTION = "Prove that √2 is irrational.";
const ITS_MODES = [
  {
    key: "greedy",
    label: "Greedy",
    sub: "1 sample, no checking",
    col: C.dim,
    compute: "1×",
    answer: "√2 is irrational because it cannot be expressed as p/q.",
    correct: false,
    note: "Incomplete — no proof given.",
    samples: 1,
  },
  {
    key: "best_of_n",
    label: "Best-of-N",
    sub: "8 samples, pick best",
    col: C.orange,
    compute: "8×",
    answer: "Assume √2 = p/q in lowest terms. Then 2 = p²/q², so p² = 2q² — p is even. Let p=2k: 4k²=2q², q²=2k², so q is also even. Contradicts lowest terms. ∴ √2 is irrational.",
    correct: true,
    note: "Correct proof from 8 candidates.",
    samples: 8,
  },
  {
    key: "mcts",
    label: "MCTS",
    sub: "Tree search + verify",
    col: C.gold,
    compute: "32×",
    answer: "Assume √2 = p/q (irreducible). Then p²=2q², ∴ p=2k, ∴ q²=2k², ∴ q=2m. Both even — contradicts gcd(p,q)=1. ∴ √2 ∉ ℚ. ∎",
    correct: true,
    note: "Concise, verified, optimal.",
    samples: 32,
  },
];

function InferenceTimeScalingViz() {
  const [mode, setMode] = useState("greedy");
  const current = ITS_MODES.find(m => m.key === mode);
  const W = 380, H = 160;

  // Build tree geometry per mode
  const renderTree = () => {
    const col = current.col;
    const nodes = [], edges = [];

    if (mode === "greedy") {
      // Single path: root → 3 steps → answer
      const pts = [[W/2, 20], [W/2, 60], [W/2, 100], [W/2, 140]];
      pts.forEach((p, i) => {
        if (i > 0) edges.push({ x1: pts[i-1][0], y1: pts[i-1][1], x2: p[0], y2: p[1], col: col + "88" });
        nodes.push({ x: p[0], y: p[1], winner: i === pts.length - 1, col, label: i === 0 ? "start" : i === pts.length - 1 ? "answer" : null });
      });
    }

    if (mode === "best_of_n") {
      // 8 parallel paths from root, winner highlighted
      const root = [W/2, 18];
      const BRANCHES = 8;
      const xs = Array.from({ length: BRANCHES }, (_, i) => 24 + i * (W - 48) / (BRANCHES - 1));
      const winnerIdx = 5;
      nodes.push({ x: root[0], y: root[1], col, label: "start" });
      xs.forEach((x, i) => {
        const isWinner = i === winnerIdx;
        edges.push({ x1: root[0], y1: root[1], x2: x, y2: 80, col: isWinner ? col : col + "44" });
        edges.push({ x1: x, y1: 80, x2: x, y2: 130, col: isWinner ? col : col + "33" });
        nodes.push({ x, y: 80, col: isWinner ? col : col + "55", winner: false });
        nodes.push({ x, y: 130, col: isWinner ? col : col + "33", winner: isWinner, label: isWinner ? "best" : null });
      });
    }

    if (mode === "mcts") {
      // Tree: root → 3 branches, each branches again, with verifier checks
      const root = [W/2, 14];
      nodes.push({ x: root[0], y: root[1], col, label: "start" });
      const L1 = [[110, 55], [W/2, 55], [270, 55]];
      L1.forEach((p, i) => {
        edges.push({ x1: root[0], y1: root[1], x2: p[0], y2: p[1], col: col + "77" });
        nodes.push({ x: p[0], y: p[1], col: col + "bb", check: true });
      });
      // Expand first and second L1 nodes
      const L2 = [[60, 100], [155, 100], [W/2, 100], [310, 100]];
      [[0,1],[2,3]].forEach(([a, b], li) => {
        [a, b].forEach(j => {
          const isWinner = li === 0 && j === 1;
          edges.push({ x1: L1[li][0], y1: L1[li][1], x2: L2[j][0], y2: L2[j][1], col: isWinner ? col : col + "44" });
          nodes.push({ x: L2[j][0], y: L2[j][1], col: isWinner ? col : col + "44", winner: isWinner, check: !isWinner });
          if (isWinner) {
            edges.push({ x1: L2[j][0], y1: L2[j][1], x2: L2[j][0], y2: 140, col });
            nodes.push({ x: L2[j][0], y: 140, col, winner: true, label: "✓ best" });
          }
        });
      });
      // Third L1 pruned
      nodes.push({ x: L1[2][0], y: L1[2][1] + 30, col: col + "22", pruned: true });
      edges.push({ x1: L1[2][0], y1: L1[2][1], x2: L1[2][0], y2: L1[2][1] + 30, col: col + "22" });
    }

    return { nodes, edges };
  };

  const { nodes, edges } = renderTree();

  return (
    <div style={{ width: "100%" }}>
      {/* Mode selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {ITS_MODES.map(m => (
          <button key={m.key} onClick={() => setMode(m.key)}
            style={{ padding: "8px 16px", borderRadius: 10, border: `1px solid ${mode === m.key ? m.col + "88" : "rgba(255,255,255,0.1)"}`, background: mode === m.key ? m.col + "20" : "rgba(255,255,255,0.04)", color: mode === m.key ? m.col : C.dimmer, fontFamily: "'Orbitron', monospace", fontSize: 9, cursor: "pointer", transition: "all 0.25s", textAlign: "center" }}>
            <div style={{ fontWeight: 700, letterSpacing: "0.08em" }}>{m.label}</div>
            <div style={{ fontSize: 9, opacity: 0.7, marginTop: 2 }}>{m.sub}</div>
            <div style={{ fontSize: 13, fontWeight: 800, marginTop: 3 }}>{m.compute}</div>
          </button>
        ))}
      </div>

      {/* Search tree */}
      <AnimatePresence mode="wait">
        <motion.div key={mode} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
          style={{ borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.25)", overflow: "hidden", marginBottom: 14 }}>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }}>
            {edges.map((e, i) => (
              <line key={i} x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} stroke={e.col} strokeWidth={1.5} />
            ))}
            {nodes.map((n, i) => (
              <g key={i}>
                <circle cx={n.x} cy={n.y} r={n.winner ? 9 : 5}
                  fill={n.col + (n.winner ? "44" : "22")} stroke={n.col} strokeWidth={n.winner ? 2 : 1} />
                {n.check && !n.winner && !n.pruned && (
                  <text x={n.x + 8} y={n.y + 3} fontSize={7} fill={current.col + "77"} fontFamily="'Orbitron', monospace">✓?</text>
                )}
                {n.pruned && (
                  <text x={n.x + 8} y={n.y + 3} fontSize={7} fill="rgba(255,255,255,0.2)" fontFamily="'Orbitron', monospace">✗</text>
                )}
                {n.label && (
                  <text x={n.x} y={n.y + (n.winner ? 20 : -10)} textAnchor="middle" fontSize={7}
                    fontFamily="'Orbitron', monospace" fill={n.col} fontWeight={n.winner ? "700" : "400"}>
                    {n.label}
                  </text>
                )}
              </g>
            ))}
          </svg>
        </motion.div>
      </AnimatePresence>

      {/* Result */}
      <div style={{ padding: "10px 14px", borderRadius: 8, background: current.correct ? current.col + "12" : "rgba(255,255,255,0.04)", border: `1px solid ${current.correct ? current.col + "55" : "rgba(255,255,255,0.1)"}` }}>
        <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: current.correct ? current.col : C.pink, marginBottom: 5 }}>
          {current.correct ? "✓ CORRECT" : "✗ INCOMPLETE"} · {current.compute} compute
        </div>
        <div style={{ color: C.white, fontSize: 13, fontFamily: "'Raleway', sans-serif", lineHeight: 1.6 }}>
          {current.answer}
        </div>
      </div>
    </div>
  );
}

// ─── Concept Section ──────────────────────────────────────────────────────────
function ConceptSection({ category, accent = C.cyan, title, hook, flow, analogy, tools }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  return (
    <section ref={ref} style={{
      padding: "40px clamp(24px,8vw,120px)",
      position: "relative", zIndex: 1,
    }}>
      {/* Outer ambient glow */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `radial-gradient(ellipse at 12% 50%, ${accent}0d 0%, transparent 58%)`,
      }} />

      <motion.div
        initial="hidden" animate={isInView ? "visible" : "hidden"}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
        style={{ position: "relative" }}>

        {/* ── Glass card ── */}
        <div style={{
          position: "relative",
          background: "rgba(255,255,255,0.022)",
          border: "1px solid rgba(255,255,255,0.065)",
          borderRadius: 24,
          overflow: "hidden",
        }}>

          {/* Glowing top border */}
          <div style={{
            position: "absolute", top: 0, left: "8%", right: "8%", height: 1,
            background: `linear-gradient(90deg, transparent, ${accent}cc, ${accent}, ${accent}cc, transparent)`,
            boxShadow: `0 0 18px ${accent}70`,
          }} />

          {/* Ghost letter backdrop */}
          <div style={{
            position: "absolute", right: -8, bottom: -16,
            fontSize: "clamp(150px,22vw,260px)", fontWeight: 900, lineHeight: 1,
            color: `${accent}05`, fontFamily: "'Raleway', sans-serif",
            userSelect: "none", pointerEvents: "none", letterSpacing: "-0.04em",
          }}>{title[0]}</div>

          {/* Content grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
            gap: "clamp(36px,5vw,72px)",
            padding: "48px clamp(28px,4vw,56px)",
            alignItems: "start",
            position: "relative",
          }}>

            {/* ── Left: label / title / hook / tools ── */}
            <div>
              {/* Category pill */}
              <motion.div variants={fadeUp} style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "5px 14px 5px 10px",
                background: `${accent}14`, border: `1px solid ${accent}38`,
                borderRadius: 100, marginBottom: 22,
              }}>
                <div style={{
                  width: 5, height: 5, borderRadius: "50%", flexShrink: 0,
                  background: accent, boxShadow: `0 0 8px ${accent}`,
                }} />
                <span style={{
                  fontFamily: "'Orbitron', monospace", fontSize: 8.5, letterSpacing: "0.26em",
                  color: accent, textTransform: "uppercase", fontWeight: 700,
                }}>{category}</span>
              </motion.div>

              <motion.h3 variants={fadeUp} style={{
                color: C.white, fontSize: "clamp(24px,2.8vw,46px)", fontWeight: 800,
                lineHeight: 1.1, margin: "0 0 18px", letterSpacing: "-0.02em",
                fontFamily: "'Raleway', sans-serif",
              }}>{title}</motion.h3>

              {hook && (
                <motion.p variants={fadeUp} style={{
                  color: C.dim, fontSize: "clamp(13px,1.2vw,16px)", lineHeight: 1.8,
                  margin: "0 0 28px",
                }}>{hook}</motion.p>
              )}

              {tools && tools.length > 0 && (
                <motion.div variants={fadeUp} style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                  {tools.map(t => (
                    <span key={t} style={{
                      padding: "4px 12px", borderRadius: 100, fontSize: 12, fontWeight: 600,
                      background: `${accent}12`, border: `1px solid ${accent}30`, color: accent,
                      letterSpacing: "0.04em",
                    }}>{t}</span>
                  ))}
                </motion.div>
              )}
            </div>

            {/* ── Right: step cards + analogy ── */}
            <div>
              {flow && flow.length > 0 && (
                <div style={{ marginBottom: analogy ? 18 : 0 }}>
                  {flow.map((step, i) => (
                    <div key={i}>
                      <motion.div
                        variants={{ hidden: { opacity: 0, x: 22 }, visible: { opacity: 1, x: 0, transition: { duration: 0.45, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] } } }}
                        style={{
                          display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center",
                          padding: "13px 18px",
                          background: `${accent}09`,
                          border: `1px solid ${accent}22`,
                          borderRadius: 12,
                        }}>
                        {/* Step number */}
                        <div style={{
                          width: 27, height: 27, borderRadius: "50%", flexShrink: 0,
                          background: `${accent}1e`, border: `1px solid ${accent}55`,
                          boxShadow: `0 0 10px ${accent}28`,
                          display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 800, color: accent,
                          fontFamily: "'Orbitron', monospace",
                        }}>{i + 1}</div>
                        <div style={{
                          color: C.white, fontSize: "clamp(11px,1vw,13px)",
                          lineHeight: 1.45, fontWeight: 500,
                        }}>{step}</div>
                      </motion.div>
                      {/* Short connector between cards */}
                      {i < flow.length - 1 && (
                        <div style={{
                          marginLeft: 27, width: 1, height: 9,
                          background: `linear-gradient(to bottom, ${accent}45, ${accent}18)`,
                        }} />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Analogy card */}
              {analogy && (
                <motion.div variants={fadeUp} style={{
                  position: "relative",
                  padding: "18px 20px 18px 50px",
                  background: "rgba(255,255,255,0.018)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 12,
                }}>
                  {/* Large quotation mark */}
                  <div style={{
                    position: "absolute", left: 14, top: 10,
                    fontSize: 42, lineHeight: 1, fontFamily: "Georgia, serif",
                    fontWeight: 900, color: accent, opacity: 0.35, userSelect: "none",
                  }}>"</div>
                  <div style={{
                    fontSize: 8.5, letterSpacing: "0.22em", color: accent, opacity: 0.6,
                    marginBottom: 7, textTransform: "uppercase", fontFamily: "'Orbitron', monospace",
                  }}>Analogy</div>
                  <p style={{
                    color: C.dim, fontSize: "clamp(11px,1vw,13px)", lineHeight: 1.8,
                    margin: 0, fontStyle: "italic",
                  }}>{analogy}</p>
                </motion.div>
              )}
            </div>

          </div>
        </div>
      </motion.div>
    </section>
  );
}

// ─── Application Layer Grid ────────────────────────────────────────────────────
const APP_CONCEPTS = [
  { Icon: Search,    accent: C.emerald, title: "RAG",                  desc: "Retrieve relevant documents at query time and inject them into context. Grounds answers in real, up-to-date sources instead of memorised knowledge." },
  { Icon: Database,  accent: C.blue,    title: "Vector Databases",      desc: "Store embeddings and serve approximate nearest-neighbour search. Given a query vector, return the most semantically similar documents in milliseconds." },
  { Icon: Wrench,    accent: C.orange,  title: "Tool Use",              desc: "The model outputs structured JSON to call external functions — search, code execution, API calls — then receives the result as context and continues." },
  { Icon: Bot,       accent: C.purple,  title: "Agents",                desc: "An LLM in a loop: observe → reason → act → observe. Multi-step autonomy lets models complete long-horizon tasks that can't fit in a single prompt." },
  { Icon: Plug,      accent: C.cyan,    title: "MCP",                   desc: "Model Context Protocol — an open standard for connecting models to tools and data sources. One protocol, any model, any server." },
  { Icon: FileText,  accent: C.gold,    title: "Context Engineering",   desc: "The art of deciding what goes into the context window: which docs, which examples, how to structure the system prompt, how to manage token budget." },
  { Icon: Copy,      accent: C.pink,    title: "Few-Shot Prompting",    desc: "Include 2–5 worked examples in the prompt. The model pattern-matches format and style — in-context learning with zero gradient updates." },
  { Icon: BarChart2, accent: C.emerald, title: "Evals",                 desc: "Automated test suites measuring quality: accuracy, factuality, preference win-rates. Without evals you can't tell if a model change helped or hurt." },
  { Icon: Shield,    accent: C.red,     title: "Guardrails",            desc: "Input/output filters — rule-based, classifier-based, or LLM-based — that detect and block harmful content, jailbreaks, PII leakage, and off-topic requests." },
  { Icon: Eye,       accent: C.blue,    title: "Multimodal",            desc: "Vision and audio encoders convert patches and spectrograms into tokens fed through the same Transformer. The same brain, with new sensory inputs wired in." },
];

function ApplicationGridSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.08 });
  return (
    <section ref={ref} style={{
      padding: "80px clamp(24px,8vw,120px)",
      position: "relative", zIndex: 1,
    }}>
      <motion.div initial="hidden" animate={isInView ? "visible" : "hidden"}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.07 } } }}>
        <motion.div variants={fadeUp} style={{
          fontFamily: "'Orbitron', monospace", fontSize: 9, letterSpacing: "0.3em",
          color: C.purple, marginBottom: 14, opacity: 0.65, textTransform: "uppercase",
        }}>Application Layer</motion.div>
        <motion.h3 variants={fadeUp} style={{
          color: C.white, fontSize: "clamp(26px,3.2vw,52px)", fontWeight: 800,
          lineHeight: 1.08, margin: "0 0 14px", letterSpacing: "-0.02em",
          fontFamily: "'Raleway', sans-serif",
        }}>How models plug into the world.</motion.h3>
        <motion.p variants={fadeUp} style={{
          color: C.dim, fontSize: "clamp(14px,1.3vw,17px)", lineHeight: 1.75,
          maxWidth: 640, margin: "0 0 48px",
        }}>
          The raw language model is just the core. These techniques and patterns turn it into something that can act, search, remember, and be trusted.
        </motion.p>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(clamp(240px,28vw,320px), 1fr))",
          gap: 20,
        }}>
          {APP_CONCEPTS.map(({ Icon, accent, title, desc }) => (
            <motion.div key={title} variants={fadeUp} style={{
              background: `${accent}09`,
              border: `1px solid ${accent}28`,
              borderRadius: 16,
              padding: "28px 24px",
              position: "relative",
              overflow: "hidden",
              transition: "border-color 0.3s, box-shadow 0.3s",
            }}
            whileHover={{ borderColor: `${accent}60`, boxShadow: `0 0 32px ${accent}18` }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: `${accent}18`, border: `1px solid ${accent}35`,
                display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center",
                marginBottom: 18,
              }}>
                <Icon size={18} style={{ color: accent }} />
              </div>
              <h4 style={{
                color: C.white, fontSize: "clamp(14px,1.3vw,17px)", fontWeight: 700,
                margin: "0 0 10px", letterSpacing: "-0.01em",
                fontFamily: "'Raleway', sans-serif",
              }}>{title}</h4>
              <p style={{
                color: C.dim, fontSize: "clamp(12px,1vw,13px)", lineHeight: 1.72,
                margin: 0,
              }}>{desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

// ─── HERO ─────────────────────────────────────────────────────────────────────
// ─── Hero 3D neural network ─────────────────────────────────────────────────
// Soft circular sprite so each node reads as a glowing point (additive blending).
function makeNodeSprite() {
  const s = 64;
  const cv = document.createElement("canvas");
  cv.width = cv.height = s;
  const g = cv.getContext("2d");
  const grad = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.25, "rgba(255,255,255,0.85)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, s, s);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// Face-on feed-forward network (the classic neural-net diagram). Every node sits
// at z=0 so it reads as a small crisp dot — no depth blow-up at the corners.
const LAYER_SIZES = [4, 7, 9, 7, 4];
const HALF_W = 7;   // half-width of the network in world units
const HALF_H = 4.2; // half-height

function buildNetwork() {
  const halfW = HALF_W, halfH = HALF_H;
  const LAYERS = LAYER_SIZES.length;
  const cols = [C.cyan, C.blue, C.purple, C.pink, C.cyan];
  const nodes = [];
  const layerStart = [];
  LAYER_SIZES.forEach((n, l) => {
    layerStart[l] = nodes.length;
    const x = LAYERS === 1 ? 0 : -halfW + (2 * halfW) * (l / (LAYERS - 1));
    const col = new THREE.Color(cols[l % cols.length]);
    for (let i = 0; i < n; i++) {
      const y = n === 1 ? 0 : -halfH + (2 * halfH) * (i / (n - 1));
      nodes.push({ pos: new THREE.Vector3(x, y, 0), col, layer: l });
    }
  });
  // Fully connect consecutive layers; store the gap index so we know firing order.
  const links = [];
  for (let l = 0; l < LAYERS - 1; l++) {
    const aS = layerStart[l], bS = layerStart[l + 1];
    for (let i = 0; i < LAYER_SIZES[l]; i++)
      for (let j = 0; j < LAYER_SIZES[l + 1]; j++)
        links.push([aS + i, bS + j, l]);
  }
  return { nodes, links, LAYERS };
}

function NeuralNet() {
  const nodeRef = useRef();
  const pulseRef = useRef();
  const sprite = useMemo(() => makeNodeSprite(), []);
  const { nodes, links, LAYERS } = useMemo(() => buildNetwork(), []);

  // Fit the camera to the canvas (not window), but back off so the net reads as a
  // contained visual within the frame — like the pixel grid in CV's hero or the
  // particle wave in Audio's — instead of bleeding to the screen edges.
  const size = useThree((s) => s.size);
  const camera = useThree((s) => s.camera);
  useLayoutEffect(() => {
    const aspect = size.width / size.height;
    const vFov = (camera.fov * Math.PI) / 180;
    const dH = HALF_H / Math.tan(vFov / 2);
    const dW = HALF_W / (Math.tan(vFov / 2) * aspect);
    camera.position.set(0, 0, Math.max(dH, dW) * 1.25);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  }, [size, camera]);

  // Node cloud — colours get modulated live by the activation wave.
  const nodeGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(nodes.length * 3);
    nodes.forEach((n, i) => {
      pos[i * 3] = n.pos.x; pos[i * 3 + 1] = n.pos.y; pos[i * 3 + 2] = 0;
    });
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("color", new THREE.BufferAttribute(new Float32Array(nodes.length * 3), 3));
    return g;
  }, [nodes]);

  // Static connection lines (vertex colours fade between the two endpoints).
  const lineGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(links.length * 6);
    const col = new Float32Array(links.length * 6);
    links.forEach(([a, b], i) => {
      const na = nodes[a], nb = nodes[b];
      pos.set([na.pos.x, na.pos.y, 0, nb.pos.x, nb.pos.y, 0], i * 6);
      col.set([na.col.r, na.col.g, na.col.b, nb.col.r, nb.col.g, nb.col.b], i * 6);
    });
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("color", new THREE.BufferAttribute(col, 3));
    return g;
  }, [links, nodes]);

  // One pulse per connection; only the connections the wave is currently crossing
  // light up, so signals visibly fire layer by layer.
  const pulseGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(links.length * 3), 3));
    g.setAttribute("color", new THREE.BufferAttribute(new Float32Array(links.length * 3), 3));
    return g;
  }, [links]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    // Wave sweeps 0 → LAYERS-1, then a short gap before looping (a fresh "thought").
    const total = (LAYERS - 1) + 1.6;
    const w = (t * 0.85) % total;

    // Nodes brighten as the wave reaches their layer.
    const nc = nodeRef.current.geometry.attributes.color.array;
    nodes.forEach((n, i) => {
      const d = n.layer - w;
      const b = Math.exp(-(d * d) / (2 * 0.42 * 0.42));
      const lvl = 0.38 + 0.62 * b;
      nc[i * 3] = n.col.r * lvl; nc[i * 3 + 1] = n.col.g * lvl; nc[i * 3 + 2] = n.col.b * lvl;
    });
    nodeRef.current.geometry.attributes.color.needsUpdate = true;

    // Pulses ride the wave front across each layer's connections.
    const pp = pulseRef.current.geometry.attributes.position.array;
    const pc = pulseRef.current.geometry.attributes.color.array;
    links.forEach(([a, b, gap], i) => {
      const p = w - gap;
      if (p >= 0 && p <= 1) {
        const A = nodes[a].pos, B = nodes[b].pos;
        pp[i * 3] = A.x + (B.x - A.x) * p;
        pp[i * 3 + 1] = A.y + (B.y - A.y) * p;
        pp[i * 3 + 2] = 0;
        const br = Math.sin(Math.PI * p);
        const c = nodes[b].col;
        pc[i * 3] = c.r * br; pc[i * 3 + 1] = c.g * br; pc[i * 3 + 2] = c.b * br;
      } else {
        pc[i * 3] = 0; pc[i * 3 + 1] = 0; pc[i * 3 + 2] = 0; // invisible under additive blending
      }
    });
    pulseRef.current.geometry.attributes.position.needsUpdate = true;
    pulseRef.current.geometry.attributes.color.needsUpdate = true;
  });

  return (
    <group>
      <lineSegments geometry={lineGeo}>
        <lineBasicMaterial vertexColors transparent opacity={0.18} blending={THREE.AdditiveBlending} depthWrite={false} />
      </lineSegments>
      <points ref={nodeRef} geometry={nodeGeo}>
        <pointsMaterial map={sprite} size={0.85} sizeAttenuation vertexColors transparent opacity={1} blending={THREE.AdditiveBlending} depthWrite={false} />
      </points>
      <points ref={pulseRef} geometry={pulseGeo}>
        <pointsMaterial map={sprite} size={0.55} sizeAttenuation vertexColors transparent opacity={1} blending={THREE.AdditiveBlending} depthWrite={false} />
      </points>
    </group>
  );
}

function HeroScene() {
  // NeuralNet fits the camera to the canvas itself (see its useLayoutEffect),
  // so constraining this box is what makes the net read as a contained visual
  // rather than a full-bleed background.
  return (
    <Canvas
      camera={{ fov: 50, near: 0.1, far: 100, position: [0, 0, 16] }}
      gl={{ antialias: true, alpha: true }}
      style={{ width: "100%", height: "100%" }}
    >
      <NeuralNet />
    </Canvas>
  );
}

function Hero() {
  return (
    <section style={{
      minHeight: "100svh", display: "flex", flexDirection: "column",
      justifyContent: "flex-start", alignItems: "center", textAlign: "center",
      padding: "100px 24px 80px", position: "relative", zIndex: 1, overflow: "hidden",
      background: C.bg,
    }}>
      {/* Contained 3D neural network, sitting below the copy like CV's pixel grid / Audio's particle wave */}
      <div style={{
        position: "absolute", left: "50%", top: "40%", transform: "translateX(-50%)",
        width: "min(1000px, 90vw)", height: "min(520px, 50vh)", zIndex: 0,
      }}>
        <HeroScene />
      </div>
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.2 }}
        style={{ position: "relative", zIndex: 1, fontFamily: "'Orbitron', monospace", fontSize: 11, letterSpacing: "0.3em", color: C.cyan, opacity: 0.8, marginBottom: 20 }}>
        AN INTERACTIVE JOURNEY
      </motion.div>
      <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: "relative", zIndex: 1,
          fontSize: "clamp(40px, 6vw, 84px)", fontWeight: 900, lineHeight: 0.98,
          letterSpacing: "-0.03em", margin: "0 0 20px", color: C.white,
          fontFamily: "'Raleway', sans-serif",
          textShadow: `0 0 48px ${C.cyan}40, 0 0 16px rgba(255,255,255,0.25)`,
        }}>
        How AI Thinks
      </motion.h1>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.7 }}
        style={{ position: "relative", zIndex: 1, color: C.dim, fontSize: "clamp(14px,1.6vw,17px)", lineHeight: 1.6, maxWidth: 480, margin: 0 }}>
        A visual journey inside modern language models. No equations. No jargon. Just understanding.
      </motion.p>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
        style={{ position: "absolute", bottom: 40, left: "50%", transform: "translateX(-50%)", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <span style={{ color: C.dimmer, fontSize: 11, fontFamily: "'Orbitron', monospace", letterSpacing: "0.2em" }}>SCROLL TO EXPLORE</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}
          style={{ width: 1, height: 40, background: `linear-gradient(${C.cyan}, transparent)` }} />
      </motion.div>
    </section>
  );
}

// ─── CHAPTER 1 — TOKENS ───────────────────────────────────────────────────────
const TOKEN_COLORS = ["#06EEF0", "#8B5CF6", "#FF8C42", "#FF6B9D", "#10B981", "#00A8FF", "#F5C542", "#FF4466"];
const SAMPLE_TOKENS = [
  { text: "The", id: 464 },
  { text: " quick", id: 4996 },
  { text: " brown", id: 7586 },
  { text: " fox", id: 21831 },
  { text: " jumps", id: 35308 },
  { text: " over", id: 625 },
  { text: " the", id: 262 },
  { text: " lazy", id: 16931 },
  { text: " dog", id: 3290 },
];

function TokensViz() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });
  const [phase, setPhase] = useState(0); // 0=sentence, 1=split, 2=numbered

  useEffect(() => {
    if (!isInView) return;
    const t1 = setTimeout(() => setPhase(1), 600);
    const t2 = setTimeout(() => setPhase(2), 1800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isInView]);

  return (
    <div ref={ref} style={{ marginTop: 56, width: "100%", maxWidth: 860 }}>
      {/* Original sentence */}
      <AnimatePresence>
        {phase === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ fontSize: "clamp(20px,3.5vw,42px)", color: C.white, fontFamily: "'Raleway', sans-serif", letterSpacing: "-0.01em", marginBottom: 12 }}>
            The quick brown fox jumps over the lazy dog
          </motion.div>
        )}
      </AnimatePresence>

      {/* Token blocks */}
      {phase >= 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
          {SAMPLE_TOKENS.map((tok, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 30, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              {phase >= 2 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
                  style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", color: TOKEN_COLORS[i % TOKEN_COLORS.length], opacity: 0.7 }}>
                  #{tok.id}
                </motion.div>
              )}
              <div style={{
                padding: "10px 16px", borderRadius: 10,
                background: TOKEN_COLORS[i % TOKEN_COLORS.length] + "20",
                border: `1.5px solid ${TOKEN_COLORS[i % TOKEN_COLORS.length]}55`,
                color: TOKEN_COLORS[i % TOKEN_COLORS.length],
                fontSize: "clamp(14px,2vw,22px)", fontFamily: "'Raleway', sans-serif", fontWeight: 600,
                whiteSpace: "pre",
              }}>
                {tok.text}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {phase >= 2 && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          style={{ color: C.dim, fontSize: 14, marginTop: 32, lineHeight: 1.7, maxWidth: 560 }}>
          Every word (and punctuation) becomes an integer ID from the model's vocabulary.
          These IDs — not letters — are what the AI actually reads.
        </motion.p>
      )}
    </div>
  );
}

// ─── CHAPTER 2 — EMBEDDINGS ───────────────────────────────────────────────────
const EMBED_WORDS = [
  { word: "King",    x: 0.25, y: 0.22, col: C.gold,    cluster: "royalty" },
  { word: "Queen",   x: 0.38, y: 0.18, col: C.gold,    cluster: "royalty" },
  { word: "Prince",  x: 0.30, y: 0.38, col: C.gold,    cluster: "royalty" },
  { word: "Princess",x: 0.44, y: 0.34, col: C.gold,    cluster: "royalty" },
  { word: "Dog",     x: 0.72, y: 0.25, col: C.emerald, cluster: "animal" },
  { word: "Cat",     x: 0.83, y: 0.22, col: C.emerald, cluster: "animal" },
  { word: "Puppy",   x: 0.78, y: 0.40, col: C.emerald, cluster: "animal" },
  { word: "Car",     x: 0.18, y: 0.72, col: C.blue,    cluster: "vehicle" },
  { word: "Truck",   x: 0.28, y: 0.78, col: C.blue,    cluster: "vehicle" },
  { word: "Train",   x: 0.12, y: 0.82, col: C.blue,    cluster: "vehicle" },
  { word: "Moon",    x: 0.65, y: 0.70, col: C.purple,  cluster: "space" },
  { word: "Planet",  x: 0.75, y: 0.75, col: C.purple,  cluster: "space" },
  { word: "Star",    x: 0.62, y: 0.82, col: C.purple,  cluster: "space" },
];

function EmbeddingsCanvas() {
  const canvasRef = useRef(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [hovered, setHovered] = useState(null);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);

  useEffect(() => {
    if (!isInView) return;
    const start = Date.now();
    const dur = 1200;
    const tick = () => {
      const p = Math.min((Date.now() - start) / dur, 1);
      progressRef.current = p;
      setProgress(p);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [isInView]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = 560; canvas.height = 420;
    const W = 560, H = 420;
    ctx.clearRect(0, 0, W, H);

    const p = progressRef.current;

    // Draw cluster circles (background halos)
    const clusters = {
      royalty: { cx: 0.345, cy: 0.28, col: C.gold },
      animal:  { cx: 0.775, cy: 0.295, col: C.emerald },
      vehicle: { cx: 0.19,  cy: 0.775, col: C.blue },
      space:   { cx: 0.673, cy: 0.757, col: C.purple },
    };
    Object.values(clusters).forEach(cl => {
      const g = ctx.createRadialGradient(cl.cx * W, cl.cy * H, 0, cl.cx * W, cl.cy * H, 80);
      g.addColorStop(0, cl.col + "22");
      g.addColorStop(1, "transparent");
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(cl.cx * W, cl.cy * H, 80, 0, Math.PI * 2); ctx.fill();
    });

    // Draw words
    EMBED_WORDS.forEach((w, i) => {
      const tx = w.x * W, ty = w.y * H;
      const x = W / 2 + (tx - W / 2) * p;
      const y = H / 2 + (ty - H / 2) * p;
      const alpha = p;

      // Glow
      const g = ctx.createRadialGradient(x, y, 0, x, y, 18);
      g.addColorStop(0, w.col + Math.round(alpha * 140).toString(16).padStart(2, "0"));
      g.addColorStop(1, "transparent");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, 18, 0, Math.PI * 2); ctx.fill();

      // Dot
      ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fillStyle = w.col + Math.round(alpha * 255).toString(16).padStart(2, "0"); ctx.fill();

      // Label
      ctx.fillStyle = w.col + Math.round(alpha * 230).toString(16).padStart(2, "0");
      ctx.font = "bold 12px 'Raleway', sans-serif";
      ctx.fillText(w.word, x + 8, y - 6);
    });
  }, [progress]);

  return (
    <div ref={ref} style={{ display: "flex", gap: 40, alignItems: "flex-start", flexWrap: "wrap", marginTop: 48, width: "100%" }}>
      <div style={{ position: "relative" }}>
        <canvas ref={canvasRef} style={{ width: 560, maxWidth: "100%", height: "auto", borderRadius: 16, border: "1px solid rgba(139,92,246,0.2)" }} />
        {/* Axis labels */}
        <div style={{ position: "absolute", bottom: 8, right: 12, fontSize: 9, fontFamily: "'Orbitron', monospace", color: C.dimmer, letterSpacing: "0.1em" }}>
          EMBEDDING SPACE (2D projection)
        </div>
      </div>
      <div style={{ maxWidth: 280, paddingTop: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            { col: C.gold,    label: "Royalty", desc: "King, Queen, Prince, Princess" },
            { col: C.emerald, label: "Animals", desc: "Dog, Cat, Puppy" },
            { col: C.blue,    label: "Vehicles", desc: "Car, Truck, Train" },
            { col: C.purple,  label: "Space", desc: "Moon, Planet, Star" },
          ].map(item => (
            <motion.div key={item.label} initial={{ opacity: 0, x: 20 }}
              animate={progress > 0.5 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5 }}
              style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: item.col, flexShrink: 0 }} />
              <div>
                <div style={{ color: item.col, fontSize: 14, fontWeight: 700 }}>{item.label}</div>
                <div style={{ color: C.dimmer, fontSize: 13 }}>{item.desc}</div>
              </div>
            </motion.div>
          ))}
        </div>
        <p style={{ color: C.dim, fontSize: 14.5, lineHeight: 1.7, marginTop: 24 }}>
          Similar words land near each other in space. The model never memorised this — it emerged from training on billions of sentences.
        </p>
      </div>
    </div>
  );
}

// ─── CHAPTER 3 — ATTENTION ────────────────────────────────────────────────────
const ATTN_SENTENCE = ["The", "cat", "sat", "on", "the", "mat", "because", "it", "was", "comfortable"];
const ATTN_WEIGHTS = {
  7: { 1: 0.85, 5: 0.72, 0: 0.3, 6: 0.45 },
  1: { 0: 0.6, 2: 0.5, 6: 0.3 },
  2: { 1: 0.7, 3: 0.4, 4: 0.2 },
  9: { 5: 0.8, 7: 0.6, 1: 0.4 },
};

function AttentionViz() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [hovered, setHovered] = useState(null);
  const svgRef = useRef(null);
  const [positions, setPositions] = useState([]);
  const wordRefs = useRef([]);

  useEffect(() => {
    if (!isInView) return;
    const measure = () => {
      const svgRect = svgRef.current?.getBoundingClientRect();
      if (!svgRect) return;
      const pos = wordRefs.current.map(el => {
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return {
          x: r.left + r.width / 2 - svgRect.left,
          y: r.top + r.height / 2 - svgRect.top,
        };
      });
      setPositions(pos);
    };
    setTimeout(measure, 100);
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isInView]);

  const weights = hovered !== null ? ATTN_WEIGHTS[hovered] || {} : {};

  return (
    <div ref={ref} style={{ marginTop: 56, width: "100%", maxWidth: 860 }}>
      <p style={{ color: C.dim, fontSize: 14, lineHeight: 1.7, marginBottom: 32, maxWidth: 560 }}>
        Hover any word. Watch which other words it "pays attention to" — how the model understands context.
      </p>
      <div style={{ position: "relative" }} ref={svgRef}>
        {/* SVG beams layer */}
        {positions.length > 0 && (
          <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible" }}>
            <defs>
              {Object.entries(weights).map(([to, w]) => (
                <marker key={`arrow-${to}`} id={`arrow-${to}`} markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                  <circle cx="3" cy="3" r="2" fill={C.cyan} opacity={w} />
                </marker>
              ))}
            </defs>
            <AnimatePresence>
              {hovered !== null && Object.entries(weights).map(([toStr, w]) => {
                const to = parseInt(toStr);
                const from = hovered;
                const p1 = positions[from], p2 = positions[to];
                if (!p1 || !p2) return null;
                return (
                  <motion.line key={`${from}-${to}`}
                    x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                    stroke={C.cyan} strokeWidth={w * 4}
                    strokeOpacity={w * 0.9}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    strokeLinecap="round"
                    style={{ filter: `drop-shadow(0 0 6px ${C.cyan})` }}
                  />
                );
              })}
            </AnimatePresence>
          </svg>
        )}
        {/* Words */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, position: "relative", zIndex: 1 }}>
          {ATTN_SENTENCE.map((word, i) => {
            const isSource = hovered === i;
            const isTarget = hovered !== null && weights[i] !== undefined;
            const strength = isTarget ? weights[i] : 0;
            return (
              <motion.div
                key={i}
                ref={el => wordRefs.current[i] = el}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                animate={{
                  scale: isSource ? 1.12 : isTarget ? 1 + strength * 0.08 : 1,
                  opacity: hovered !== null && !isSource && !isTarget ? 0.3 : 1,
                }}
                transition={{ duration: 0.2 }}
                style={{
                  padding: "10px 18px", borderRadius: 12, cursor: "default",
                  fontSize: "clamp(18px,2.5vw,28px)", fontFamily: "'Raleway', sans-serif", fontWeight: 700,
                  background: isSource
                    ? C.cyan + "30"
                    : isTarget
                    ? `rgba(6,238,240,${strength * 0.25})`
                    : "rgba(240,246,255,0.05)",
                  border: `1.5px solid ${isSource ? C.cyan : isTarget ? C.cyan + Math.round(strength * 200).toString(16).padStart(2,"0") : "rgba(240,246,255,0.12)"}`,
                  color: isSource ? C.cyan : isTarget ? C.white : C.dim,
                  transition: "background 0.2s, border 0.2s, color 0.2s",
                  userSelect: "none",
                }}>
                {word}
                {isTarget && (
                  <div style={{ fontSize: 9, textAlign: "center", opacity: 0.7, fontFamily: "'Orbitron', monospace", color: C.cyan, marginTop: 2 }}>
                    {Math.round(strength * 100)}%
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
      {hovered === 7 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          style={{ marginTop: 24, padding: "14px 18px", borderRadius: 12, background: C.cyan + "15", border: `1px solid ${C.cyan}40`, maxWidth: 480 }}>
          <span style={{ color: C.cyan, fontSize: 14.5, fontWeight: 600 }}>
            "it" strongly attends to "cat" (85%) and "mat" (72%) — understanding what "it" refers to.
          </span>
        </motion.div>
      )}
    </div>
  );
}

// ─── CHAPTER 4 — TRANSFORMER LAYERS ──────────────────────────────────────────
const LAYER_LABELS = [
  { name: "Input Embedding", col: C.blue,    desc: "Words become vectors" },
  { name: "Attention Layer 1", col: C.cyan,  desc: "Find relationships" },
  { name: "Feed-Forward 1",  col: C.purple,  desc: "Transform & enrich" },
  { name: "Attention Layer 2", col: C.cyan,  desc: "Deeper context" },
  { name: "Feed-Forward 2",  col: C.purple,  desc: "Refine meaning" },
  { name: "Output Projection", col: C.orange, desc: "Predict next token" },
];

function TransformerViz() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [activeLayer, setActiveLayer] = useState(null);
  const [flowing, setFlowing] = useState(false);

  useEffect(() => {
    if (!isInView) return;
    setTimeout(() => setFlowing(true), 800);
  }, [isInView]);

  return (
    <div ref={ref} style={{ marginTop: 56, width: "100%", maxWidth: 720, perspective: "1200px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, transform: "rotateX(12deg)", transformOrigin: "center bottom" }}>
        {[...LAYER_LABELS].reverse().map((layer, i) => {
          const realIndex = LAYER_LABELS.length - 1 - i;
          const isActive = activeLayer === realIndex;
          return (
            <motion.div key={i}
              initial={{ opacity: 0, y: 30, rotateX: -10 }}
              animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.7 }}
              onMouseEnter={() => setActiveLayer(realIndex)}
              onMouseLeave={() => setActiveLayer(null)}
              style={{
                padding: "16px 28px", borderRadius: 14,
                background: isActive ? layer.col + "25" : "rgba(240,246,255,0.03)",
                border: `1.5px solid ${isActive ? layer.col + "80" : layer.col + "30"}`,
                backdropFilter: "blur(12px)",
                display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
                cursor: "default", transition: "all 0.25s",
                position: "relative", overflow: "hidden",
              }}>
              {/* Flowing light particle */}
              {flowing && (
                <motion.div
                  animate={{ x: ["0%", "100%"], opacity: [0, 0.7, 0] }}
                  transition={{ repeat: Infinity, duration: 2.4, delay: realIndex * 0.28, ease: "linear" }}
                  style={{
                    position: "absolute", top: "50%", left: 0, transform: "translateY(-50%)",
                    width: 60, height: 2, borderRadius: 1,
                    background: `linear-gradient(90deg, transparent, ${layer.col}, transparent)`,
                    pointerEvents: "none",
                  }}
                />
              )}
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, zIndex: 1 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: layer.col, flexShrink: 0, boxShadow: `0 0 8px ${layer.col}` }} />
                <div>
                  <div style={{ color: C.white, fontSize: 15, fontWeight: 700, fontFamily: "'Raleway', sans-serif" }}>{layer.name}</div>
                  <div style={{ color: layer.col, fontSize: 13, opacity: 0.8, marginTop: 2 }}>{layer.desc}</div>
                </div>
              </div>
              <div style={{ color: layer.col + "60", fontFamily: "'Orbitron', monospace", fontSize: 10, zIndex: 1 }}>
                LAYER {realIndex + 1}
              </div>
            </motion.div>
          );
        })}
      </div>
      <motion.div initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 1 }}
        style={{ textAlign: "center", marginTop: 24, color: C.dimmer, fontSize: 12, fontFamily: "'Orbitron', monospace", letterSpacing: "0.1em" }}>
        ↑ INFORMATION FLOWS UPWARD THROUGH EACH LAYER ↑
      </motion.div>
    </div>
  );
}

// ─── CHAPTER 5 — PREDICTION ──────────────────────────────────────────────────
const PREDICTIONS = [
  [
    { word: "cat",   prob: 0.42, col: C.orange },
    { word: "dog",   prob: 0.28, col: C.blue },
    { word: "bird",  prob: 0.15, col: C.purple },
    { word: "mouse", prob: 0.09, col: C.cyan },
    { word: "fish",  prob: 0.06, col: C.pink },
  ],
  [
    { word: "sat",   prob: 0.38, col: C.orange },
    { word: "ran",   prob: 0.24, col: C.blue },
    { word: "slept", prob: 0.20, col: C.purple },
    { word: "played",prob: 0.12, col: C.cyan },
    { word: "ate",   prob: 0.06, col: C.pink },
  ],
  [
    { word: "on",    prob: 0.52, col: C.orange },
    { word: "near",  prob: 0.22, col: C.blue },
    { word: "under", prob: 0.14, col: C.purple },
    { word: "by",    prob: 0.08, col: C.cyan },
    { word: "above", prob: 0.04, col: C.pink },
  ],
];

function PredictionViz() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });
  const [step, setStep] = useState(0);
  const [chosen, setChosen] = useState([]);
  const [selecting, setSelecting] = useState(false);

  useEffect(() => {
    if (!isInView || step >= PREDICTIONS.length) return;
    const cycle = () => {
      setSelecting(true);
      setTimeout(() => {
        const pick = PREDICTIONS[step][0].word;
        setChosen(prev => [...prev, pick]);
        setStep(s => s + 1);
        setSelecting(false);
      }, 1400);
    };
    const t = setTimeout(cycle, 600);
    return () => clearTimeout(t);
  }, [isInView, step]);

  const currentPreds = PREDICTIONS[Math.min(step, PREDICTIONS.length - 1)];

  return (
    <div ref={ref} style={{ marginTop: 56, width: "100%", maxWidth: 720 }}>
      {/* Context so far */}
      <div style={{ marginBottom: 32, fontSize: "clamp(18px,2.5vw,28px)", fontFamily: "'Raleway', sans-serif", fontWeight: 600, color: C.white, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <span style={{ color: C.dim }}>The</span>
        {chosen.map((w, i) => (
          <motion.span key={i} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
            style={{ color: C.orange }}>
            {w}
          </motion.span>
        ))}
        <motion.span animate={{ opacity: [1, 0, 1] }} transition={{ repeat: Infinity, duration: 1 }}
          style={{ display: "inline-block", width: 3, height: "1em", background: C.orange, verticalAlign: "middle" }} />
      </div>

      {/* Probability bars */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 460 }}>
        {currentPreds.map((pred, i) => (
          <motion.div key={pred.word} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
            style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
            <div style={{ width: 72, textAlign: "right", color: i === 0 && selecting ? C.orange : C.dim, fontSize: 15, fontFamily: "'Raleway', sans-serif", fontWeight: i === 0 ? 700 : 400, transition: "color 0.3s" }}>
              {pred.word}
            </div>
            <div style={{ flex: 1, height: 10, borderRadius: 5, background: "rgba(240,246,255,0.06)", overflow: "hidden" }}>
              <motion.div
                animate={{ width: `${pred.prob * 100}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                style={{
                  height: "100%", borderRadius: 5,
                  background: i === 0 && selecting
                    ? `linear-gradient(90deg, ${C.orange}, ${C.pink})`
                    : pred.col + "60",
                  boxShadow: i === 0 && selecting ? `0 0 12px ${C.orange}` : "none",
                  transition: "background 0.3s, box-shadow 0.3s",
                }}
              />
            </div>
            <div style={{ width: 36, fontSize: 12, fontFamily: "'Orbitron', monospace", color: i === 0 ? C.orange : C.dimmer }}>
              {Math.round(pred.prob * 100)}%
            </div>
          </motion.div>
        ))}
      </div>
      {selecting && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          style={{ marginTop: 16, color: C.orange, fontSize: 12, fontFamily: "'Orbitron', monospace", letterSpacing: "0.12em" }}>
          → SELECTING HIGHEST PROBABILITY TOKEN...
        </motion.div>
      )}
    </div>
  );
}

// ─── CHAPTER 6 — CONTEXT WINDOW ───────────────────────────────────────────────
const CTX_TOKENS = [
  "Once", "upon", "a", "time", "in", "a", "kingdom", "far", "away", ",", "there",
  "lived", "a", "wise", "old", "wizard", "who", "knew", "the", "secrets", "of", "the",
  "universe", ".", "He", "spent", "his", "days", "studying", "the", "stars", "and",
  "his", "nights", "crafting", "spells", "of", "extraordinary", "power", ".",
];

function ContextViz() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [offset, setOffset] = useState(0);
  const limit = 20;

  useEffect(() => {
    if (!isInView) return;
    const t = setInterval(() => setOffset(o => Math.min(o + 1, CTX_TOKENS.length - limit)), 600);
    return () => clearInterval(t);
  }, [isInView]);

  return (
    <div ref={ref} style={{ marginTop: 56, width: "100%", maxWidth: 860 }}>
      <div style={{ overflowX: "hidden", position: "relative" }}>
        {/* Fade gradients */}
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 80, background: `linear-gradient(90deg, ${C.bg}, transparent)`, zIndex: 2, pointerEvents: "none" }} />
        <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 80, background: `linear-gradient(-90deg, ${C.bg}, transparent)`, zIndex: 2, pointerEvents: "none" }} />

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "8px 0", transition: "none" }}>
          {CTX_TOKENS.map((tok, i) => {
            const inWindow = i >= offset && i < offset + limit;
            const isFresh = i >= offset + limit - 4;
            const isOld = i < offset + 3;
            return (
              <motion.div key={i}
                animate={{
                  opacity: inWindow ? (isOld ? 0.3 : isFresh ? 1 : 0.75) : 0.1,
                  scale: isFresh ? 1.05 : 1,
                }}
                transition={{ duration: 0.4 }}
                style={{
                  padding: "8px 12px", borderRadius: 8, whiteSpace: "nowrap",
                  fontSize: 14, fontFamily: "'Raleway', sans-serif", fontWeight: 600,
                  background: inWindow
                    ? isFresh ? C.blue + "30" : "rgba(240,246,255,0.06)"
                    : "rgba(240,246,255,0.02)",
                  border: `1px solid ${inWindow ? (isFresh ? C.blue + "60" : "rgba(240,246,255,0.1)") : "transparent"}`,
                  color: inWindow ? (isFresh ? C.blue : C.dim) : C.dimmer,
                  flexShrink: 0,
                }}>
                {tok}
              </motion.div>
            );
          })}
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, marginTop: 24 }}>
        <div style={{ width: 12, height: 12, borderRadius: 2, background: C.blue + "40", border: `1px solid ${C.blue}60` }} />
        <span style={{ color: C.dim, fontSize: 14.5 }}>Active context window ({limit} tokens)</span>
        <div style={{ width: 12, height: 12, borderRadius: 2, background: "rgba(240,246,255,0.06)", border: "1px solid rgba(240,246,255,0.1)" }} />
        <span style={{ color: C.dim, fontSize: 14.5 }}>Older tokens fade out</span>
      </div>
      <p style={{ color: C.dim, fontSize: 14, lineHeight: 1.7, marginTop: 20, maxWidth: 560 }}>
        The model can only see what fits in its context window. Old tokens aren't forgotten — they simply fall off the edge. This is why very long conversations can lose early details.
      </p>
    </div>
  );
}

// ─── CHAPTER 7 — TRAINING ────────────────────────────────────────────────────
function TrainingViz() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [lossSteps, setLossSteps] = useState([]);

  useEffect(() => {
    if (!isInView) return;
    const steps = [];
    for (let i = 0; i <= 60; i++) {
      steps.push(3.2 * Math.exp(-i * 0.045) + 0.2 + (Math.random() - 0.5) * 0.08);
    }
    let current = 0;
    const t = setInterval(() => {
      if (current >= steps.length) { clearInterval(t); return; }
      setLossSteps(prev => [...prev, steps[current]]);
      current++;
    }, 40);
    return () => clearInterval(t);
  }, [isInView]);

  const maxLoss = 3.4;
  const W = 480, H = 220, PAD = 24;

  const milestones = [
    { step: 8,  loss: 2.3, label: "grammar", col: C.cyan },
    { step: 22, loss: 1.6, label: "facts",   col: C.purple },
    { step: 42, loss: 0.9, label: "reasoning", col: C.orange },
  ];

  return (
    <div ref={ref} style={{ marginTop: 56, width: "100%", maxWidth: 720 }}>
      <div style={{ color: C.dim, fontSize: 12, fontFamily: "'Orbitron', monospace", letterSpacing: "0.12em", marginBottom: 12 }}>
        TRAINING LOSS ↓ (lower = better)
      </div>
      <svg width={W} height={H} style={{ maxWidth: "100%", borderRadius: 12, background: "rgba(240,246,255,0.02)", border: "1px solid rgba(240,246,255,0.06)", overflow: "visible" }}>
        {/* Grid lines */}
        {[0.2, 0.4, 0.6, 0.8].map(f => (
          <line key={f} x1={44} y1={PAD + f * (H - PAD * 2)} x2={W - 20} y2={PAD + f * (H - PAD * 2)}
            stroke="rgba(240,246,255,0.05)" strokeWidth={1} />
        ))}
        {/* Milestone vertical lines + labels */}
        {milestones.map(m => {
          const x = 44 + m.step * ((W - 64) / 60);
          const y = H - PAD - (m.loss / maxLoss) * (H - PAD * 2);
          const visible = lossSteps.length > m.step;
          return visible ? (
            <g key={m.label}>
              <line x1={x} y1={PAD} x2={x} y2={H - PAD} stroke={m.col + "33"} strokeWidth={1} strokeDasharray="4 3" />
              <rect x={x - 28} y={PAD - 18} width={56} height={14} rx={3} fill={m.col + "22"} />
              <text x={x} y={PAD - 7} textAnchor="middle" fontSize={8} fontFamily="'Orbitron', monospace" fill={m.col}>{m.label}</text>
              <circle cx={x} cy={y} r={4} fill={m.col} style={{ filter: `drop-shadow(0 0 4px ${m.col})` }} />
            </g>
          ) : null;
        })}
        {/* Loss path */}
        {lossSteps.length > 1 && (
          <polyline
            points={lossSteps.map((v, i) => `${44 + i * ((W - 64) / 60)},${H - PAD - (v / maxLoss) * (H - PAD * 2)}`).join(" ")}
            fill="none" stroke={C.emerald} strokeWidth={2.5}
            style={{ filter: `drop-shadow(0 0 5px ${C.emerald}88)` }}
          />
        )}
        {/* Current loss dot */}
        {lossSteps.length > 0 && (() => {
          const last = lossSteps[lossSteps.length - 1];
          const x = 44 + (lossSteps.length - 1) * ((W - 64) / 60);
          const y = H - PAD - (last / maxLoss) * (H - PAD * 2);
          return (
            <motion.circle cx={x} cy={y} r={5} fill={C.emerald}
              animate={{ r: [5, 7, 5] }} transition={{ repeat: Infinity, duration: 0.8 }}
              style={{ filter: `drop-shadow(0 0 8px ${C.emerald})` }}
            />
          );
        })()}
        {/* Y axis labels */}
        <text x={8} y={PAD + 4} fill={C.dimmer} fontSize={9} fontFamily="monospace">{maxLoss.toFixed(1)}</text>
        <text x={8} y={H - PAD + 4} fill={C.dimmer} fontSize={9} fontFamily="monospace">0.2</text>
        <text x={44} y={H - 2} fill={C.dimmer} fontSize={9} fontFamily="monospace">training steps →</text>
      </svg>
      {lossSteps.length > 0 && (
        <div style={{ color: C.emerald, fontSize: 12, fontFamily: "'Orbitron', monospace", marginTop: 10 }}>
          LOSS: {(lossSteps[lossSteps.length - 1] ?? 0).toFixed(3)}
        </div>
      )}
    </div>
  );
}

// ─── CHAPTER 8 — HALLUCINATIONS ───────────────────────────────────────────────
const HALLUC_EXAMPLES = [
  {
    prompt: "The Eiffel Tower was built in",
    candidates: [
      { token: "1889", prob: 0.11, correct: true },
      { token: "1887", prob: 0.41, correct: false },
      { token: "1891", prob: 0.28, correct: false },
      { token: "1885", prob: 0.13, correct: false },
      { token: "1893", prob: 0.07, correct: false },
    ],
    note: "Model is most confident about 1887 — but the correct answer is 1889.",
  },
  {
    prompt: "The capital of Australia is",
    candidates: [
      { token: "Canberra", prob: 0.09, correct: true },
      { token: "Sydney",   prob: 0.58, correct: false },
      { token: "Melbourne",prob: 0.22, correct: false },
      { token: "Brisbane", prob: 0.07, correct: false },
      { token: "Perth",    prob: 0.04, correct: false },
    ],
    note: "Sydney is the most famous city — the model confuses fame with capital status.",
  },
];

function HallucinationViz() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });
  const [exIdx, setExIdx] = useState(0);

  const ex = HALLUC_EXAMPLES[exIdx];
  const sorted = [...ex.candidates].sort((a, b) => b.prob - a.prob);
  const winner = sorted[0];

  return (
    <div ref={ref} style={{ marginTop: 56, width: "100%", maxWidth: 620 }}>
      {/* Toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {HALLUC_EXAMPLES.map((_, i) => (
          <button key={i} onClick={() => setExIdx(i)}
            style={{ padding: "6px 14px", borderRadius: 100, fontSize: 9, fontFamily: "'Orbitron', monospace", cursor: "pointer", border: `1px solid ${exIdx === i ? C.red + "88" : "rgba(255,255,255,0.1)"}`, background: exIdx === i ? C.red + "18" : "transparent", color: exIdx === i ? C.red : C.dimmer }}>
            Example {i + 1}
          </button>
        ))}
      </div>

      {/* Prompt */}
      <div style={{ padding: "10px 16px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", marginBottom: 16 }}>
        <span style={{ color: C.dim, fontSize: 15, fontFamily: "'Raleway', sans-serif" }}>{ex.prompt} </span>
        <span style={{ color: C.red, fontSize: 15, fontFamily: "'Raleway', sans-serif", fontWeight: 700 }}>___</span>
      </div>

      {/* Probability bars */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
        {sorted.map((c, i) => {
          const isWinner = c === winner;
          const col = c.correct ? C.emerald : isWinner ? C.red : C.dim;
          return (
            <motion.div key={c.token} initial={{ opacity: 0, x: -12 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ delay: i * 0.08 }}
              style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 70, fontSize: 13, fontFamily: "'Raleway', sans-serif", color: col, fontWeight: isWinner ? 700 : 400, textAlign: "right", flexShrink: 0 }}>
                {c.token}
              </div>
              <div style={{ flex: 1, height: 22, borderRadius: 5, background: "rgba(255,255,255,0.05)", overflow: "hidden", position: "relative" }}>
                <motion.div
                  initial={{ width: 0 }} animate={isInView ? { width: `${c.prob * 100}%` } : {}}
                  transition={{ delay: 0.3 + i * 0.08, duration: 0.5, ease: "easeOut" }}
                  style={{ height: "100%", borderRadius: 5, background: c.correct ? C.emerald + "55" : isWinner ? C.red + "55" : col + "22",
                    boxShadow: isWinner ? `0 0 10px ${C.red}66` : c.correct ? `0 0 6px ${C.emerald}44` : "none" }} />
                <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 10, fontFamily: "'Orbitron', monospace", color: col }}>
                  {Math.round(c.prob * 100)}%
                </span>
              </div>
              {c.correct && <span style={{ fontSize: 11, color: C.emerald, fontFamily: "'Orbitron', monospace" }}>✓ TRUE</span>}
              {isWinner && !c.correct && <span style={{ fontSize: 11, color: C.red, fontFamily: "'Orbitron', monospace" }}>✗ WRONG</span>}
            </motion.div>
          );
        })}
      </div>

      {/* Explanation */}
      <motion.div initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.7 }}
        style={{ padding: "14px 18px", borderRadius: 10, background: C.red + "10", border: `1px solid ${C.red}33` }}>
        <div style={{ color: C.red, fontSize: 10, fontFamily: "'Orbitron', monospace", marginBottom: 6 }}>WHY IT HALLUCINATES</div>
        <p style={{ color: C.dim, fontSize: 14, lineHeight: 1.6, margin: 0 }}>{ex.note} The model has no fact-checker — it picks the token that fits the pattern best.</p>
      </motion.div>
    </div>
  );
}

// ─── CHAPTER 9 — SCALING ──────────────────────────────────────────────────────
function ScalingViz() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [pathLen, setPathLen] = useState(0);
  const pathRef = useRef(null);

  const W = 560, H = 260;
  const PAD = { l: 58, r: 24, t: 20, b: 48 };
  const CW = W - PAD.l - PAD.r, CH = H - PAD.t - PAD.b;

  // Scaling law: L ∝ C^-0.05 (Chinchilla-like)
  // Map log10(compute) 20..24 → x, loss 1.6..3.0 → y
  const xMin = 20, xMax = 24.5;
  const yMin = 1.55, yMax = 3.05;
  const xOf = (logC) => PAD.l + ((logC - xMin) / (xMax - xMin)) * CW;
  const yOf = (loss) => PAD.t + ((loss - yMin) / (yMax - yMin)) * CH;

  // Power-law curve points
  const CURVE_PTS = Array.from({ length: 80 }, (_, i) => {
    const logC = xMin + (i / 79) * (xMax - xMin);
    const loss = 1.6 + 1.4 * Math.pow(10, -0.085 * (logC - xMin));
    return [xOf(logC), yOf(loss)];
  });

  const curvePath = CURVE_PTS.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");

  const milestones = [
    { logC: 20.5, loss: 2.75, label: "1B", sub: "Phi, Gemma", col: C.blue },
    { logC: 21.5, loss: 2.38, label: "7B",  sub: "Mistral, Llama", col: C.cyan },
    { logC: 22.8, loss: 2.0,  label: "70B", sub: "Llama 3, Qwen",  col: C.purple },
    { logC: 24.0, loss: 1.72, label: "1T+", sub: "GPT-4, Claude",  col: C.pink },
  ];

  useEffect(() => {
    if (!isInView || !pathRef.current) return;
    const total = pathRef.current.getTotalLength();
    setPathLen(total);
  }, [isInView]);

  const yTicks = [1.7, 2.0, 2.3, 2.6, 2.9];
  const xTicks = [20, 21, 22, 23, 24];
  const xLabels = { 20: "1×", 21: "10×", 22: "100×", 23: "1K×", 24: "10K×" };

  return (
    <div ref={ref} style={{ marginTop: 40, width: "100%", maxWidth: 620 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", overflow: "visible" }}>
        {/* Grid lines */}
        {yTicks.map(y => (
          <line key={y} x1={PAD.l} y1={yOf(y)} x2={W - PAD.r} y2={yOf(y)} stroke="rgba(255,255,255,0.06)" strokeWidth={1} />
        ))}
        {xTicks.map(x => (
          <line key={x} x1={xOf(x)} y1={PAD.t} x2={xOf(x)} y2={PAD.t + CH} stroke="rgba(255,255,255,0.06)" strokeWidth={1} />
        ))}

        {/* Axes */}
        <line x1={PAD.l} y1={PAD.t + CH} x2={W - PAD.r} y2={PAD.t + CH} stroke="rgba(255,255,255,0.2)" strokeWidth={1} />
        <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t + CH} stroke="rgba(255,255,255,0.2)" strokeWidth={1} />

        {/* Y axis labels */}
        {yTicks.map(y => (
          <text key={y} x={PAD.l - 6} y={yOf(y) + 3} textAnchor="end" fontSize={8} fontFamily="'Orbitron', monospace" fill={C.dimmer}>{y.toFixed(1)}</text>
        ))}

        {/* X axis labels */}
        {xTicks.map(x => (
          <text key={x} x={xOf(x)} y={PAD.t + CH + 14} textAnchor="middle" fontSize={8} fontFamily="'Orbitron', monospace" fill={C.dimmer}>{xLabels[x]}</text>
        ))}

        {/* Axis titles */}
        <text x={PAD.l + CW / 2} y={H - 4} textAnchor="middle" fontSize={8} fontFamily="'Orbitron', monospace" fill={C.dim}>TRAINING COMPUTE (relative) →</text>
        <text x={12} y={PAD.t + CH / 2} textAnchor="middle" fontSize={8} fontFamily="'Orbitron', monospace" fill={C.dim} transform={`rotate(-90, 12, ${PAD.t + CH / 2})`}>↑ LOSS (lower = better)</text>

        {/* Scaling law curve — animated draw */}
        {pathLen > 0 && (
          <motion.path
            ref={pathRef}
            d={curvePath} fill="none" stroke={C.cyan} strokeWidth={2.5}
            strokeDasharray={pathLen} strokeDashoffset={pathLen}
            animate={{ strokeDashoffset: 0 }}
            transition={{ duration: 1.6, ease: "easeOut" }}
            style={{ filter: `drop-shadow(0 0 5px ${C.cyan}66)` }}
          />
        )}
        <path ref={pathLen === 0 ? pathRef : undefined} d={curvePath} fill="none" stroke="transparent" strokeWidth={1}
          onAnimationEnd={() => { if (pathRef.current) setPathLen(pathRef.current.getTotalLength()); }} />

        {/* Milestone points */}
        {isInView && milestones.map((m, i) => {
          const cx = xOf(m.logC), cy = yOf(m.loss);
          const above = m.loss < 2.2;
          return (
            <motion.g key={m.label} initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.2 + i * 0.15, duration: 0.3 }}>
              {/* Drop line */}
              <line x1={cx} y1={cy} x2={cx} y2={PAD.t + CH} stroke={m.col + "44"} strokeWidth={1} strokeDasharray="3 3" />
              <circle cx={cx} cy={cy} r={6} fill={m.col + "33"} stroke={m.col} strokeWidth={1.5} />
              <text x={cx} y={above ? cy - 12 : cy + 18} textAnchor="middle" fontSize={9} fontFamily="'Orbitron', monospace" fill={m.col} fontWeight="700">{m.label}</text>
              <text x={cx} y={above ? cy - 3 : cy + 27} textAnchor="middle" fontSize={7} fontFamily="'Orbitron', monospace" fill={C.dimmer}>{m.sub}</text>
            </motion.g>
          );
        })}
      </svg>
      <p style={{ color: C.dim, fontSize: 14, lineHeight: 1.7, marginTop: 24, maxWidth: 500, textAlign: "center", margin: "24px auto 0" }}>
        Scaling up parameters and training data consistently unlocks new capabilities — writing, coding, reasoning — that couldn't be programmed directly.
      </p>
    </div>
  );
}

// ─── CHAPTER 10 — MODERN AI ───────────────────────────────────────────────────
const MODULES = [
  { name: "Vision",   col: C.blue,    icon: "◎", angle: 0 },
  { name: "Audio",    col: C.emerald, icon: "♫", angle: 51.4 },
  { name: "Video",    col: C.purple,  icon: "▶", angle: 102.8 },
  { name: "Reasoning",col: C.cyan,    icon: "⟳", angle: 154.2 },
  { name: "Tools",    col: C.orange,  icon: "⚙", angle: 205.6 },
  { name: "Memory",   col: C.pink,    icon: "◈", angle: 257.1 },
  { name: "Agents",   col: C.gold,    icon: "⬡", angle: 308.5 },
];

const MODERN_AI_TABS = [
  {
    key: "vision",
    label: "Vision",
    col: C.blue,
    icon: "◎",
    input: { label: "IMAGE INPUT", content: "🖼️  a photo of a golden retriever sitting in a park" },
    output: { label: "MODEL OUTPUT", content: "A golden retriever is sitting on grass in a sunny park, facing the camera with its tongue out. The dog appears happy and relaxed." },
    note: "GPT-4V, Gemini, Claude 3 can all describe, analyze, and reason about images.",
  },
  {
    key: "code",
    label: "Code",
    col: C.emerald,
    icon: "⟨/⟩",
    input: { label: "PROMPT", content: "Write a function that returns the nth Fibonacci number." },
    output: { label: "GENERATED CODE", content: "def fib(n):\n    a, b = 0, 1\n    for _ in range(n):\n        a, b = b, a + b\n    return a", isCode: true },
    note: "Modern LLMs write, debug, and explain code across dozens of languages.",
  },
  {
    key: "reasoning",
    label: "Reasoning",
    col: C.purple,
    icon: "⟳",
    input: { label: "QUESTION", content: "If I have 3 apples and give away half, then buy 4 more, how many do I have?" },
    output: { label: "STEP-BY-STEP", content: "1. Start with 3 apples\n2. Give away half → 3 ÷ 2 = 1.5 → 1 apple\n3. Buy 4 more → 1 + 4 = 5\n\nAnswer: 5 apples", isCode: true },
    note: "Chain-of-thought reasoning lets models solve multi-step problems by thinking out loud.",
  },
];

function ModernAIViz() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [tab, setTab] = useState("vision");
  const current = MODERN_AI_TABS.find(t => t.key === tab);

  return (
    <div ref={ref} style={{ marginTop: 56, width: "100%", maxWidth: 680 }}>
      {/* Tab selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {MODERN_AI_TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: "8px 18px", borderRadius: 100, fontSize: 10, fontFamily: "'Orbitron', monospace", cursor: "pointer", letterSpacing: "0.1em", border: `1px solid ${tab === t.key ? t.col + "88" : "rgba(255,255,255,0.12)"}`, background: tab === t.key ? t.col + "20" : "transparent", color: tab === t.key ? t.col : C.dimmer, fontWeight: tab === t.key ? 700 : 400, transition: "all 0.2s" }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
          {/* I/O cards */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            {/* Input */}
            <div style={{ flex: 1, minWidth: 200, padding: "12px 16px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <div style={{ fontSize: 8, fontFamily: "'Orbitron', monospace", color: C.dimmer, marginBottom: 8, letterSpacing: "0.12em" }}>{current.input.label}</div>
              <div style={{ color: C.white, fontSize: 14, fontFamily: "'Raleway', sans-serif", lineHeight: 1.6 }}>{current.input.content}</div>
            </div>

            {/* Arrow */}
            <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <motion.div animate={{ x: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.2 }}
                style={{ fontSize: 20, color: current.col }}>→</motion.div>
            </div>

            {/* Output */}
            <div style={{ flex: 1, minWidth: 200, padding: "12px 16px", borderRadius: 10, background: current.col + "0F", border: `1px solid ${current.col}44` }}>
              <div style={{ fontSize: 8, fontFamily: "'Orbitron', monospace", color: current.col, marginBottom: 8, letterSpacing: "0.12em" }}>{current.output.label}</div>
              <div style={{ color: current.output.isCode ? current.col : C.white, fontSize: current.output.isCode ? 12 : 14, fontFamily: current.output.isCode ? "monospace" : "'Raleway', sans-serif", lineHeight: 1.6, whiteSpace: current.output.isCode ? "pre" : "normal" }}>
                {current.output.content}
              </div>
            </div>
          </div>

          {/* Note */}
          <div style={{ fontSize: 13, color: C.dim, lineHeight: 1.6 }}>{current.note}</div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ─── CHAPTER 11 — INFERENCE ───────────────────────────────────────────────────
function InferenceViz() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });
  const [outputTokens, setOutputTokens] = useState([]);
  const fullOutput = ["The", " sky", " is", " blue", " because", " of", " a", " phenomenon", " called", " Rayleigh", " scattering", "."];

  const paused = useContext(AnimContext);

  useEffect(() => {
    if (!isInView || outputTokens.length >= fullOutput.length || paused) return;
    const t = setTimeout(() => {
      setOutputTokens(prev => [...prev, fullOutput[prev.length]]);
    }, 280);
    return () => clearTimeout(t);
  }, [isInView, outputTokens, paused]);

  return (
    <div ref={ref} style={{ marginTop: 56, width: "100%", maxWidth: 720 }}>
      {/* Input */}
      <motion.div initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}}
        style={{ padding: "16px 20px", borderRadius: 12, background: C.purple + "15", border: `1px solid ${C.purple}40`, marginBottom: 24, maxWidth: 480 }}>
        <div style={{ color: C.dimmer, fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.12em", marginBottom: 8 }}>USER INPUT</div>
        <div style={{ color: C.white, fontSize: 16, fontFamily: "'Raleway', sans-serif" }}>Why is the sky blue?</div>
      </motion.div>

      {/* Token flow */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        {["Why", "is", "the", "sky", "blue", "?"].map((tok, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 + i * 0.06 }}
            style={{ padding: "6px 12px", borderRadius: 8, background: C.purple + "25", border: `1px solid ${C.purple}50`, color: C.purple, fontSize: 14.5, fontWeight: 600 }}>
            {tok}
          </motion.div>
        ))}
        <motion.div animate={{ x: [0, 20, 0], opacity: [0.4, 1, 0.4] }} transition={{ repeat: Infinity, duration: 1.2 }}
          style={{ color: C.orange, fontSize: 20 }}>→</motion.div>
        <div style={{ padding: "8px 14px", borderRadius: 10, background: C.orange + "15", border: `1px solid ${C.orange}40`, color: C.orange, fontSize: 12, fontFamily: "'Orbitron', monospace" }}>
          TRANSFORMER
        </div>
        <motion.div animate={{ x: [0, 20, 0], opacity: [0.4, 1, 0.4] }} transition={{ repeat: Infinity, duration: 1.2, delay: 0.3 }}
          style={{ color: C.orange, fontSize: 20 }}>→</motion.div>
      </div>

      {/* Output */}
      <motion.div initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.5 }}
        style={{ padding: "16px 20px", borderRadius: 12, background: C.orange + "10", border: `1px solid ${C.orange}30`, maxWidth: 560, minHeight: 110 }}>
        <div style={{ color: C.dimmer, fontSize: 10, fontFamily: "'Orbitron', monospace", letterSpacing: "0.12em", marginBottom: 8 }}>GENERATING RESPONSE</div>
        <div style={{ color: C.white, fontSize: 16, fontFamily: "'Raleway', sans-serif", lineHeight: 1.6 }}>
          {outputTokens.map((tok, i) => (
            <motion.span key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
              {tok}
            </motion.span>
          ))}
          {outputTokens.length < fullOutput.length && (
            <motion.span animate={{ opacity: [1, 0, 1] }} transition={{ repeat: Infinity, duration: 0.6 }}
              style={{ display: "inline-block", width: 2, height: "1em", background: C.orange, verticalAlign: "middle", marginLeft: 2 }} />
          )}
        </div>
      </motion.div>
      <p style={{ color: C.dim, fontSize: 14.5, lineHeight: 1.7, marginTop: 20, maxWidth: 500 }}>
        The model generates one token at a time. Each token becomes part of the context for the next prediction. This repeats until the response is complete.
      </p>
    </div>
  );
}

// ─── DIVIDER ──────────────────────────────────────────────────────────────────
function Divider({ col = C.cyan }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, padding: "0 clamp(24px,8vw,120px)", position: "relative", zIndex: 1 }}>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${col}40, transparent)` }} />
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: col, boxShadow: `0 0 8px ${col}` }} />
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${col}40, transparent)` }} />
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function AI() {
  return (
    <div style={{
      width: "100vw", background: C.bg, minHeight: "100vh",
      fontFamily: "'Raleway', sans-serif", overflowX: "hidden",
      color: C.white,
    }}>
      <Helmet>
        <title>How AI Thinks — Shreeram Ganeshan</title>
        <meta name="description" content="A visual journey inside modern language models. Understand how ChatGPT and Claude actually work." />
      </Helmet>

      {/* Scroll-driven ambient backdrop behind all content (desktop only) */}
      <style>{`@media (max-width: 768px) { .ai-star-bg { display: none; } }`}</style>
      <StarBackground />

      {/* ── Hero ── */}
      <Hero />

      <Divider col={C.cyan} />

      {/* ── Chapter 1: Tokens ── */}
      <Section id="tokens" label="Chapter 1 · Tokens" title="Words become numbers." accent={C.cyan}
        subtitle="Before an AI can process language, it must convert text into something a computer can work with: integers. This process is called tokenization.">
        <TokensViz />
      </Section>

      <Divider col={C.purple} />

      {/* ── Chapter 2: Embeddings ── */}
      <Section id="embeddings" label="Chapter 2 · Embeddings" title="Numbers become meaning." accent={C.purple}
        subtitle="Each token ID maps to a vector — a list of hundreds of numbers. Words with similar meanings end up close together in this mathematical space, without anyone programming the rules.">
        <EmbeddingsCanvas />
      </Section>

      <Divider col={C.cyan} />

      {/* ── Chapter 3: Attention ── */}
      <Section id="attention" label="Chapter 3 · Attention" title="Every word watches every other." accent={C.cyan}
        subtitle="The key insight of the Transformer: every token can directly compare itself to every other token. This is how 'it' finds 'cat' in a sentence, even if they're far apart.">
        <AttentionViz />
      </Section>

      <Divider col={C.orange} />

      {/* ── Chapter 4: Transformer ── */}
      <Section id="transformer" label="Chapter 4 · Transformer" title="Stacked layers of understanding." accent={C.orange}
        subtitle="The Transformer processes tokens through a stack of layers. Each layer refines the representation — lower layers see syntax, higher layers grasp meaning.">
        <TransformerViz />
      </Section>

      <Divider col={C.cyan} />

      {/* ── Concepts: Foundation ── */}
      <ConceptVizSection category="Foundation · Neural Networks" accent={C.cyan} title="The brain-inspired building block."
        hook="A single neuron multiplies each input by a learned weight, sums them, adds a bias, and fires an output through an activation gate. Stack millions together and intelligence emerges.">
        <NeuralNetworkViz />
      </ConceptVizSection>

      <ConceptVizSection category="Foundation · Deep Learning" accent={C.blue} title="Depth creates abstraction."
        hook="Each layer transforms its input into a richer representation. Layer 1 sees pixels. Layer 5 sees 'cat'. Click a layer to watch the image reveal what it detects.">
        <DeepLearningViz />
      </ConceptVizSection>

      <ConceptVizSection category="Foundation · LLMs" accent={C.purple} title="Language models at scale."
        hook="A sentence enters. Words become tokens. Tokens become vectors. Vectors flow through transformer layers. Then — one predicted word at a time — the model speaks."
        fullWidth>
        <LLMFlowViz />
      </ConceptVizSection>

      <Divider col={C.orange} />

      {/* ── Concepts: Math ── */}
      <ConceptVizSection category="Math · Logits & Softmax" accent={C.gold} title="Scores become probabilities."
        hook="The model scores every word in its vocabulary. Toggle to watch Softmax transform raw scores into a valid probability distribution — and watch 'cat' win.">
        <LogitsSoftmaxViz />
      </ConceptVizSection>

      <ConceptVizSection category="Math · Temperature" accent={C.pink} title="Dial the randomness."
        hook="Drag the slider. At low temperature the model picks the top word almost every time. At high temperature the distribution flattens — surprising words become possible.">
        <TemperatureViz />
      </ConceptVizSection>

      {/* ── Chapter 5: Prediction ── */}
      <Section id="prediction" label="Chapter 5 · Prediction" title="One token at a time." accent={C.orange}
        subtitle="After processing all context, the model assigns a probability to every word in its vocabulary. The most likely next token wins — but there's always a chance for surprise.">
        <PredictionViz />
      </Section>

      <Divider col={C.blue} />

      {/* ── Chapter 6: Context Window ── */}
      <Section id="context" label="Chapter 6 · Context Window" title="The model's working memory." accent={C.blue}
        subtitle="The context window is how much text the model can see at once. Everything the model knows about your conversation fits inside it. What falls off the edge is forgotten.">
        <ContextViz />
      </Section>

      <Divider col={C.cyan} />

      {/* ── Concept: KV Cache ── */}
      <ConceptVizSection category="Efficiency · KV Cache" accent={C.emerald} title="Don't recompute what you've already seen."
        hook="Every new token needs to attend to all past tokens. Without a cache, Key & Value matrices are recomputed from scratch each time — O(n²). The KV cache stores them once and reuses them.">
        <KVCacheViz />
      </ConceptVizSection>

      <Divider col={C.emerald} />

      {/* ── Chapter 7: Training ── */}
      <Section id="training" label="Chapter 7 · Training" title="Learning from everything." accent={C.emerald}
        subtitle="Training means showing the model trillions of tokens, asking it to predict the next one, measuring how wrong it was, and nudging its billions of parameters slightly in the right direction. Repeated billions of times.">
        <TrainingViz />
      </Section>

      <Divider col={C.cyan} />

      {/* ── Concepts: Learning ── */}
      <ConceptVizSection category="Learning · Self-Supervised Learning" accent={C.cyan} title="The label is already in the data."
        hook="No human labels needed. The model masks a word, predicts it from context, checks against the real word, and updates weights. Watch the cycle run automatically.">
        <SelfSupervisedViz />
      </ConceptVizSection>

      <ConceptVizSection category="Learning · Pretraining" accent={C.blue} title="The foundation everything builds on."
        hook="Trillions of tokens — web pages, books, code, science — stream into the model as it learns to predict the next token. Watch the data flow in.">
        <PretrainingViz />
      </ConceptVizSection>

      <ConceptVizSection category="Math · Backpropagation" accent={C.pink} title="The chain rule, applied at scale."
        hook="After the loss is computed, gradients flow backward through every layer via the chain rule. Blue = forward pass computing activations. Red = backward pass propagating error signals.">
        <BackpropViz />
      </ConceptVizSection>

      <ConceptVizSection category="Math · Gradient Descent" accent={C.gold} title="Walk downhill, very slowly."
        hook="Every parameter moves a tiny step in the direction that reduces loss. Drag the learning rate slider to see how step size affects the path — too small is slow, too large overshoots.">
        <GradientDescentViz />
      </ConceptVizSection>

      <ConceptVizSection category="Learning · Fine-Tuning" accent={C.emerald} title="Specialising the generalist."
        hook="A pretrained model knows language. Fine-tuning updates only some of its layers on a smaller curated dataset. Toggle to see which layers are frozen vs actively training.">
        <FineTuningViz />
      </ConceptVizSection>

      <ConceptVizSection category="Learning · RLHF" accent={C.purple} title="Learning from human preferences."
        hook="Three actors iterate: the LLM generates responses, a human picks the better one, and a reward model learns that preference. Then PPO steers the LLM to score higher.">
        <RLHFViz />
      </ConceptVizSection>

      <ConceptVizSection category="Learning · LoRA" accent={C.orange} title="Fine-tune without touching most weights."
        hook="All base weights are frozen. Tiny low-rank matrices A and B are inserted alongside each layer and trained instead. Toggle to attach adapters and drag the rank slider to see the parameter cost.">
        <LoRAViz />
      </ConceptVizSection>

      <ConceptVizSection category="Learning · Reinforcement Learning" accent={C.pink} title="Learning from rewards, not labels."
        hook="A robot navigates a maze. Early episodes explore randomly and take long paths. Later episodes learn the optimal route. Run an episode, then advance to watch the policy improve.">
        <RLMazeViz />
      </ConceptVizSection>

      <ConceptVizSection category="Learning · Distillation" accent={C.cyan} title="A small model learns from a large one."
        hook="The teacher outputs soft probabilities — not just 'cat' but how likely each word is. The student learns that richer signal, not just which answer was right.">
        <DistillationViz />
      </ConceptVizSection>

      <Divider col={C.orange} />

      {/* ── Chapter 8: Inference ── */}
      <Section id="inference" label="Chapter 8 · Inference" title="Generating a response." accent={C.orange}
        subtitle="When you ask a question, the model tokenizes it, runs it through every layer of the Transformer, picks the most likely next token, then repeats — building the response word by word.">
        <InferenceViz />
      </Section>

      <Divider col={C.cyan} />

      {/* ── Concepts: Reasoning ── */}
      <ConceptVizSection category="Reasoning · Chain of Thought" accent={C.blue} title="Show your work."
        hook="Toggle between a direct answer (fast but wrong) and chain-of-thought — watch the model reason step by step and arrive at the correct answer.">
        <ChainOfThoughtViz />
      </ConceptVizSection>

      <ConceptVizSection category="Reasoning · Reasoning Models" accent={C.purple} title="Models that think before they speak."
        hook="Toggle between a fast model (instant, wrong) and a reasoning model that checks systematically. Watch the internal thought trace build before the final answer appears.">
        <ReasoningModelsViz />
      </ConceptVizSection>

      <Divider col={C.red} />

      {/* ── Chapter 9: Hallucinations ── */}
      <Section id="hallucinations" label="Chapter 9 · Hallucinations" title="Confident. Not always correct." accent={C.red}
        subtitle="LLMs predict what sounds right, not what is true. There is no internal fact-checker. High confidence means the pattern fit — not that the answer is accurate.">
        <HallucinationViz />
      </Section>

      <Divider col={C.cyan} />

      {/* ── Application Layer: unified card grid ── */}
      <ApplicationGridSection />

      <Divider col={C.pink} />

      {/* ── Chapter 10: Scaling ── */}
      <Section id="scaling" label="Chapter 10 · Scaling" title="Bigger unlocks more." accent={C.pink}
        subtitle="Increase the number of parameters and training data and something surprising happens: new capabilities emerge that nobody programmed. Writing, reasoning, coding — all from predicting tokens at scale." center>
        <ScalingViz />
      </Section>

      <Divider col={C.cyan} />

      {/* ── Concepts: Efficiency ── */}
      <ConceptVizSection category="Efficiency · Quantization" accent={C.orange} title="Shrink the model, keep the smarts."
        hook="Weights are stored as 32-bit floats by default. Click 8-bit or 4-bit to snap them to fewer discrete levels — watch the bars shift and the VRAM footprint drop.">
        <QuantizationViz />
      </ConceptVizSection>

      <ConceptVizSection category="Efficiency · SLMs" accent={C.emerald} title="Small language models: more for less."
        hook="Hover any orb. Larger = more parameters. SLMs like Phi-3 and SmolLM are small enough to run on a phone — yet competitive on focused tasks.">
        <SLMViz />
      </ConceptVizSection>

      <ConceptVizSection category="Efficiency · MoE" accent={C.purple} title="Mixture of Experts: sparse activation."
        hook="Each token is routed to just 2 of 8 experts. The rest sleep. Click a token to see which experts wake up — the router learns which specialists handle what.">
        <MoEViz />
      </ConceptVizSection>

      <ConceptVizSection category="Efficiency · Inference-time Scaling" accent={C.gold} title="Think longer, get smarter."
        hook="Same model, same weights — but more compute at answer time. Watch the same question answered three ways: greedy (fast, wrong), Best-of-N (better), MCTS (verified, optimal).">
        <InferenceTimeScalingViz />
      </ConceptVizSection>

      <Divider col={C.gold} />

      {/* ── Chapter 11: Modern AI ── */}
      <Section id="modern" label="Chapter 11 · Modern AI" title="The language core grows arms." accent={C.gold}
        subtitle="Today's frontier models extend the language core with new perceptual and action modules — seeing images, hearing audio, executing tools, maintaining memory, and spawning sub-agents." center>
        <ModernAIViz />
      </Section>

      {/* ── Footer ── */}
      <section style={{ minHeight: "50vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", padding: "100px 24px", position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1 }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: 10, letterSpacing: "0.3em", color: C.cyan, opacity: 0.7, marginBottom: 24 }}>
            END OF JOURNEY
          </div>
          <h2 style={{ fontSize: "clamp(32px,6vw,80px)", fontWeight: 900, lineHeight: 1.05, letterSpacing: "-0.02em", margin: "0 0 24px" }}>
            You now understand<br />
            <span style={{ background: `linear-gradient(135deg, ${C.cyan}, ${C.purple})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              how ChatGPT works.
            </span>
          </h2>
          <p style={{ color: C.dim, fontSize: "clamp(15px,2vw,20px)", lineHeight: 1.7, maxWidth: 520, margin: "0 auto" }}>
            Tokens. Embeddings. Attention. Layers. Prediction. Scaled up to trillions of examples.
            That is the entire recipe for modern AI.
          </p>
        </motion.div>
      </section>

    </div>
  );
}
